/**
 * WorkflowsFacet — the ADR-075 dsl activity viewer AND (ADR-079 Phase 5b) the
 * Workflow Studio editor, documentation/PRD-workflow-editor.md +
 * documentation/adr/ADR-079-ai-assisted-workflow-authoring-studio.md.
 *
 * ADR-111 IA amendment: this is now the Workflows FACET of one agent, embedded
 * by FleetEditor.tsx under /fleet-editor/agents/:agent/workflows(/:name) — it
 * no longer hosts the Skills/Crons/Files sibling views or their tab switcher
 * (those are separate facets/routes now); `agent` is a fixed prop, not an
 * internal filter, and deep-linking a specific workflow is via `initialName`/
 * `initialEdit` props (read from the route by the shell) rather than this
 * component's own useParams.
 *
 * Three-pane layout (PRD §3), unchanged by 5b:
 *   LEFT   activities grouped by agent, dsl vs legacy badge, click to select.
 *   CENTER the selected activity's `do` graph — @xyflow/react + dagre
 *          top-down auto-layout. In edit mode this renders the DRAFT's
 *          graph live (client-side parse, ADR-079 D1) rather than the
 *          server's last-saved copy — with the ASSISTANT DOCKED BENEATH it
 *          (ADR-136 D1), always present rather than a mode you switch into.
 *   RIGHT  Inspector (selected node's fields — read-only view, or an editable
 *          field form in Visual/Field mode, ADR-121 D4) + Validation
 *          (server-side, debounced while editing) + a collapsible Raw YAML.
 *
 * The editor model (ADR-079 D1/D2, refined by ADR-121 D4): one shared
 * working draft (draftYAML + its parsed draftActivity) that every mode
 * reads and writes. Three modes are wired up — Visual (5c, un-deferred by
 * ADR-121 D4: the node graph is the DEFAULT and only primary workflow view);
 * Field and YAML are demoted behind the "Raw / advanced" drawer (ADR-121
 * D1/D6 — still fully reachable, never the first thing shown).
 *
 * The assistant is NOT one of them (ADR-136 D1). It used to be — `mode ===
 * 'chat'` rendered it INSTEAD of the canvas, and the inspector fell back to
 * its read-only branch, so asking a question cost the operator both the
 * picture and the panel describing what they were asking about. The one
 * surface where "show me what you mean" matters most was the one where the
 * view went away. It now docks under the canvas in every mode, and selection
 * is shared both ways: the selected node rides with the turn as `focus` (so
 * "make this step deterministic" resolves to the step on screen), and a
 * finding's Fix button selects the node it concerns before pre-filling the
 * composer. It calls POST /api/activities/copilot with the running
 * conversation + the current draft and loads the copilot's returned
 * (already-validated, D4) draft straight into the SAME draftYAML/
 * draftActivity state — see applyCopilotResponse (workflowDraft.ts) for the
 * merge, and handleChatSend below for the request.
 *
 * YAML mode edits draftYAML directly (parsed client-side via
 * workflowDraft.ts's js-yaml wrapper for a live graph preview); Field and
 * Visual both edit draftActivity via workflowDraft.ts's scalar-field
 * mutators (Field through its own top-level task-list palette; Visual by
 * clicking a node — anywhere in the tree, including nested `for`/`fork`/
 * `switch` bodies, since updateTaskFields/updateSwitchCaseExpr already walk
 * the whole tree) — both re-serialize into draftYAML, so switching modes
 * never loses work (D2: "Mode switches are lossless"). A legacy
 * (stages/steps) activity has no graph/Field form (Field is disabled, and
 * Visual falls back to the read-only LegacyView) — the assistant can still
 * target it since the copilot always returns a dsl draft, and YAML remains
 * the only way to hand-edit one.
 *
 * Editing entry points: [Edit] opens an existing activity's draft; "New
 * workflow" (D7 scaffold-from-scratch) starts a blank dsl draft for a chosen
 * agent + new activity name with NO server-side detail loaded yet
 * (isScaffold state) — see startNewWorkflow below. Both open on the canvas
 * with the assistant docked under it; neither has to choose between them.
 *
 * The propose pipeline (D5): [Propose] is visible only to fleet_owner,
 * re-validates the draft, then POSTs /api/activities/:agent/:name/propose —
 * MC's first file-authoring capability (works for both an edited existing
 * file and a brand-new one from the scaffold flow). An invalid draft is
 * never proposed (D4) — the button is disabled and the API 400s
 * defensively either way.
 *
 * Legacy migration (owner decision): the path from a legacy single-prompt
 * activity to a DSL workflow is NOT a dedicated feature — it's the "Convert
 * to DSL flow" affordance in the assistant dock (shown only while the open
 * draft is still legacy), which seeds the canonical conversion instructions + the
 * CURRENT DRAFT (draftYAML, not detail.yaml — the draft may have been
 * edited in YAML mode since the activity was opened) through the SAME
 * handleChatSend path a typed turn uses. See handleConvertToDsl below.
 */
import type React from 'react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentProps } from 'react'
import ChangesetFiles from './ChangesetFiles'
import { useModelCatalog, type ModelChoice } from '../components/ModelPicker'
import { ModelOverrideLink } from '../components/ModelOverrideLink'
import { AssistantModelState, useAssistantModel } from '../components/AssistantModelPin'
import {
  activityFindings, deriveFindings, summarize, worstSeverity,
  type Finding, type FindingSeverity,
} from './workflowFindings'
import { adviseNodes } from './workflowAdvice'

// The whole change, in the order the server applies it: the activity in the open
// slot first, then everything else. A pure module function rather than a closure
// so effects depend on its INPUTS (draft, extras, slot) and not on a new function
// identity every render.
export function buildChangeset(
  agent: string,
  name: string,
  draftYAML: string,
  extraFiles: ProposedChangesetFile[],
): ProposedChangesetFile[] {
  const selfPath = `agents/${agent}/activities/${name}.yaml`
  return [
    { path: selfPath, content: draftYAML, op: 'write' as const },
    // THE SLOT'S OWN FILE IS NEVER AN EXTRA, and this is the guarantee rather than a
    // tidy-up.
    //
    // The activity is prepended above from the DRAFT, so an extra naming the same path
    // sends it twice and the server refuses the whole propose:
    // `file "agents/analytics/activities/weekly-health.yaml" appears twice in one
    // changeset`. Reported 2026-09-03 on a run that had passed all seven phases — the
    // authoring worked and the change could not be proposed.
    //
    // It became reachable when the implementation phase started returning the activity as
    // a CHANGESET FILE rather than only as `draft`: before ADR-166, `files` held only the
    // other files a turn touched, so this could not happen and nothing guarded it.
    //
    // The DRAFT wins, deliberately. It is what the editor renders, what validation ran
    // against, and what the operator may have edited since the turn — the extra's copy is
    // at best identical and at worst stale.
    ...extraFiles.filter(f => f.path !== selfPath),
  ]
}
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useFleetVersion } from '../hooks/useFleetVersion'
import { isPrefixStaged, useStagedPaths } from '../hooks/useStagedPaths'
import { useResizableWidth } from '../hooks/useResizableWidth'
import { useTheme } from '../theme/theme'
import { StagedBadge } from '../components/StagedBadge'
import { DataRefFieldAffordance } from './DataRefFieldAffordance'
import { TaskMappingEditor } from './TaskMappingEditor'
import dagre from 'dagre'
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  type Node as RFNode,
  type Edge as RFEdge,
  type NodeProps,
  type NodeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useAwaitingProposal } from './useAwaitingProposal'
import { useAttentionNotify } from '../context/AttentionContext'
import { copilotJobAttentionId, localAttentionId } from '../utils/attentionStore'
import { staleNoticeApplies } from './draftStaleNotice'
import { reconcileConversation } from './conversationReconcile'
import { api, AuthorTestsFailingError, CopilotNotConfiguredError, RunChangedError, RunFinishedError, StaleDraftError, UnauthorizedError, UnsavedAnswerGoneError } from '../api/client'
import { registrationButtonTone, registrationTone } from '../components/ProposeResultBanner'
import type {
  ProposedChangesetFile,
  ActivitySummary,
  ActivityDetail,
  ConversationSummary,
  EditorConversation,
  CopilotIntent,
  CopilotMessage,
  EditorSession,
  FailingCheck,
  ProposeActivityResult,
  ValidateActivityResult, CopilotInFlight,
  DataReferenceMatch, DataReferenceMatchLocation, DataReferenceMatches,
  DataReferenceOffers,
  ModelCatalogResponse,
  AssistantModelView,} from '../api/client'
import { AuthoringRail } from '../components/AuthoringRail'
import { ScopeFirstControl, scopingControlFor, scopingForSend, useSlotRun, type ScopingControl } from './scopeFirst'
import { WorkflowRuns } from '../components/WorkflowRuns'
import { FailingChecksPanel } from '../components/FailingChecksPanel'
import { DeletePanel } from '../components/DeletePanel'
import { useAuth } from '../context/AuthContext'
import {
  buildWorkflowGraph,
  isDialectActivity,
  type GraphEdge,
  type GraphNode,
  type ParsedEntry,
  type TaskType,
} from './workflowGraph'
import {  updateTaskMapping,
  getTaskMapping,
  mappingSiteFor,
  type MappingEntry,
  addTopLevelTask,
  addTaskInContainer,
  isContainerType,
  applyCopilotResponse,
  buildConvertToDslSeed,
  buildScaffoldYAML,
  defaultSpecFor,
  moveTaskAnywhere,
  parseDraftYAML,
  removeTaskAnywhere,
  referrersOf,
  descendantsOf,
  getTaskTree,
  type TaskTreeEntry,
  ensureDataPin,
  applyDataReferenceSelections,
  describeLocation,
  joinCommandArgs,
  splitCommandArgs,
  ensureTrailingUserTurn,
  mergeHeldAnswer,
  conversationTurns,
  restoreConversationTurns,
  catalogMessages,
  type DataReferenceSelection,
  type DataReferenceReplaceSelection,
  serializeDraftYAML,
  slugifyWorkflowName,
  updateSwitchCaseExpr,
  updateTaskFields,
  type CopilotTurn,
} from './workflowDraft'
import { useMatches, sameLocation } from './useMatches'
import { HardcodedValuesPanel } from './HardcodedValuesPanel'
import { scrollWithin } from './scrollWithin'
import { PendingReferences } from '../components/PendingReferences'
import { DataMentionChips } from './DataMentionChips'
import { DataMentionPopup } from './DataMentionPopup'
import {
  applyMentionPick,
  mentionAtCaret,
  mentionKeyAction,
  mentionLevel,
  mentionRowKey,
  type MentionRow,
} from './mentionAtCaret'

function toStoredTranscript(turns: CopilotTurn[]) {
  return turns.map(m => ({ role: m.role, content: m.content }))
}

// ---------------------------------------------------------------------------
// Node visual language (PRD §4)
// ---------------------------------------------------------------------------

const TASK_STYLES: Record<TaskType, string> = {
  agent: 'bg-blue-50 border-blue-300 text-blue-900',
  command: 'bg-green-50 border-green-300 text-green-900',
  switch: 'bg-amber-50 border-amber-400 text-amber-900',
  fork: 'bg-cyan-50 border-cyan-300 text-cyan-900',
  for: 'bg-slate-100 border-slate-400 text-slate-900',
  run: 'bg-purple-50 border-purple-300 text-purple-900',
  set: 'bg-gray-50 border-gray-300 text-gray-700',
  // ADR-128 step 1: `python` is the eighth task type the validator has always
  // accepted; it was missing here and rendered as nothing.
  python: 'bg-indigo-50 border-indigo-300 text-indigo-900',
  approval: 'bg-rose-50 border-rose-300 text-rose-900',
  // Not a task type — the graph's own "I do not model this" marker. Styled to
  // look wrong on purpose: an unmodelled type must be visible, because the
  // silent alternative is what hid `python` for two years.
  unknown: 'bg-red-50 border-red-400 border-dashed text-red-900',
}

const TASK_ICON: Record<TaskType, string> = {
  agent: '▭',
  command: '▭',
  switch: '◇',
  fork: '⇉',
  for: '⟳',
  run: '▭▭',
  set: '▭',
  python: '⌨',
  approval: '✋',
  unknown: '⚠',
}

// Plain-language names, NOT the DSL keyword. This was an IDENTITY map — every
// label was the raw type name — which made it useless exactly where it mattered:
// an operator looking for "how do I call another workflow" cannot guess that the
// answer is a task type named `run`. Reported from matthaios against
// technology-briefing ("I don't see a way to do this today") for a capability the
// DSL has had since ADR-075 Track 1 Phase 3 — and which that very agent was
// already using, twice, inside a fork.
//
// The DSL keyword is still shown next to the label wherever this is used, because
// the keyword is what appears in the YAML and in every validator message.
const TASK_LABEL: Record<TaskType, string> = {
  agent: 'Ask an agent',
  command: 'Run a command',
  switch: 'Branch on a condition',
  fork: 'Run branches in parallel',
  for: 'Repeat for each item',
  run: 'Call another workflow',
  set: 'Set values',
  python: 'Run Python',
  approval: 'Wait for approval',
  unknown: 'Not modelled here',
}

// Plain-language, one-line descriptions of each task type — surfaced in the
// node inspector and the graph legend so a non-technical operator understands
// what each colored box does without reading the DSL guide.
const TASK_DESC: Record<TaskType, string> = {
  agent: 'Delegates a prompt to an agent and captures its reply.',
  command: 'Runs an allowlisted command on the fleet host.',
  switch: 'Branches to different tasks based on conditions.',
  fork: 'Runs several branches in parallel.',
  for: 'Repeats its body once per item in a list.',
  run: 'Runs another workflow as a sub-step (reuse).',
  set: 'Sets values for later steps to reference.',
  python: 'Runs a deterministic Python step; its `inputs` are the only way values enter the code.',
  approval: 'Pauses the run and shows its preview to an operator, who approves or rejects it in Mission Control. Its output is the decision.',
  unknown: 'This editor does not model this task type yet — it RUNS, but the graph cannot show its shape. Read the YAML.',
}

// `python` becomes addable; `unknown` never is — it is a rendering state that
// says "this editor does not model what is in the file", not something an author
// can create.
const ADDABLE_TASK_TYPES: TaskType[] = ['agent', 'command', 'switch', 'fork', 'for', 'run', 'set', 'python', 'approval']

type TaskNodeData = Record<string, unknown> & {
  label: string
  taskType: TaskType
  parentId?: string
  /** ADR-136 D2 — worst finding on this step, and how many there are. */
  severity?: FindingSeverity
  findingCount?: number
  /** Delete this step from the draft. Absent in the read view, where there is
   *  no draft to delete from. */
  onDelete?: (name: string) => void
}
type TaskRFNode = RFNode<TaskNodeData, 'task'>

const NODE_WIDTH = 220
const NODE_HEIGHT = 56

function TaskNode({ id, data, selected }: NodeProps<TaskRFNode>) {
  return (
    <div
      className={`rounded-md border-2 px-3 py-2 text-xs shadow-sm ${TASK_STYLES[data.taskType]} ${
        selected ? 'ring-2 ring-indigo-500 ring-offset-1' : ''
      }`}
      style={{ width: NODE_WIDTH }}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-400 !w-2 !h-2" />
      <div className="flex items-center gap-1.5 min-w-0">
        <span aria-hidden="true">{TASK_ICON[data.taskType]}</span>
        <span className="font-medium truncate">{data.label}</span>
        {/* ADR-136 D2 — a step with a problem says so ON the step. The operator
            should not have to read a list to find out which one is wrong. */}
        {data.severity && (
          <span
            className={`ml-auto shrink-0 rounded px-1 text-[10px] font-semibold ${
              data.severity === 'blocks'
                ? 'bg-red-600 text-white'
                : data.severity === 'warns'
                  ? 'bg-fixed-amber-400 text-fixed-amber-950'
                  : 'bg-slate-300 text-slate-800'
            }`}
            title={
              data.severity === 'blocks'
                ? 'This will stop the change from shipping'
                : 'This works, but is worth a look'
            }
            aria-label={`${data.findingCount ?? 1} finding on this step`}
          >
            {data.findingCount && data.findingCount > 1 ? data.findingCount : '!'}
          </span>
        )}
        {/* Delete from the canvas. The node is where the operator is looking, so
            it is where the gesture belongs — the task tree's ✕ was the only one
            and meant leaving the graph to remove something you were pointing at.
            Both call the same guarded handler, which names the body and the
            referrers before anything is removed. */}
        {data.onDelete && (
          <button
            type="button"
            aria-label={`Delete step ${id}`}
            title="Delete this step"
            onClick={e => {
              // ReactFlow selects the node on click; without this the delete
              // also changes the selection under a confirm dialog.
              e.stopPropagation()
              // `id` is the TASK NAME. `data.label` is not — a `for` node's
              // label is "for each row in … (max 10)" and a `run` node's is
              // "run → other-workflow", so deleting by label would name a task
              // that does not exist and silently no-op.
              data.onDelete?.(id)
            }}
            className={`shrink-0 rounded px-1 text-[10px] leading-none text-gray-400
                        hover:bg-red-100 hover:text-red-600 ${data.severity ? '' : 'ml-auto'}`}
          >
            ✕
          </button>
        )}
      </div>
      {data.parentId && (
        <p className="mt-0.5 text-[10px] opacity-70 truncate">inside {data.parentId}</p>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-gray-400 !w-2 !h-2" />
    </div>
  )
}

const NODE_TYPES: NodeTypes = { task: TaskNode }

// ---------------------------------------------------------------------------
// GraphNode/GraphEdge → React Flow + dagre layout
// ---------------------------------------------------------------------------

function toRFNodes(nodes: GraphNode[], findings: Finding[] = [], onDelete?: (name: string) => void): TaskRFNode[] {
  return nodes.map(n => ({
    id: n.id,
    type: 'task',
    position: { x: 0, y: 0 },
    data: {
      label: n.label,
      taskType: n.taskType,
      parentId: n.parentId,
      // ADR-136 D2 — the worst thing known about this step, and how many.
      severity: worstSeverity(findings, n.id) ?? undefined,
      findingCount: findings.filter(f => f.nodeId === n.id).length || undefined,
      onDelete,
    },
    draggable: false,
    connectable: false,
  }))
}

export function toRFEdges(edges: GraphEdge[]): RFEdge[] {
  return edges.map(e => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.label,
    type: 'smoothstep',
    style:
      e.kind === 'data'
        ? { stroke: 'rgb(var(--mc-slate-400))', strokeDasharray: '4 3' }
        : { stroke: 'rgb(var(--mc-slate-600))' },
    labelStyle: { fontSize: 10, fill: 'rgb(var(--mc-slate-600))' },
    labelBgStyle: { fill: 'rgb(var(--mc-slate-50))' },
  }))
}

/** Top-down layout using dagre, keyed off the CONTROL-flow edges only (data
 * edges are toggle-gated for display and shouldn't reshuffle node positions). */
function layoutNodes(nodes: TaskRFNode[], controlEdges: RFEdge[]): TaskRFNode[] {
  const g = new dagre.graphlib.Graph()
  g.setGraph({ rankdir: 'TB', nodesep: 32, ranksep: 56 })
  g.setDefaultEdgeLabel(() => ({}))
  for (const n of nodes) g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT })
  for (const e of controlEdges) g.setEdge(e.source, e.target)
  dagre.layout(g)
  return nodes.map(n => {
    const pos = g.node(n.id)
    if (!pos) return n
    return { ...n, position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 } }
  })
}

// ---------------------------------------------------------------------------
// Legacy (stages/steps) fallback view
// ---------------------------------------------------------------------------

interface LegacyStep {
  id?: string
  label?: string
  run?: string
  command?: string
}
interface LegacyStage {
  id?: string
  label?: string
  steps?: LegacyStep[]
}

/** The slot's own activity path — the primary, which is never one of the
 *  changeset's supporting files. */
function selfPathFor(slot: { agent: string; name: string }): string {
  return `agents/${slot.agent}/activities/${slot.name}.yaml`
}

/** The SUPPORTING files in a copilot turn's changeset: everything but the slot's
 *  own activity, normalized. Empty when the turn touched no supporting file,
 *  which is what decides whether it owns them. */
function supportingFilesOf(
  files: ProposedChangesetFile[] | undefined,
  selfPath: string,
): ProposedChangesetFile[] {
  return (files ?? [])
    .filter(f => f.path !== selfPath)
    .map(f => ({ path: f.path, content: f.content ?? '', op: f.op ?? 'write' }))
}

/** The draft a session carries for one slot, or `undefined` when it carries none.
 *
 *  Three cases have to stay distinct, and two of them look identical in the JSON:
 *  the server marshals `Content` with `json:"content,omitempty"`
 *  (`editorsession/store.go`), so a WRITE of an empty draft comes back as
 *  `{path, op: "write"}` with no `content` at all — the same shape as an entry
 *  that never had content. An empty draft is the operator having cleared the
 *  pane, which is an edit; reading it as "no draft here" sends the editor to the
 *  published file and lets the autosave commit that over the stored row.
 *
 *  A `delete` entry is not a draft either way — it is the changeset removing the
 *  file — so it must not be read as an empty one. */
function storedDraftFor(
  files: ProposedChangesetFile[] | undefined,
  path: string,
): string | undefined {
  const entry = files?.find(f => f.path === path)
  if (!entry || entry.op === 'delete') return undefined
  return entry.content ?? ''
}

function LegacyView({ activity }: { activity: unknown }) {
  const stages =
    activity && typeof activity === 'object' && Array.isArray((activity as { stages?: unknown }).stages)
      ? ((activity as { stages: LegacyStage[] }).stages)
      : null

  if (!stages) {
    return (
      <div className="text-sm text-gray-500 p-6 text-center">
        Legacy activity (stages/steps) — see Raw YAML for the full file.
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4 overflow-auto h-full">
      <p className="text-xs text-gray-500">
        Legacy activity (stages/steps) — stages run in sequence; steps within a stage run in parallel.
      </p>
      {stages.map((stage, i) => (
        <div key={stage.id ?? i} className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-sm font-semibold text-gray-800">
            {i + 1}. {stage.label ?? stage.id ?? `stage ${i}`}
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {(stage.steps ?? []).map((step, j) => (
              <div key={step.id ?? j} className="rounded border border-gray-100 bg-gray-50 px-3 py-2">
                <p className="text-xs font-medium text-gray-700">{step.label ?? step.id ?? `step ${j}`}</p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{step.run ?? step.command ?? ''}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Editor mode switcher. ADR-121 D4 un-defers the ADR-075 node-graph Visual
// editor: the graph is the DEFAULT workflow view (see enterEditMode/
// startNewWorkflow below) — refining D1's "the copilot is the PRIMARY,
// default surface" to "AI/visual lead, YAML follows." Field/YAML, the raw
// manual-edit modes, stay demoted behind a clearly-secondary "Raw / advanced"
// toggle: fully reachable (ADR-121 D6 — the on-disk SoR stays authoritative),
// never the first thing shown, never removed.
//
// ADR-136 D1 — Chat is NOT here. A mode is a choice between surfaces, and the
// assistant was never an alternative to the canvas: it is the thing that talks
// ABOUT the canvas. It docks beneath it (AssistantDock below), always present,
// and EditorMode is now only "what is in the editor pane".
type EditorMode = 'yaml' | 'visual'

// ---------------------------------------------------------------------------
// The editor pane switch. ADR-121 D4 made the node graph the default view; D6
// keeps raw YAML reachable but demoted.
//
// It is now ONE control, not a tab row plus a drawer. Retiring "Field" left a
// single "Visual" tab whose only job was to be highlighted, above a drawer
// containing one item — two rows of chrome to express a two-state choice, on a
// page whose whole complaint was that it had no room. The graph is the view;
// YAML is a labelled escape hatch beside it, and the same control returns.
// ---------------------------------------------------------------------------

function ModeTabs({
  mode,
  onChange,
}: {
  mode: EditorMode
  onChange: (m: EditorMode) => void
}) {
  const onYAML = mode === 'yaml'
  return (
    <div className="border-b border-gray-100 flex items-center gap-2 px-2 py-1">
      <span className="text-xs font-medium text-gray-700">{onYAML ? 'Raw YAML' : 'Visual'}</span>
      <button
        type="button"
        onClick={() => onChange(onYAML ? 'visual' : 'yaml')}
        aria-pressed={onYAML}
        className={`ml-auto px-2 py-0.5 text-[11px] rounded border transition-colors ${
          onYAML
            ? 'border-gray-400 bg-white text-gray-800'
            : 'border-transparent text-gray-400 hover:text-gray-700 hover:border-gray-200'
        }`}
        title={onYAML
          ? 'Back to the graph'
          : 'Edit the raw YAML directly — the escape hatch behind the graph and the assistant (ADR-121 D6)'}
      >
        {onYAML ? '← Back to graph' : 'Raw / advanced: YAML'}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The assistant's conversation panel (ADR-079 D2 primary interface / 5d) — a
// conversation with the copilot over the shared draft. Purely presentational;
// all the request/merge logic lives in handleChatSend (main component) and
// applyCopilotResponse (workflowDraft.ts). ADR-136 D1 docks it under the canvas
// (see AssistantDock below) instead of rendering it as a mode.
// ---------------------------------------------------------------------------

// Exported for the ADR-142 capability-gap contract test. The gap was FILED in
// production for four rounds while this bubble rendered only the rationale, so
// "the operator can see it" is the property under test, and asserting it needs
// the component that draws it.
export function ChatBubble({ turn }: { turn: CopilotTurn }) {
  const isUser = turn.role === 'user'
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-xs whitespace-pre-wrap break-words ${
          isUser ? 'bg-indigo-600 text-white' : 'bg-gray-50 border border-gray-200 text-gray-800'
        }`}
      >
        {turn.content}
        {!isUser && turn.answer && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-200">
            <p className="text-[10px] text-gray-400">
              answer — the draft was not changed
              {turn.engine ? ` · ${turn.engine}` : ''}
            </p>
          </div>
        )}
        {!isUser && turn.capabilityGap && (
          <div
            className={`mt-1 rounded border px-2 py-1.5 ${
              turn.capabilityGap.filed
                ? 'border-indigo-300 bg-indigo-50'
                : 'border-amber-300 bg-amber-50'
            }`}
          >
            <p className="text-[10px] font-semibold text-slate-800">
              {turn.capabilityGap.filed
                ? 'Capability gap filed — this limitation is now tracked'
                : 'A capability gap was identified but NOT recorded'}
            </p>
            <p className="mt-0.5 text-[10px] text-slate-700">
              <span className="font-medium">Missing:</span> {turn.capabilityGap.want}
            </p>
            {turn.capabilityGap.filed ? (
              <p className="mt-0.5 text-[10px] text-slate-700">
                The draft above is the workaround that works today. The gap waits for the
                capability to become usable by this agent and then resumes on its own — it is
                not approved by being filed, and nothing reaches <code>main</code> without your
                review.
                {turn.capabilityGap.conversation_id && (
                  <>
                    {' '}
                    <a
                      className="underline"
                      href={`/chat?conversation=${turn.capabilityGap.conversation_id}`}
                    >
                      Open the case
                    </a>
                    .
                  </>
                )}
              </p>
            ) : (
              <p className="mt-0.5 text-[10px] text-slate-700">
                {turn.capabilityGap.note ||
                  'No reason was given. The limitation is real but nothing is tracking it.'}
              </p>
            )}
          </div>
        )}
        {!isUser && !turn.answer && turn.validation && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-200 space-y-1">
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                turn.validation.ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {turn.validation.ok ? '✓ valid' : `⚠ ${turn.validation.errors.length} error(s)`}
            </span>
            {!turn.validation.ok && turn.validation.errors.length > 0 && (
              <ul className="text-[10px] text-red-700 space-y-0.5 list-disc list-inside">
                {turn.validation.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )}
            <PendingReferences pending={turn.validation.pending} className="text-[10px]" />
            {/* ADR-134 D4 — say WHY the loop stopped, and whose problem it is.
                Without this the operator gets a rejected draft and a wall of
                validator text: the pre-D4 experience, with the classification
                sitting unused one layer below. */}
            {turn.blocker && (
              <div
                className={`mt-1 rounded border px-2 py-1.5 ${
                  turn.blocker.outside
                    ? 'border-amber-300 bg-amber-50'
                    : 'border-slate-300 bg-slate-50'
                }`}
              >
                <p className="text-[10px] font-semibold text-slate-800">
                  {turn.blocker.outside
                    ? 'Blocked by something outside this change'
                    : 'Could not fix this automatically'}
                  {` — gave up after ${turn.blocker.attempts} attempt${
                    turn.blocker.attempts === 1 ? '' : 's'
                  }`}
                </p>
                <p className="mt-0.5 text-[10px] text-slate-700">
                  {turn.blocker.outside
                    ? 'The fix is not in this workflow. Add the file it names to this change, or hand it to whoever owns it — retrying will not help.'
                    : 'Try a different approach, or edit the draft directly. The errors above are the whole reason it stopped.'}
                </p>
                {turn.blocker.files.length > 0 && (
                  <p className="mt-0.5 text-[10px] text-slate-600">
                    Implicated:{' '}
                    {turn.blocker.files.map((f, i) => (
                      <span key={f}>
                        {i > 0 ? ', ' : ''}
                        <code>{f}</code>
                      </span>
                    ))}
                  </p>
                )}
              </div>
            )}
            {(turn.model || turn.attempts || turn.engine) && (
              <p className="text-[10px] text-gray-400">
                {turn.engine ? `${turn.engine} · ` : ''}
                {turn.model}
                {turn.attempts ? ` · ${turn.attempts} attempt${turn.attempts === 1 ? '' : 's'}` : ''}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** The Edit/Ask switch. Extracted because the compact (collapsed-dock) composer
 *  puts it inline with the input row rather than on its own line, and an
 *  operator who cannot see whether they are in Ask mode will edit a draft they
 *  meant to ask about. */
function IntentToggle({
  intent,
  onIntentChange,
  disabled,
}: {
  intent: CopilotIntent
  onIntentChange: (i: CopilotIntent) => void
  disabled: boolean
}) {
  return (
    <div className="inline-flex rounded border border-gray-200 overflow-hidden shrink-0" role="group" aria-label="Copilot mode">
      {(['edit', 'ask'] as CopilotIntent[]).map(i => (
        <button
          key={i}
          type="button"
          aria-pressed={intent === i}
          disabled={disabled}
          onClick={() => onIntentChange(i)}
          className={`px-2 py-0.5 text-[11px] font-medium disabled:opacity-40 ${
            intent === i ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          {i === 'edit' ? 'Edit' : 'Ask'}
        </button>
      ))}
    </div>
  )
}

function ChatPanel({
  messages,
  input,
  onInputChange,
  onSend,
  sending,
  elapsedMs,
  intent,
  onIntentChange,
  // ADR-183 Phase 6 — the per-turn model override. Optional, so a caller that
  // does not offer the control (and every existing test) is unchanged: the
  // picker renders only when onModelChange is supplied.
  model,
  onModelChange,
  pin,
  onPinMine,
  onClearPin,
  onPinFleet,
  pinProposal,
  pinError,
  catalog,
  catalogLoading,
  error,
  onDismissError,
  unsavedAnswerError,
  unsavedAnswerGone,
  onRetryUnsavedAnswer,
  retryingUnsavedAnswer,
  runFinished,
  onStartNewRun,
  startingNewRun,
  startNewRunError,
  unconfigured,
  showConvertToDsl,
  onConvertToDsl,
  scopingControl,
  scopeFirst,
  onScopeFirstChange,
  compact,
}: {
  messages: CopilotTurn[]
  input: string
  onInputChange: (v: string) => void
  onSend: () => void
  sending: boolean
  // How long the in-flight turn has been running. The panel used to promise
  // "up to 20s", which was never true of a delegated Fleet Architect turn —
  // so it now reports the truth instead of a number someone guessed.
  elapsedMs: number
  // ADR-112 D3: Edit authors the draft, Ask answers a question about it and
  // changes nothing. Explicit, because an operator's intent is not reliably
  // inferable from their prose and guessing wrong either ignores a question or
  // rewrites an artifact they asked to be left alone.
  intent: CopilotIntent
  onIntentChange: (i: CopilotIntent) => void
  model?: ModelChoice
  onModelChange?: (next: ModelChoice) => void
  catalog?: ModelCatalogResponse | null
  catalogLoading?: boolean
  /** ADR-194 — the STANDING pin: what will answer, and which layer chose it.
   *  Optional, so a composer rendered without it behaves exactly as before. */
  pin?: AssistantModelView | null
  onPinMine?: (next: ModelChoice) => void
  onClearPin?: () => void
  onPinFleet?: (next: ModelChoice) => void
  pinProposal?: { pr_number: number; url: string } | null
  pinError?: string | null
  error: string | null
  onDismissError: () => void
  unsavedAnswerError?: string | null
  unsavedAnswerGone?: boolean
  onRetryUnsavedAnswer?: () => void
  retryingUnsavedAnswer?: boolean
  runFinished?: boolean
  onStartNewRun?: () => void
  startingNewRun?: boolean
  startNewRunError?: string | null
  unconfigured: boolean
  // The legacy-activity->DSL migration seed affordance (owner decision: seed
  // it in this chat rather than build a dedicated migration feature). Shown
  // only while the open draft is still legacy (see the caller in the main
  // component) and hidden/disabled while a copilot request is in flight.
  showConvertToDsl?: boolean
  onConvertToDsl?: () => void
  /** ADR-200 amendment (2026-09-29) — the per-run "Scope this request first" box. Offered
   *  only when this send would open a new run; optional, so a composer rendered without it
   *  (and every existing test) is unchanged. See ./scopeFirst. */
  scopingControl?: ScopingControl
  scopeFirst?: boolean
  onScopeFirstChange?: (next: boolean) => void
  // ADR-136 D1's risk row: "Assistant collapses to a single input row; the
  // canvas is the pane that never yields." Compact drops the transcript and
  // keeps the composer, so the assistant is still REACHABLE on a viewport that
  // cannot afford it — rather than the canvas shrinking to make room, or the
  // assistant disappearing and taking its findings with it.
  compact?: boolean
}) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [caret, setCaret] = useState(() => input.length)
  const [offers, setOffers] = useState<DataReferenceOffers | null>(null)
  const [offersError, setOffersError] = useState<string | null>(null)
  const [highlight, setHighlight] = useState(0)
  const [dismissedAt, setDismissedAt] = useState<number | null>(null)
  const [confirmStartNewRun, setConfirmStartNewRun] = useState(false)
  const offersRequested = useRef(false)
  const pendingCaretRef = useRef<{ value: string; caret: number } | null>(null)

  const mention = mentionAtCaret(input, caret)
  const hasMention = mention !== null
  const mentionStart = mention?.start ?? null
  const popupOpen = mention !== null && dismissedAt !== mention.start
  const rows = mention && offers ? mentionLevel(offers, mention) : []
  const rowsKey = rows.map(mentionRowKey).join('\u0000')

  useEffect(() => {
    if (!hasMention || offersRequested.current) return
    offersRequested.current = true
    setOffersError(null)
    api.getDataReferenceOffers('agent.run', [])
      .then(setOffers)
      .catch(error => {
        setOffersError(error instanceof Error ? error.message : String(error))
      })
  }, [hasMention])

  useEffect(() => {
    setHighlight(0)
  }, [rowsKey])

  useEffect(() => {
    if (dismissedAt !== null && mentionStart !== dismissedAt) {
      setDismissedAt(null)
    }
  }, [dismissedAt, mentionStart])

  useLayoutEffect(() => {
    const pending = pendingCaretRef.current
    if (!pending) return
    pendingCaretRef.current = null
    if (pending.value !== input) return

    const current = textareaRef.current
    if (!current) return
    current.focus()
    current.setSelectionRange(pending.caret, pending.caret)
    setCaret(pending.caret)
  }, [input])

  function applyPick(pick: MentionRow) {
    if (!mention) return

    const next = applyMentionPick(input, caret, mention, pick)
    if (next.value === input) {
      pendingCaretRef.current = null
      const current = textareaRef.current
      current?.focus()
      current?.setSelectionRange(next.caret, next.caret)
      setCaret(next.caret)
      return
    }

    pendingCaretRef.current = { value: next.value, caret: next.caret }
    onInputChange(next.value)
  }

  function onComposerKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return

    if (popupOpen && mention) {
      const action = mentionKeyAction(e.key, {
        count: rows.length,
        highlight,
      })

      if (action) {
        e.preventDefault()
        if (action.kind === 'move') {
          setHighlight(action.highlight)
        } else if (action.kind === 'pick') {
          const row = rows[highlight] ?? rows[0]
          if (row) applyPick(row)
        } else if (action.kind === 'close') {
          setDismissedAt(mention.start)
        }
        return
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (input.trim() && !sending) onSend()
    }
  }

  useEffect(() => {
    if (compact) return
    bottomRef.current?.scrollIntoView({ block: 'nearest' })
  }, [messages.length, sending, compact])

  return (
    <div className={compact ? 'shrink-0 flex flex-col' : 'flex-1 min-h-0 flex flex-col'}>
      {showConvertToDsl && (
        <div className="shrink-0 flex items-center justify-between gap-2 border-b border-indigo-100 bg-indigo-50/50 px-3 py-1.5">
          <span className="text-[11px] text-gray-500">
            Legacy single-prompt activity — migrate it to a deterministic DSL workflow.
          </span>
          <button
            type="button"
            disabled={sending}
            onClick={onConvertToDsl}
            className="text-xs font-medium text-indigo-700 underline hover:text-indigo-900 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            Convert to DSL flow
          </button>
        </div>
      )}
      {!compact && (
        <div data-testid="chat-transcript" className="flex-1 min-h-0 overflow-auto p-3 space-y-3">
          {messages.length === 0 && !sending && (
            <p className="text-xs text-gray-400 text-center pt-6 px-4">
              {intent === 'ask' ? (
                <>
                  Ask about the current draft — e.g. "which claims in the description does a task
                  actually enforce?" The copilot answers and changes nothing.
                </>
              ) : (
                <>
                  Describe the workflow you want — e.g. "classify inbound tickets, route by domain,
                  reject duplicates" — or an edit to the current draft. The copilot drafts it; you
                  review before proposing.
                </>
              )}
            </p>
          )}
          {messages.map((m, i) => (
            <ChatBubble key={i} turn={m} />
          ))}
          {sending && (
            <div className="flex items-center gap-2 text-xs text-gray-400 italic">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
              {intent === 'ask' ? 'The Fleet Architect is reading the draft' : 'The Fleet Architect is working on it'}
              {elapsedMs >= 3000 ? ` — ${Math.round(elapsedMs / 1000)}s elapsed` : '…'}
              {elapsedMs >= 60000 && ' (a full authoring turn can take a few minutes)'}
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}
      {unsavedAnswerError && (
        <div className="flex items-center justify-between gap-2 text-xs text-amber-900 bg-amber-50 border-t border-amber-200 px-3 py-2">
          <span>This answer was produced but not saved: {unsavedAnswerError}</span>
          {!unsavedAnswerGone && <button
            type="button"
            onClick={onRetryUnsavedAnswer}
            disabled={retryingUnsavedAnswer}
            className="shrink-0 underline font-medium hover:text-amber-950 disabled:opacity-50"
          >
            {retryingUnsavedAnswer ? 'Retrying…' : 'Retry save'}
          </button>}
        </div>
      )}
      {unconfigured ? (
        <p className="text-xs text-amber-800 bg-amber-50 border-t border-amber-100 px-3 py-2">
          The AI copilot isn't configured for this fleet — use Field/YAML modes to author manually.
        </p>
      ) : (
        <>
          {runFinished && (
            <div data-testid="chat-run-finished" className="border-t border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
              <p>This workflow's authoring run is finished, so your message was not sent. If it has not been proposed yet, you can still propose it from Runs. To make another change, start a new run. Your draft and this conversation are kept.</p>
              {startNewRunError && <p className="mt-1 text-red-600" role="alert">{startNewRunError}</p>}
              {!confirmStartNewRun ? (
                <button type="button" disabled={startingNewRun} className="mt-1 font-medium underline disabled:opacity-50" onClick={() => setConfirmStartNewRun(true)}>
                  {startingNewRun ? 'Starting…' : 'Start a new run'}
                </button>
              ) : (
                <div className="mt-2">
                  <p>Discard the finished run's gate verdicts and claims? Your draft and the conversation are not touched.</p>
                  <div className="mt-1 flex gap-3">
                    <button type="button" className="font-medium underline" onClick={() => {
                      setConfirmStartNewRun(false)
                      onStartNewRun?.()
                    }}>Discard the run</button>
                    <button type="button" className="underline" onClick={() => setConfirmStartNewRun(false)}>Keep it</button>
                  </div>
                </div>
              )}
            </div>
          )}
          {error && (
            <div className="flex items-center justify-between gap-2 text-xs text-red-600 bg-red-50 border-t border-red-100 px-3 py-1.5">
              <span>{error}</span>
              <button
                type="button"
                onClick={onDismissError}
                aria-label="Dismiss error"
                className="text-red-400 hover:text-red-700 shrink-0"
              >
                ✕
              </button>
            </div>
          )}
          {!compact && (
            <div className="border-t border-gray-100 px-2 pt-2 flex items-center gap-2">
              <IntentToggle intent={intent} onIntentChange={onIntentChange} disabled={sending} />
              <span className="text-[11px] text-gray-400">
                {intent === 'ask'
                  ? 'Answers a question about the draft — never changes it.'
                  : 'Authors or edits the draft; you review before proposing.'}
              </span>
              {/* ADR-183 Phase 6 — THE ORIGINAL REQUEST, as a control: answer
                  THIS turn on a different model, with no env-var edit and no
                  redeploy.
                  BEHIND A LINK, not inline (fleet owner, after #2422): it "would
                  barely ever get used and should be a link to a modal for
                  selection and not this constant use of real estate". A picker,
                  its label and its unavailable-vendor summary sat above every
                  author's input box on every turn, for an experiment run
                  occasionally. What stays visible is an override that is IN
                  FORCE — see ModelOverrideLink.
                  Gated on mc_callable rather than selectable, because this call
                  is placed by Mission Control itself — a vendor whose key is in
                  1Password but not in this container must not be offered here,
                  or the turn fails later with a 503 naming neither cause. */}
              {onModelChange && (
                <ModelOverrideLink
                  value={model ?? { provider: '', model: '' }}
                  onChange={onModelChange}
                  catalog={catalog ?? null}
                  loading={catalogLoading}
                  disabled={sending}
                  gate="mc_callable"
                  pin={pin ?? null}
                  onPinMine={onPinMine}
                  onClearPin={onClearPin}
                  onPinFleet={onPinFleet}
                  proposal={pinProposal ?? null}
                />
              )}
            </div>
          )}
          {/* ADR-194 — THE STATE IS NOT COLLAPSED, only the menu is. The per-turn
              override hides behind a link at the fleet owner's request, and
              correctly so; the ANSWER does not. Measured on spartan 2026-09-21,
              25 of 25 runs went to the deployment default while four profiles
              named another vendor and nobody could tell. */}
          {pin && <AssistantModelState pin={pin} catalog={catalog ?? null} />}
          {pinError && (
            <div className="text-xs text-red-600" role="alert">{pinError}</div>
          )}
          {!compact && scopingControl && onScopeFirstChange && (
            <ScopeFirstControl
              control={scopingControl}
              checked={!!scopeFirst}
              onChange={onScopeFirstChange}
              disabled={sending}
            />
          )}
          <div className="relative">
            {popupOpen && mention && (
              <DataMentionPopup
                offers={offers}
                error={offersError}
                mention={mention}
                highlight={highlight}
                onHighlight={setHighlight}
                onPick={applyPick}
              />
            )}
            <div className={`p-2 flex items-end gap-2 ${compact ? 'border-t border-gray-100' : 'pt-1.5'}`}>
              {/* Collapsed, the toggle moves onto the one row that is left. It
                  cannot be dropped: an operator who cannot see they are in Ask
                  mode will edit a draft they meant only to ask about. */}
              {compact && <IntentToggle intent={intent} onIntentChange={onIntentChange} disabled={sending} />}
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => {
                  onInputChange(e.target.value)
                  setCaret(e.target.selectionStart ?? e.target.value.length)
                }}
                onSelect={e => {
                  if (pendingCaretRef.current) return
                  setCaret(e.currentTarget.selectionStart ?? e.currentTarget.value.length)
                }}
                onKeyDown={onComposerKeyDown}
                rows={compact ? 1 : 2}
                placeholder={
                  intent === 'ask'
                    ? 'Ask a question about the current draft…'
                    : 'Describe the workflow, or an edit to the current draft…'
                }
                disabled={sending}
                aria-expanded={popupOpen}
                aria-controls={popupOpen ? 'data-mention-popup' : undefined}
                // Drag the corner to grow it, same as the Chat composer
                // (Chat.tsx). A workflow instruction is frequently a paragraph,
                // and `resize-none` made the operator write it through a
                // two-line slot. `max-h-[60vh]` keeps a dragged-tall composer
                // from swallowing the transcript above it.
                className="flex-1 text-sm border border-gray-200 rounded px-2 py-1.5 resize-y min-h-[2.25rem] max-h-[60vh] focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:bg-gray-50"
              />
              <button
                type="button"
                disabled={sending || !input.trim()}
                onClick={onSend}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40 shrink-0"
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
            {!compact && (
              <DataMentionChips value={input} offers={offers} onChange={onInputChange} />
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// AssistantDock — ADR-136 D1. The assistant lives BENEATH the canvas, always
// present, and is not a mode.
//
// What it replaces: `mode === 'chat'` rendered ChatPanel INSTEAD of the canvas,
// and the inspector's editable form was gated on `mode === 'visual'`, so asking
// a question cost the operator both the picture and the panel describing what
// they were asking about. The assistant's whole value is that it can point at
// the thing being discussed, and it cannot point at a panel that unmounted to
// make room for it.
//
// Height: the expanded dock defaults to a SHARE of the centre column (38%,
// capped), never a fixed number of pixels — the D1 risk row made structural, so
// the dock takes only what is left over. The operator can then DRAG the divider
// to rebalance, within bounds that keep both panes usable: D1's "the canvas
// never yields" is a floor under the graph, not a ban on the operator deciding
// they would rather read a long answer for a minute.
// ---------------------------------------------------------------------------

/** Below this viewport height the dock defaults to its single-row form. Chosen
 *  as the point where a 38% dock leaves the canvas under ~300px — enough for
 *  the toolbar and two ranks of nodes, which is no longer a graph. The operator
 *  can still expand it; this is the DEFAULT, not a lock. */
const SHORT_VIEWPORT_PX = 720

/** The canvas floor. Below this the graph stops being a graph — the toolbar
 *  plus roughly two ranks of nodes — so the divider stops rather than letting
 *  the dock take the pane it exists to talk about. */
const MIN_CANVAS_PX = 220
/** The dock floor: its header plus the composer row. Dragging below this snaps
 *  to the collapsed single-row form, which is the honest end of that gesture
 *  rather than a squashed transcript nobody can read. */
const MIN_DOCK_PX = 140
/** Remembered across sessions — an operator who wants a tall assistant wants it
 *  every time, and re-dragging on every page load is the kind of small tax that
 *  makes a resizable pane feel worse than a fixed one. */
const DOCK_HEIGHT_KEY = 'mc.workflows.assistantDockHeight'

/** ADR-111 — the inspector column's tabs. Split by LIFECYCLE, not by
 *  convenience: Inspector changes on every node click, Findings changes as the
 *  draft does, Files is touched once per change. Stacking all three in one
 *  scroll meant the once-per-change content permanently crowded out the
 *  per-click content. */
const INSPECTOR_TABS: { id: InspectorTab; label: string }[] = [
  { id: 'inspector', label: 'Inspector' },
  { id: 'findings', label: 'Findings' },
  { id: 'files', label: 'Files' },
]
type InspectorTab = 'inspector' | 'findings' | 'files'

/** The inspector column's floor — narrower and the field forms wrap to
 *  uselessness. */
const MIN_PANEL_PX = 260
/** The canvas floor when the inspector is being widened. Same principle as the
 *  dock's: the operator may rebalance, but not to nothing. */
const MIN_CENTER_PX = 420
const PANEL_WIDTH_KEY = 'mc.workflows.inspectorWidth'

function readStoredPanelWidth(): number | null {
  try {
    const raw = window.localStorage.getItem(PANEL_WIDTH_KEY)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) && n >= MIN_PANEL_PX ? n : null
  } catch {
    return null
  }
}

/** The workflow list's width. The default stays what it always was; the drag
 *  exists because fleets name workflows descriptively, and at 260px a long
 *  kebab-case name lost its distinguishing tail behind the badges. */
const DEFAULT_LIST_PX = 260
const MIN_LIST_PX = 200
/** A list wider than this is a second canvas, not a list. */
const MAX_LIST_PX = 640
/** The grid's `gap-4`, twice — the list's ceiling is what is left after the
 *  inspector, the canvas floor, and both gutters. */
const GRID_GAPS_PX = 32
const LIST_WIDTH_KEY = 'mc.workflows.listWidth'

function readStoredDockHeight(): number | null {
  try {
    const raw = window.localStorage.getItem(DOCK_HEIGHT_KEY)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) && n >= MIN_DOCK_PX ? n : null
  } catch {
    // Private mode, or storage disabled. A remembered height is a convenience;
    // losing it must never stop the editor rendering.
    return null
  }
}

/** Relative time for a catalog row. ADR-137 D2 leans on this: a resumed
 *  conversation may discuss a draft that has moved on, and "3 days ago" is what
 *  lets the operator hold the two timelines apart. */
function agoLabel(iso: string): string {
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return ''
  const mins = Math.round((Date.now() - then) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  return days === 1 ? 'yesterday' : `${days}d ago`
}

/**
 * ADR-137 D4 — the catalog, behind a tab in the dock.
 *
 * Not a modal and not a third pane: ADR-136 D1 spent real effort making the
 * canvas the pane that never yields, and covering it to show a list of chats
 * would hand back the thing the dock exists to protect.
 */
/** ADR-137 D8 — the author filter. `MINE` is the default; the catalog is
 *  fleet-wide but opens on your own, because the common case is resuming your own
 *  train of thought and a list that opens on colleagues' makes you hunt for it. */
export const AUTHOR_MINE = '__mine__'
export const AUTHOR_ANYONE = '__all__'

/** The distinct authors on this slot, for the filter control. Derived from the
 *  rows rather than fetched: the dock already holds the whole slot. */
export function authorsOf(items: ConversationSummary[], self: string): string[] {
  const seen = new Set<string>()
  for (const c of items) if (c.operator && c.operator !== self) seen.add(c.operator)
  return [...seen].sort()
}

export function filterByAuthor(
  items: ConversationSummary[],
  filter: string,
  self: string,
): ConversationSummary[] {
  if (filter === AUTHOR_ANYONE) return items
  if (filter === AUTHOR_MINE) return items.filter(c => c.operator === self)
  return items.filter(c => c.operator === filter)
}

/** Short form of an email for a dense list — the local part is enough to tell
 *  colleagues apart, and the column is ~90px wide. Full address in the title. */
function authorLabel(email: string): string {
  return email.split('@')[0] || email
}

function ConversationHistory({
  items,
  activeId,
  loading,
  error,
  onResume,
  onDelete,
  self,
  filter,
  onFilterChange,
  allItems,
  canDeleteAny,
}: {
  items: ConversationSummary[]
  activeId: string | null
  loading: boolean
  error: string | null
  onResume: (id: string) => void
  onDelete: (id: string, title: string) => void
  /** The signed-in operator — who "Mine" means, and who may delete what. */
  self: string
  filter: string
  onFilterChange: (f: string) => void
  /** Unfiltered, so the filter's options do not disappear as it is used. */
  allItems: ConversationSummary[]
  /** ADR-137 D9 — fleet_owner may delete anyone's; everyone else, only their own. */
  canDeleteAny: boolean
}) {
  // BOUNDED TO THE RECENT ONES, with the rest one click away.
  //
  // The catalog holds up to ADR-137 D6's per-slot cap and every authoring turn adds to
  // it, so a workflow under active development reaches three figures within a day —
  // spartan's weekly-health passed 115 in one. A list that long buries the conversation
  // an operator came back for underneath ninety they will never reopen, and it is the
  // FIRST thing the History tab shows.
  //
  // Five, because the reason to open History is almost always "the one just before this
  // one". Anything older is a search, and a search wants the full list deliberately
  // rather than by scrolling.
  const [showAll, setShowAll] = useState(false)
  const RECENT = 5
  const shown = showAll ? items : items.slice(0, RECENT)
  const hidden = items.length - shown.length
  const others = authorsOf(allItems, self)
  // The control is offered whenever a colleague has ever talked about this
  // workflow. On a slot only you have touched it would be a dropdown with one
  // real option, so it stays out of the way.
  const picker = others.length > 0 && (
    <div className="flex items-center gap-1.5 border-b border-gray-100 px-3 py-1.5">
      <label htmlFor="conv-author" className="text-[10px] uppercase tracking-wider text-gray-400">
        By
      </label>
      <select
        id="conv-author"
        value={filter}
        onChange={e => onFilterChange(e.target.value)}
        className="flex-1 min-w-0 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-700"
      >
        <option value={AUTHOR_MINE}>Me</option>
        <option value={AUTHOR_ANYONE}>Anyone ({allItems.length})</option>
        {others.map(a => (
          <option key={a} value={a}>{authorLabel(a)}</option>
        ))}
      </select>
    </div>
  )

  if (error) {
    return <p className="p-3 text-xs text-red-600">{error}</p>
  }
  if (loading && items.length === 0 && allItems.length === 0) {
    return <p className="p-3 text-xs text-gray-400 animate-pulse">Loading…</p>
  }
  if (items.length === 0) {
    return (
      <>
        {picker}
        <p className="p-3 text-xs text-gray-400">
          {/* Distinguish "nobody has" from "you have not" — with the fleet-wide
              catalog these are different facts, and the second one has an
              obvious next move. */}
          {allItems.length > 0
            ? 'None of your own conversations about this workflow yet — switch to Anyone to read a colleague’s.'
            : 'No earlier conversations about this workflow yet. This one will be here when you come back.'}
        </p>
      </>
    )
  }
  return (
    <>
      {picker}
    <ul className="flex-1 min-h-0 overflow-auto divide-y divide-gray-100">
      {shown.map(c => (
        <li
          key={c.id}
          className={`flex items-center gap-2 px-3 py-2 text-xs ${c.id === activeId ? 'bg-indigo-50/60' : 'hover:bg-gray-50'}`}
        >
          <button
            type="button"
            onClick={() => onResume(c.id)}
            className="flex-1 min-w-0 text-left"
            title="Open this conversation. Your draft is not changed."
          >
            <span className="block truncate text-gray-800">{c.title || 'Untitled conversation'}</span>
            <span className="block text-[10px] text-gray-400">
              {agoLabel(c.updated_at)} · {c.turns} turn{c.turns === 1 ? '' : 's'}
              {/* Whose thinking this was. Shown only for a COLLEAGUE'S: labelling
                  your own rows "me" on the filter that already says Me is noise,
                  and the whole reason to name an author is to tell someone
                  else's reasoning apart from your own. */}
              {c.operator && c.operator !== self && (
                <> · <span className="text-gray-500" title={c.operator}>{authorLabel(c.operator)}</span></>
              )}
              {c.id === activeId && ' · open'}
            </span>
          </button>
          {/* ADR-137 D3 — the conversation that shipped a change, and the PR it
              shipped. This is what makes the catalog an index into why a
              workflow is shaped as it is, rather than a pile of chats. */}
          {c.pr_number !== undefined && (
            <span
              className="shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800"
              title="This conversation produced a proposal"
            >
              PR #{c.pr_number}
            </span>
          )}
          {/* ADR-137 D9 — read is fleet-wide, erase is not. A conversation you did
              not write carries no ✕ at all, rather than one that fails: an
              affordance that does nothing is worse than an absent one, and the
              server refuses it regardless. fleet_owner keeps it on every row —
              the one way to remove something posted in error. */}
          {(canDeleteAny || c.operator === self) && (
            <button
              type="button"
              onClick={() => onDelete(c.id, c.title)}
              aria-label={`Delete conversation: ${c.title || 'Untitled conversation'}`}
              className="shrink-0 text-gray-300 hover:text-red-600"
              title={
                c.operator === self
                  ? 'Delete this conversation'
                  : `Delete ${authorLabel(c.operator)}’s conversation (fleet owner)`
              }
            >
              ✕
            </button>
          )}
        </li>
      ))}
      {hidden > 0 && (
        <li className="px-3 py-2">
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="text-[11px] text-indigo-700 hover:underline">
            Show {hidden} older conversation{hidden === 1 ? '' : 's'}
          </button>
        </li>
      )}
      {showAll && items.length > RECENT && (
        <li className="px-3 py-2">
          <button
            type="button"
            onClick={() => setShowAll(false)}
            className="text-[11px] text-gray-600 hover:underline">
            Show only the {RECENT} most recent
          </button>
        </li>
      )}
    </ul>
    </>
  )
}

function AssistantDock({
  expanded,
  onToggleExpanded,
  scopeNodeId,
  onClearScope,
  unread,
  height,
  onResize,
  onDragCollapse,
  tab,
  onTabChange,
  history,
  historyLoading,
  historyError,
  activeConversationId,
  onResumeConversation,
  onDeleteConversation,
  onNewConversation,
  historySelf,
  historyFilter,
  onHistoryFilterChange,
  historyCanDeleteAny,
  railAgent,
  railWorkflow,
  onRunProposed,
  onContinuePhase,
  canEdit,
  selectedRunId,
  onSelectRun,
  ...chat
}: {
  expanded: boolean
  onToggleExpanded: () => void
  /** ADR-136 D1 — the node selected on the canvas. The conversation is scoped
   *  to it, and saying so is half the point: an operator who cannot see the
   *  scope cannot tell whether "this step" was understood. */
  scopeNodeId: string | null
  onClearScope: () => void
  /** An answer arrived while the dock was collapsed. Without this the reply to
   *  a question typed into the one-row composer lands somewhere the operator
   *  cannot see, which reads as the assistant having failed. */
  unread: boolean
  /** The dragged height in px, or null to use the default share of the column. */
  height: number | null
  /** Called with a new height as the divider is dragged. */
  onResize: (px: number) => void
  /** Dragged below MIN_DOCK_PX — the gesture's honest end is the collapsed form. */
  onDragCollapse: () => void
  /** ADR-137 D4 — the catalog behind the second tab. */
  tab: 'chat' | 'history' | 'process'
  onTabChange: (t: 'chat' | 'history' | 'process') => void
  /** The slot the Process tab reads. Passed in rather than read from a route param
   *  here: the dock is presentational, and the phase state belongs to the SLOT the
   *  editor has open, which is the same (agent, workflow) the copilot turn was sent
   *  for. Reading it from anywhere else could show one workflow's rail over
   *  another's draft. */
  railAgent: string
  railWorkflow: string
  onRunProposed?: (agent: string, workflow: string) => void
  /** ADR-167 D8 — drive the run's next phase. Its own prop rather than reusing
   *  ChatPanel's `onSend`, which takes no text: the continuation is a specific
   *  sentence, and a button that sent whatever happened to be in the composer would
   *  be a different action wearing this one's label. */
  onContinuePhase: (prompt: string) => void
  /** Whether the operator is editing. Gates the WRITE controls only — the run history
   *  below them is readable either way, which is the whole reason the dock is no longer
   *  hidden outside edit mode. */
  canEdit: boolean
  /** A run deep-linked into the page (`?run=…`), so a run URL stays shareable. */
  selectedRunId?: string
  onSelectRun?: (id: string) => void
  history: ConversationSummary[]
  historyLoading: boolean
  historyError: string | null
  activeConversationId: string | null
  onResumeConversation: (id: string) => void
  onDeleteConversation: (id: string, title: string) => void
  onNewConversation: () => void
  /** ADR-137 D8/D9 — the catalog is the fleet's, so the dock has to know who is
   *  looking at it: which rows are "mine", and which carry a delete. */
  historySelf: string
  historyFilter: string
  onHistoryFilterChange: (f: string) => void
  historyCanDeleteAny: boolean
} & Omit<ComponentProps<typeof ChatPanel>, 'compact'>) {
  const dockRef = useRef<HTMLDivElement>(null)
  /** How many runs the record holds, reported up by WorkflowRuns.
   *
   *  HELD HERE because the rail renders the list as a CHILD and cannot see it, and the
   *  rail's headline needs it: without it the header said "No authoring run on this
   *  workflow yet." over five runs, one of them still going. Reported by the operator with
   *  a screenshot, 2026-09-17. */
  const [railHistoryCount, setRailHistoryCount] = useState(0)
  /** The id of the run in the LIVE SLOT, reported up by the rail and passed down to the run
   *  list — which reads the RECORD and cannot tell which row is live. The difference decides
   *  whether a row may offer redrive (404s on an empty slot) or resume. */
  const [railLiveRunId, setRailLiveRunId] = useState<string | null>(null)
  /** Whether the run list is offering "Resume this run", reported up by WorkflowRuns.
   *
   *  HELD HERE for the same reason as the count: the rail renders the list as a CHILD, and
   *  its headline described that control — "N runs … cannot be resumed" — while the card
   *  below it offered exactly that. */
  const [railResumable, setRailResumable] = useState(false)

  /** Resize by dragging the divider. Pointer events (not mouse) so a trackpad,
   *  a touchscreen and a stylus all work, and pointer CAPTURE so the drag keeps
   *  tracking when the cursor leaves the band — without it a quick drag
   *  detaches the moment you outrun the handle. */
  function startDrag(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault()
    const handle = e.currentTarget
    handle.setPointerCapture(e.pointerId)
    // The column the two panes divide. Its top bounds the canvas; the dock's
    // height is whatever is left below the pointer.
    const column = dockRef.current?.parentElement
    if (!column) return

    const onMove = (ev: PointerEvent) => {
      const rect = column.getBoundingClientRect()
      const proposed = rect.bottom - ev.clientY
      const ceiling = Math.max(MIN_DOCK_PX, rect.height - MIN_CANVAS_PX)
      if (proposed < MIN_DOCK_PX) {
        onDragCollapse()
        return
      }
      onResize(Math.min(proposed, ceiling))
    }
    const onUp = (ev: PointerEvent) => {
      handle.releasePointerCapture(ev.pointerId)
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', onUp)
    }
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', onUp)
  }

  /** What the History tab lists under the current author filter (ADR-137 D8) —
   *  and therefore what its count must report. */
  const visibleHistory = filterByAuthor(history, historyFilter, historySelf)
  const railShown = tab === 'process' && expanded

  /** Keyboard equivalent — a pointer-only divider is unreachable for anyone who
   *  cannot use one, and this is the control that decides how much of the page
   *  each pane gets. */
  function onHandleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    const column = dockRef.current?.parentElement
    const current = dockRef.current?.getBoundingClientRect().height ?? MIN_DOCK_PX
    const ceiling = column
      ? Math.max(MIN_DOCK_PX, column.getBoundingClientRect().height - MIN_CANVAS_PX)
      : current
    const next = current + (e.key === 'ArrowUp' ? 24 : -24)
    if (next < MIN_DOCK_PX) return onDragCollapse()
    onResize(Math.min(next, ceiling))
  }

  return (
    <div
      ref={dockRef}
      className="relative shrink-0 flex flex-col border-t border-gray-200 bg-white"
      style={expanded ? { height: height ?? 'min(38%, 360px)' } : undefined}
      data-testid="assistant-dock"
    >
      {expanded && (
        // ABSOLUTE, and stacked — not a static child with a negative margin.
        // As a static child the strip had no layout height and was drawn in the
        // band above the dock's border, which the canvas pane owns; that pane is
        // `relative`, and CSS paints positioned descendants after static ones,
        // so the strip rendered UNDER ReactFlow and ReactFlow took the
        // pointerdown. What survived was a hairline at the very edge that an
        // operator had to hunt for. The inspector's divider is `absolute … z-10`
        // and has always been grabbable, which is why the right-hand column
        // "already works" — this matches it, over a 10px band straddling the
        // line so the target is where the eye already is.
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize the assistant"
          tabIndex={0}
          onPointerDown={startDrag}
          onKeyDown={onHandleKeyDown}
          data-testid="assistant-dock-resizer"
          className="absolute left-0 right-0 -top-2 h-2.5 z-10 cursor-row-resize group
                     flex items-center justify-center
                     hover:bg-indigo-100/70 focus:bg-indigo-100 focus:outline-none"
          title="Drag to resize — or use the arrow keys"
        >
          <span className="h-1 w-10 rounded-full bg-gray-300 group-hover:bg-indigo-400" />
        </div>
      )}
      <div className="shrink-0 flex items-center gap-2 px-2 py-1 bg-gray-50/80 border-b border-gray-100">
        {/* ADR-137 D4 — two tabs, the live conversation first. History carries a
            count only once there IS history, so the tab says something exists
            rather than waiting to be discovered. */}
        {/* ADR-166 D7 / ADR-167 D6 — a third tab, because the phase state has to be
            readable WHILE the run runs. The assistant tab shows what the run SAID; this
            one shows what its gates DECIDED, which is the part an operator cannot get
            from the prose. */}
        {/* THE ASSISTANT TAB IS EDIT-ONLY. Its composer writes the draft, so offering it
            outside edit mode would be a write control on a read-only surface — and its
            Edit/Ask switch is literally a second button labelled "Edit", which never
            coexisted with the page's own until the dock stopped being edit-gated. */}
        {(canEdit ? (['chat', 'history', 'process'] as const) : (['history', 'process'] as const)).map(t => (
          <button
            key={t}
            type="button"
            aria-pressed={tab === t}
            onClick={() => onTabChange(t)}
            className={`px-1.5 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider transition-colors ${
              tab === t ? 'bg-white text-gray-700 shadow-sm' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {/* The count is of what the tab will SHOW — the current filter's rows,
                not the slot's total. A badge that says 12 opening onto 2 is a
                tab that lies about its own contents. */}
            {/* "Runs", not "Process". The tab used to show only the run in flight, so a
                finished run had no home on this page and its history lived behind a
                separate route you navigated away to. The current run IS the newest run —
                one tab, current expanded, earlier collapsed beneath it. */}
            {t === 'chat' ? 'Assistant' : t === 'process' ? 'Runs' : `History${visibleHistory.length > 0 ? ` (${visibleHistory.length})` : ''}`}
          </button>
        ))}
        <button
          type="button"
          onClick={onNewConversation}
          className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-600 hover:border-indigo-300 hover:text-indigo-700"
          title="Start a fresh conversation. The current one stays in History."
        >
          + New chat
        </button>
        {tab === 'chat' && scopeNodeId && (
          <span
            className="inline-flex items-center gap-1 rounded bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 text-[10px] text-indigo-700"
            title="Your next turn is about this step. Clear it to ask about the whole workflow."
          >
            about <span className="font-mono">{scopeNodeId}</span>
            <button
              type="button"
              onClick={onClearScope}
              aria-label="Clear the selected step"
              className="text-indigo-400 hover:text-indigo-700"
            >
              ✕
            </button>
          </span>
        )}
        {!expanded && unread && (
          <button
            type="button"
            onClick={onToggleExpanded}
            className="text-[10px] font-medium text-indigo-700 underline hover:text-indigo-900"
          >
            New answer — show it
          </button>
        )}
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          className="ml-auto text-[11px] text-gray-500 hover:text-gray-800"
          title={expanded ? 'Collapse the assistant to one row — the canvas keeps its space either way' : 'Expand the assistant'}
        >
          {expanded ? '▾ Collapse' : '▸ Expand'}
        </button>
      </div>
      {/* THE RAIL OWNS THE SCROLLING, and the run history is its CHILD.
       *
       * It used to be two siblings inside one scroller, with the header `sticky` — and
       * sticky cannot leave its own parent's box. The header pinned only while the
       * rail's div was on screen and then scrolled away with it, which is precisely the
       * reported behaviour: "it stays if you scroll some but not for total surface
       * vertical scroll."
       *
       * Nesting the list inside the rail lets the rail be a flex column with a header
       * that does not scroll at all. That is a stronger guarantee than sticky rather
       * than a workaround for it: there is no scroll position at which the header can
       * be off screen, because it is not in the scrolling box.
       *
       * Keep it mounted while hidden so a checkpoint reached on another tab still raises
       * Input needed (Asana 1219065149686528). WorkflowRuns renders only while shown, so
       * its own 5s poll does not run behind other tabs. `contents` keeps the rail root a
       * direct flex child; the class swap prevents `.contents` overriding `[hidden]`.
       */}
      <div
        hidden={!railShown}
        className={railShown ? 'contents' : 'hidden'}
        data-testid="authoring-rail-slot"
      >
        <AuthoringRail
          agent={railAgent} workflow={railWorkflow}
          onProposed={onRunProposed}
          // THE SAME SEND A TYPED TURN USES, following the "Convert to DSL flow"
          // precedent rather than opening a parallel path: the continuation has to land
          // in the conversation, the draft and the run exactly as a typed one does, or
          // the button would advance a run the transcript has no record of.
          onContinue={canEdit ? onContinuePhase : undefined}
          continuing={chat.sending}
          // The write controls — drive a phase, answer a checkpoint, discard the run —
          // are the only part of this tab that mutates anything. The state above them and
          // the history below are readable either way.
          canEdit={canEdit}
          // HOW MANY RUNS THE RECORD HOLDS, so the rail can tell "nothing has ever run
          // here" from "your run happened and its live state is gone". Held here because
          // the rail renders WorkflowRuns as a child and cannot see its list.
          historyCount={railHistoryCount}
          // AND WHETHER THE LIST IS OFFERING RESUME, so the headline cannot say the runs
          // below it cannot be resumed while one of their cards resumes them.
          resumable={railResumable}
          onLiveRun={setRailLiveRunId}
        >
          {railShown && <WorkflowRuns
            agent={railAgent} workflow={railWorkflow}
            viewer={historySelf}
            selectedRunId={selectedRunId}
            onSelectRun={onSelectRun}
            onHistoryCount={setRailHistoryCount}
            onResumable={setRailResumable}
            liveRunId={railLiveRunId}
            // Only in edit mode, and WorkflowRuns applies it only to the CURRENT run: the
            // engine keeps one run per slot, so an earlier run has nothing to rewind — it
            // is history, not state.
            canRedrive={canEdit}
          />}
        </AuthoringRail>
      </div>
      {railShown ? null : tab === 'history' && expanded ? (
        <ConversationHistory
          items={visibleHistory}
          allItems={history}
          activeId={activeConversationId}
          loading={historyLoading}
          error={historyError}
          onResume={onResumeConversation}
          onDelete={onDeleteConversation}
          self={historySelf}
          filter={historyFilter}
          onFilterChange={onHistoryFilterChange}
          canDeleteAny={historyCanDeleteAny}
        />
      ) : canEdit ? (
        // Collapsed, History has nowhere to render a list — so the composer
        // stays, which is the one thing a single row can usefully be.
        <ChatPanel {...chat} compact={!expanded} />
      ) : (
        // ...but NOT to a reader. The composer writes the draft, and it carries its own
        // Edit/Ask switch — literally a second button labelled "Edit", which never
        // coexisted with the page's own until the dock stopped being edit-gated. A
        // collapsed dock outside edit mode says what it holds and offers the way in.
        <button
          type="button"
          onClick={onToggleExpanded}
          className="px-3 py-2 text-left text-[11px] text-gray-500 hover:text-gray-700">
          Authoring runs for this workflow — expand to read them.
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// "New workflow" affordance (ADR-079 D7 scaffold-from-scratch) — pick an
// agent from the roster already visible in the activity list + a new
// activity name, and start a blank draft in Edit mode on the canvas, with the
// assistant docked beneath it.
// ---------------------------------------------------------------------------

const NEW_WORKFLOW_NAME_RE = /^[a-z][a-z0-9-]*$/

function NewWorkflowForm({
  agents,
  onStart,
  onCancel,
}: {
  agents: string[]
  onStart: (agent: string, name: string) => void
  onCancel: () => void
}) {
  const [agent, setAgent] = useState(agents[0] ?? '')
  const [name, setName] = useState('')
  // Accept whatever the operator types and normalize it ourselves — the API's
  // kebab-case requirement is the application's job to satisfy, not something
  // to push onto the user. `slug` is what we actually start the workflow with.
  const slug = slugifyWorkflowName(name)
  const canStart = agent !== '' && slug !== '' && NEW_WORKFLOW_NAME_RE.test(slug)
  const showSlugHint = slug !== '' && slug !== name.trim()

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2 flex items-center gap-2 flex-wrap">
      <span className="text-xs font-medium text-indigo-700 shrink-0">New workflow</span>
      <select
        value={agent}
        onChange={e => setAgent(e.target.value)}
        disabled={agents.length === 0}
        className="text-xs border border-gray-200 rounded px-2 py-1 bg-white disabled:bg-gray-100"
      >
        {agents.length === 0 && <option value="">no agents yet</option>}
        {agents.map(a => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
      <div className="flex-1 min-w-[140px]">
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Name your workflow (e.g. Handle Refund)"
          className="w-full text-xs border border-gray-200 rounded px-2 py-1"
        />
        {showSlugHint && (
          <p className="mt-0.5 text-[10px] text-gray-500">
            will be created as <span className="font-mono text-indigo-700">{slug}</span>
          </p>
        )}
      </div>
      <button
        type="button"
        disabled={!canStart}
        title={!canStart && name.trim() !== '' ? 'enter a name with at least one letter' : undefined}
        onClick={() => onStart(agent, slug)}
        className="text-xs px-2 py-1 rounded bg-indigo-600 text-white disabled:opacity-40 shrink-0"
      >
        Start drafting
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700 shrink-0"
      >
        Cancel
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Field mode — top-level task list (add/remove/reorder) + selected task's
// scalar-field edit form.
// ---------------------------------------------------------------------------

/**
 * A python step's `file:` body, read-only, with a way out to edit it.
 *
 * ADR-080 allows `code` (inline) or `file` (a relative path in the activity
 * dir), never both. The editor showed the path and a sentence saying the Files
 * tab exists — so the code a step actually runs was invisible here, and reading
 * it meant knowing where it lived and finding it by hand.
 *
 * Read-only on purpose: this file has its own editor with its own propose path,
 * and a second editable copy would be two writers on one file inside one
 * change. The link opens it there, in a new tab, so the workflow draft is not
 * navigated away from mid-edit.
 */
function PythonFileBody({ agent, path }: { agent: string; path: string }) {
  const [content, setContent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setContent(null)
    setError(null)
    api
      .getFleetFile(path)
      .then(f => {
        if (cancelled) return
        // `found: false` is a 404 the API reports in-band — a step pointing at a
        // file that is not there is a real and reportable state, not an error.
        setContent(f.found === false ? null : f.content)
        if (f.found === false) setError('This file does not exist yet.')
      })
      .catch(() => { if (!cancelled) setError('Could not read this file.') })
    return () => { cancelled = true }
  }, [path])

  const href = `/fleet-editor/agents/${encodeURIComponent(agent)}/files?path=${encodeURIComponent(path)}`
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-gray-500">
          runs <span className="font-mono">{path.split('/').pop()}</span>
        </span>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-[11px] text-indigo-600 underline hover:text-indigo-800"
          title={`Open ${path} in the Files tab, in a new tab`}
        >
          Edit in Files ↗
        </a>
      </div>
      {error && <p className="text-[10px] text-amber-800">{error}</p>}
      {content !== null && (
        <pre className="text-[10px] text-gray-700 bg-gray-50 border border-gray-100 rounded p-2 whitespace-pre-wrap break-words max-h-64 overflow-auto font-mono">
          {content}
        </pre>
      )}
      {content === null && !error && <p className="text-[10px] text-gray-400 animate-pulse">Loading…</p>}
      <p className="text-[10px] text-gray-400">
        A task declares <span className="font-mono">code</span> or <span className="font-mono">file</span>, never both.
      </p>
    </div>
  )
}

/**
 * The task tree — every task at every depth, selectable, reorderable within its
 * own siblings, and removable.
 *
 * This replaces TopLevelTaskList, which was the only place structural editing
 * lived and which only ever showed the OUTERMOST tasks. That combination is why
 * "Field mode" existed at all: the mode was not about editing fields (the
 * Inspector has always edited any node at any depth) but about being the one
 * surface that could add, remove and reorder — badly, at one level.
 *
 * Reordering is bounded to a task's own sibling list. An arrow that hopped a
 * container boundary would reparent work between scopes, which is a different
 * edit and must not share a gesture with "move down".
 */
function TaskTree({
  entries,
  selectedId,
  onSelect,
  onAdd,
  onRemove,
  onMove,
  containerTarget,
  readOnly = false,
}: {
  entries: TaskTreeEntry[]
  selectedId: string | null
  onSelect: (id: string) => void
  onAdd: (name: string, ttype: TaskType) => void
  /** The container the palette inserts INTO, when one is selected (ADR-128). */
  containerTarget?: string
  onRemove: (name: string) => void
  onMove: (name: string, delta: -1 | 1) => void
  /** The reader's tree: every task at every depth, selectable, with the
   *  reorder/delete/add controls left out rather than disabled — a reader has
   *  no edit to make, so a row of dead arrows would only be noise. */
  readOnly?: boolean
}) {
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState<TaskType>('agent')

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tasks</p>
      <ul className="space-y-0.5">
        {entries.map(t => (
          <li
            key={`${t.parentId ?? ''}/${t.name}`}
            className={`flex items-center gap-1 rounded px-1 py-1 text-xs ${
              selectedId === t.name ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50 text-gray-700'
            }`}
            style={{ paddingLeft: 4 + t.depth * 12 }}
          >
            <button type="button" onClick={() => onSelect(t.name)} className="flex-1 min-w-0 text-left truncate">
              <span className="mr-1" aria-hidden="true">{TASK_ICON[t.ttype as TaskType] ?? '▭'}</span>
              {t.name}
              <span className="ml-1 text-[10px] text-gray-400">{t.ttype}</span>
              {/* Says WHERE a nested task lives, not just how deep it is — a
                  task in `case 2` and one in `default` look identical otherwise. */}
              {t.branchLabel && <span className="ml-1 text-[10px] text-gray-300">{t.branchLabel}</span>}
            </button>
            {!readOnly && (<>
            <button
              type="button"
              title="Move up"
              aria-label={`Move ${t.name} up`}
              disabled={t.index === 0}
              onClick={() => onMove(t.name, -1)}
              className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 px-0.5"
            >
              ↑
            </button>
            <button
              type="button"
              title="Move down"
              aria-label={`Move ${t.name} down`}
              disabled={t.index === t.siblingCount - 1}
              onClick={() => onMove(t.name, 1)}
              className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 px-0.5"
            >
              ↓
            </button>
            <button
              type="button"
              title="Delete task"
              aria-label={`Delete ${t.name}`}
              onClick={() => onRemove(t.name)}
              className="text-red-400 hover:text-red-600 px-0.5"
            >
              ✕
            </button>
            </>)}
          </li>
        ))}
        {entries.length === 0 && (
          <li className="px-1 py-1 text-xs text-gray-400">
            {readOnly ? 'No tasks.' : 'No tasks yet — add one below, or ask the assistant.'}
          </li>
        )}
      </ul>
      {!readOnly && (<>
      <div className="flex items-center gap-1 pt-1">
        <input
          type="text"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="new-task-name"
          className="flex-1 min-w-0 text-xs border border-gray-200 rounded px-2 py-1"
        />
        <select
          value={newType}
          onChange={e => setNewType(e.target.value as TaskType)}
          aria-label="New task type"
          className="text-xs border border-gray-200 rounded px-1 py-1 bg-white"
        >
          {/* The label, not the bare keyword. This <select> is the one place an
              operator CHOOSES a task type, and it was the only place that showed
              neither TASK_LABEL nor TASK_DESC — eight bare words like "run" and
              "fork" with the explanation available only in a collapsible legend
              elsewhere. That is how "call another workflow" stayed undiscoverable
              while being one option in this list. */}
          {ADDABLE_TASK_TYPES.map(t => (
            <option key={t} value={t} title={TASK_DESC[t]}>{TASK_LABEL[t]} ({t})</option>
          ))}
        </select>
        <button
          type="button"
          disabled={!newName.trim()}
          onClick={() => {
            onAdd(newName.trim(), newType)
            setNewName('')
          }}
          className="text-xs px-2 py-1 rounded bg-indigo-600 text-white disabled:opacity-40"
        >
          Add
        </button>
      </div>
      {/* An <option title> only appears on hover, and not at all on touch — so the
          selected type's meaning is stated in the form itself. */}
      <p className="text-[10px] text-gray-500 mt-1">{TASK_DESC[newType]}</p>
      {containerTarget && (
        <p className="text-[10px] text-gray-400">
          Adds inside <span className="font-mono">{containerTarget}</span> — select a non-container task to add at the top level.
        </p>
      )}
      </>)}
    </div>
  )
}

/** `command.args` is edited as one comma-separated field, so its display text has to be split
 *  back into the array the DSL holds. One definition, because the caret path's
 *  `encodeFieldValue` has to split exactly the way the blur handler does or the two writes
 *  would disagree about what the field says. */
function TaskFieldForm({
  task,
  onScalarChange,
  onSwitchCaseChange,
  onMappingChange,
  onEnsurePin,
  lookupMatch,
  onApplyReferences,
  slot,
  callableWorkflows,
  draft,
}: {
  task: ParsedEntry
  onScalarChange: (patch: Parameters<typeof updateTaskFields>[2]) => void
  onSwitchCaseChange: (clauseIndex: number, expr: string) => void
  /** Write a whole name->value mapping for this task (`set`, `python.inputs`,
   *  `run.inputs`). One callback for all three, because the mutator derives WHICH site
   *  from the task's own type rather than being told. */
  onMappingChange: (entries: MappingEntry[]) => void
  /** ADR-127 D15 — establish the `data:` pin an inserted reference needs, or return
   *  a refusal so the token is never inserted. Threaded to every picker here. */
  onEnsurePin: (pin: { alias: string; set: string }) => string | null
  /** ADR-179 — what the matcher found at one literal site, or null. The form computes no
   *  matches: every field asks by location and the page answers from one response. */
  lookupMatch?: (location: DataReferenceMatchLocation) => DataReferenceMatch | null
  /** ADR-179 — a picked reference and its pin, for the page to write as one mutation.
   *  Supersedes `onEnsurePin` + `onChange`, which wrote two mutations from one draft. */
  onApplyReferences?: (selections: DataReferenceSelection[]) => void
  /** The draft this task lives in (ADR-153 D3), so a field can offer the
   *  workflow-local values a `set` bound earlier. */
  draft?: string
  /** The agent and activity this task belongs to, so a python `file:` — which
   *  ADR-080 defines as relative to the ACTIVITY DIR — can be resolved to a
   *  repo path and read. Absent in contexts with no owning slot, where the file
   *  body simply is not shown. */
  slot?: { agent: string; name: string }
  /** Workflow names this fleet already has, for the `run.workflow` picker. Excludes
   *  the open workflow — a `run` naming itself is a cycle the validator rejects. */
  callableWorkflows?: string[]
}) {
  const spec = task.spec as Record<string, unknown> | undefined
  // ADR-080: `file` is relative to the activity's own directory.
  const activityAgent = slot?.agent
  const activityDir = slot ? `agents/${slot.agent}/activities` : ''
  const usesFileBody = !!activityAgent && typeof spec?.file === 'string' && spec.file !== ''
  // The three sites that hold a mapping. Derived from the task type in the FILE, so a task
  // whose type the editor does not model cannot accidentally get a mapping editor.
  const mappingSite = mappingSiteFor(task.ttype)
  const mappingEditor = mappingSite ? (
    <TaskMappingEditor
      site={mappingSite}
      taskName={task.name}
      draft={draft}
      entries={getTaskMapping(task)}
      onChange={onMappingChange}
      onEnsurePin={onEnsurePin}
      lookupMatch={lookupMatch}
      onApplyReferences={onApplyReferences}
    />
  ) : null

  if (task.ttype === 'agent') {
    const runLocation = { task: task.name, slot: 'agent.run' }
    const runMatch = lookupMatch?.(runLocation) ?? null
    const runValue = typeof spec?.run === 'string' ? spec.run : ''
    return (
      <div className="space-y-2">
        <label className="block text-xs text-gray-500">
          run
          {/* ADR-127 D9-D15: the picker offers only what the canonical generator served and
              inserts its token verbatim, so a reference cannot be assembled wrongly here. */}
          <DataRefFieldAffordance slot="agent.run"
            varsContext={draft === undefined ? undefined : { draft, task: task.name }}
            location={runLocation}
            matches={runMatch ? [runMatch] : undefined}
            draftValue={runValue}
            onApplyReferences={onApplyReferences}
            onEnsurePin={onEnsurePin} onChange={run => onScalarChange({ run })}>
            {ref => (
              <textarea
                ref={ref as React.RefObject<HTMLTextAreaElement>}
                rows={3}
                defaultValue={runValue}
                onBlur={e => onScalarChange({ run: e.target.value })}
                className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
              />
            )}
          </DataRefFieldAffordance>
        </label>
        <TargetTimeoutFields spec={spec} onScalarChange={onScalarChange} />
      </div>
    )
  }

  if (task.ttype === 'command') {
    const args = Array.isArray(spec?.args) ? (spec?.args as unknown[]).map(String) : []
    return (
      <div className="space-y-2">
        <label className="block text-xs text-gray-500">
          command
          <input
            type="text"
            defaultValue={typeof spec?.command === 'string' ? spec.command : ''}
            onBlur={e => onScalarChange({ command: e.target.value })}
            className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
          />
        </label>
        <label className="block text-xs text-gray-500">
          args (comma-separated)
          {/* The picker inserts the canonical token at the caret; the field keeps its
              own comma semantics, and no offered token contains a comma. */}
          <DataRefFieldAffordance
            slot="command.args"
            // The location is the whole `args` array — the field is it, comma-joined, so no
            // single index owns a caret offset. `encodeFieldValue` re-splits the display text
            // the way the blur handler does, so the caret path writes the field AND its pin
            // in one mutation (ADR-179 design decision 10). Each hint carries its own index
            // and replaces one entry in place.
            location={{ task: task.name, slot: 'command.args' }}
            encodeFieldValue={splitCommandArgs}
            entryDelimiter=","
            matches={args
              .map((_, i) => lookupMatch?.({ task: task.name, slot: 'command.args', index: i }))
              .filter((m): m is DataReferenceMatch => !!m)}
            draftValue={joinCommandArgs(args)}
            onApplyReferences={onApplyReferences}
            onEnsurePin={onEnsurePin}
            onChange={value => onScalarChange({ args: splitCommandArgs(value) })}
          >
            {ref => (
              <input
                ref={ref as React.RefObject<HTMLInputElement>}
                type="text"
                defaultValue={joinCommandArgs(args)}
                onBlur={e => onScalarChange({ args: splitCommandArgs(e.target.value) })}
                className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
              />
            )}
          </DataRefFieldAffordance>
        </label>
        <TargetTimeoutFields spec={spec} onScalarChange={onScalarChange} />
      </div>
    )
  }

  if (task.ttype === 'switch' && Array.isArray(task.spec)) {
    const clauses = task.spec as Array<Record<string, unknown>>
    return (
      <div className="space-y-2">
        {clauses.map((clause, i) =>
          'case' in clause ? (
            <label key={i} className="block text-xs text-gray-500">
              case {i}
              {/* An EXPRESSION slot: the offers arrive as `.data.…`, not `{{data.…}}`
                  (ADR-126 D1), which is why the slot name has to be the real one. */}
              <DataRefFieldAffordance
                slot="switch.case"
            onEnsurePin={onEnsurePin}
                onChange={value => onSwitchCaseChange(i, value)}
              >
                {ref => (
                  <input
                    ref={ref as React.RefObject<HTMLInputElement>}
                    type="text"
                    defaultValue={typeof clause.case === 'string' ? clause.case : ''}
                    onBlur={e => onSwitchCaseChange(i, e.target.value)}
                    className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
                  />
                )}
              </DataRefFieldAffordance>
            </label>
          ) : (
            <p key={i} className="text-xs text-gray-400">default clause (no expression to edit)</p>
          ),
        )}
        <p className="text-[10px] text-gray-400">
          Adding/removing branches isn't supported here — use YAML mode.
        </p>
      </div>
    )
  }

  if (task.ttype === 'for') {
    return (
      <div className="space-y-2">
        <label className="block text-xs text-gray-500">
          each
          <input
            type="text"
            defaultValue={typeof spec?.each === 'string' ? spec.each : ''}
            onBlur={e => onScalarChange({ each: e.target.value })}
            className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
          />
        </label>
        <label className="block text-xs text-gray-500">
          in
          {/* The one slot that wants an ARRAY: it is offered `.data.<alias>.items`
              and never a scalar, because a `for` over one member is not a loop. */}
          <DataRefFieldAffordance slot="for.in"
            varsContext={draft === undefined ? undefined : { draft, task: task.name }}
            onEnsurePin={onEnsurePin} onChange={value => onScalarChange({ in: value })}>
            {ref => (
              <input
                ref={ref as React.RefObject<HTMLInputElement>}
                type="text"
                defaultValue={typeof spec?.in === 'string' ? spec.in : ''}
                onBlur={e => onScalarChange({ in: e.target.value })}
                className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
              />
            )}
          </DataRefFieldAffordance>
        </label>
        <label className="block text-xs text-gray-500">
          max
          <input
            type="number"
            defaultValue={typeof spec?.max === 'number' ? spec.max : undefined}
            onBlur={e => onScalarChange({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
            className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
          />
        </label>
        <p className="text-[10px] text-gray-400">
          Editing the loop body's tasks isn't supported here — use YAML mode.
        </p>
      </div>
    )
  }

  if (task.ttype === 'approval') {
    // ADR-203 D1. The preview is normally `{{steps.<preview-step>.output}}`; both fields
    // take the data-reference picker, as every template site does.
    const titleLocation = { task: task.name, slot: 'approval.title' }
    const titleMatch = lookupMatch?.(titleLocation) ?? null
    const titleValue = typeof spec?.title === 'string' ? spec.title : ''
    const previewLocation = { task: task.name, slot: 'approval.preview' }
    const previewMatch = lookupMatch?.(previewLocation) ?? null
    const previewValue = typeof spec?.preview === 'string' ? spec.preview : ''
    const varsContext = draft === undefined ? undefined : { draft, task: task.name }
    return (
      <div className="space-y-2">
        <label className="block text-xs text-gray-500">
          title
          <DataRefFieldAffordance slot="approval.title"
            varsContext={varsContext}
            location={titleLocation}
            matches={titleMatch ? [titleMatch] : undefined}
            draftValue={titleValue}
            onApplyReferences={onApplyReferences}
            onEnsurePin={onEnsurePin} onChange={title => onScalarChange({ title })}>
            {ref => (
              <input
                ref={ref as React.RefObject<HTMLInputElement>}
                defaultValue={titleValue}
                onBlur={e => onScalarChange({ title: e.target.value })}
                className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
              />
            )}
          </DataRefFieldAffordance>
        </label>
        <label className="block text-xs text-gray-500">
          preview (what the operator approves)
          <DataRefFieldAffordance slot="approval.preview"
            varsContext={varsContext}
            location={previewLocation}
            matches={previewMatch ? [previewMatch] : undefined}
            draftValue={previewValue}
            onApplyReferences={onApplyReferences}
            onEnsurePin={onEnsurePin} onChange={preview => onScalarChange({ preview })}>
            {ref => (
              <textarea
                ref={ref as React.RefObject<HTMLTextAreaElement>}
                rows={3}
                defaultValue={previewValue}
                onBlur={e => onScalarChange({ preview: e.target.value })}
                className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
              />
            )}
          </DataRefFieldAffordance>
        </label>
      </div>
    )
  }

  if (task.ttype === 'set') {
    // A `set`'s spec IS the mapping, so the mapping editor is the whole form.
    return <div className="space-y-2">{mappingEditor}</div>
  }

  if (task.ttype === 'python') {
    return (
      <div className="space-y-2">
        {/* `code` and `file` are exclusive and `file` is a path, not a value — neither takes
            a data reference, so neither gets a picker. The inputs are where values enter. */}
        {usesFileBody ? (
          // The code this step runs, read-only, instead of a sentence about
          // where it might be. Offering an inline `code` box here as well would
          // invite writing into a field the validator forbids alongside `file`.
          <PythonFileBody agent={activityAgent!} path={`${activityDir}/${spec!.file as string}`} />
        ) : (
          <label className="block text-xs text-gray-500">
            code
            <textarea
              aria-label="Python code"
              defaultValue={typeof spec?.code === 'string' ? spec.code : ''}
              onBlur={e => onScalarChange({ code: e.target.value })}
              rows={4}
              className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
            />
          </label>
        )}
        {mappingEditor}
        <TargetTimeoutFields spec={spec} onScalarChange={onScalarChange} />
      </div>
    )
  }

  if (task.ttype === 'run') {
    return (
      <div className="space-y-2">
        <label className="block text-xs text-gray-500">
          workflow to call
          <input
            defaultValue={typeof spec?.workflow === 'string' ? spec.workflow : ''}
            onBlur={e => onScalarChange({ workflow: e.target.value })}
            list={callableWorkflows?.length ? `run-workflow-options-${task.name}` : undefined}
            placeholder="workflow-name"
            className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
          />
          {/* ADR-126 D11 still holds: a `data:` REFERENCE is rejected here, because this
              names a UNIT rather than a value. The previous comment concluded from that
              "so there is deliberately no picker" — which over-applied the rule. A list of
              the fleet's existing WORKFLOW NAMES is not a reference; it is precisely the
              literal this field requires, and the only thing that can legally go here.
              Offering none meant an operator had to already know the exact kebab-case name
              of the workflow they wanted to call, and a typo surfaced only later as a
              cross-file validator error. A <datalist> suggests without constraining, so a
              workflow being authored in the same changeset can still be typed by hand. */}
          {callableWorkflows?.length ? (
            <datalist id={`run-workflow-options-${task.name}`}>
              {callableWorkflows.map(w => <option key={w} value={w} />)}
            </datalist>
          ) : null}
          <span className="text-[10px] text-gray-400">
            The workflow to run as a sub-step — pick an existing one or type a name. Its
            steps run in place of this task, so shared logic lives in one file. A data
            reference is rejected here: this names which workflow to run, not a value to
            pass it (use <code>inputs</code> below for that).
          </span>
        </label>
        {mappingEditor}
      </div>
    )
  }

  return (
    <p className="text-xs text-gray-400">
      `{task.ttype}` tasks aren't field-editable yet — use YAML mode.
    </p>
  )
}

function TargetTimeoutFields({
  spec,
  onScalarChange,
}: {
  spec: Record<string, unknown> | undefined
  onScalarChange: (patch: Parameters<typeof updateTaskFields>[2]) => void
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="block text-xs text-gray-500">
        target
        <input
          type="text"
          defaultValue={typeof spec?.target === 'string' ? spec.target : ''}
          onBlur={e => onScalarChange({ target: e.target.value })}
          placeholder="(defaults to owner)"
          className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
        />
      </label>
      <label className="block text-xs text-gray-500">
        timeout_s
        <input
          type="number"
          defaultValue={typeof spec?.timeout_s === 'number' ? spec.timeout_s : undefined}
          onBlur={e => onScalarChange({ timeout_s: e.target.value === '' ? undefined : Number(e.target.value) })}
          placeholder="300"
          className="mt-1 w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
        />
      </label>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Validation panel (shared by read view + edit mode)
// ---------------------------------------------------------------------------

export function ValidationPanel({
  validation,
  validating,
  findings,
  onSelectNode,
  onApplyFix,
}: {
  validation: ValidateActivityResult | null
  validating: boolean
  // ADR-136 D2 — the same errors, attributed to the tasks they concern.
  findings?: Finding[]
  onSelectNode?: (id: string) => void
  // ADR-136 D5 — hand the finding to the assistant as a pre-filled request.
  // ADR-136 D1 — with the node it concerns, so the canvas highlights the step
  // the prompt is about instead of leaving the operator to find it.
  onApplyFix?: (prompt: string, nodeId?: string) => void
}) {
  if (!validation) {
    return <p className="text-xs text-gray-400">{validating ? 'Validating…' : '—'}</p>
  }
  const all = findings ?? []
  const onNodes = all.filter(f => f.nodeId !== undefined)
  const activityWide = all.filter(f => f.nodeId === undefined)
  const counts = summarize(all)
  return (
    <div className="space-y-1.5">
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          validation.ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {validating
          ? 'validating…'
          : validation.ok
            ? 'valid'
            : counts.blocks > 0
              ? `${counts.blocks} will block this from shipping`
              : `${validation.errors.length} error(s)`}
      </span>
      {validation.note && <p className="text-xs text-gray-400 italic">{validation.note}</p>}
      <PendingReferences pending={validation.pending} className="text-xs" />

      {/* Node-scoped findings name their task and select it — the operator sees
          WHICH step is wrong, not a list of sentences about a graph on screen. */}
      {onNodes.length > 0 && (
        <ul className="space-y-1 text-xs">
          {onNodes.map((f, i) => (
            <li key={i} className={f.severity === 'blocks' ? 'text-red-700' : 'text-amber-800'}>
              <button
                type="button"
                className="font-mono underline decoration-dotted hover:opacity-80"
                onClick={() => onSelectNode?.(f.nodeId as string)}
                title="Show this step on the canvas"
              >
                {f.nodeId}
              </button>
              {' — '}
              {f.message}
              {/* ADR-136 D5 — one click asks the assistant to make the change.
                  A prompt rather than a transform: converting a step needs code
                  written, and a stub would be a change nobody can evaluate. */}
              {f.fixPrompt && onApplyFix && (
                <button
                  type="button"
                  className="ml-1 rounded border border-current px-1 py-0.5 text-[10px] hover:bg-white/60"
                  onClick={() => onApplyFix(f.fixPrompt as string, f.nodeId)}
                >
                  {f.fixLabel ?? 'Fix this'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Findings with no task to attach to keep a prose channel, per D2 — a
          real problem with nowhere to go must not vanish. */}
      {activityWide.length > 0 && (
        <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
          {activityWide.map((f, i) => (
            <li key={i}>{f.message}</li>
          ))}
        </ul>
      )}

      {/* Pre-D2 fallback: a validation result with no derived findings (an older
          server, or a shape we could not attribute) still shows its errors. */}
      {all.length === 0 && validation.errors.length > 0 && (
        <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
          {validation.errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Error message helper (mirrors ImprovementPRReview's parseApiError — the
// api client throws `Error("HTTP <status>: <json-or-text-body>")`)
// ---------------------------------------------------------------------------

function friendlyErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof Error)) return fallback
  const m = /^HTTP (\d+): ([\s\S]*)$/.exec(err.message)
  if (!m) return err.message || fallback
  try {
    const body = JSON.parse(m[2]) as { error?: string; detail?: string; errors?: string[] }
    if (body.errors && body.errors.length > 0) return body.errors.join('; ')
    return body.detail || body.error || fallback
  } catch {
    // The body wasn't JSON, so it didn't come from this app — it came from
    // something in front of it (a Cloudflare or Caddy error page). Naming the
    // status is the difference between a diagnosable report and the anonymous
    // "The copilot request failed." an operator once had to escalate to read.
    return `${fallback} (${httpStatusHint(Number(m[1]))})`
  }
}

/** A short, operator-readable gloss for a status whose body we couldn't parse. */
function httpStatusHint(status: number): string {
  switch (status) {
    case 502:
    case 503:
      return `HTTP ${status} — Mission Control was restarting or unreachable; try again`
    case 504:
    case 524:
      return `HTTP ${status} — the request timed out before the fleet answered`
    default:
      return `HTTP ${status}`
  }
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

/** The shareable address of one workflow (ADR-111 IA-6). One definition, because
 *  the two places that navigate here drifted once already: the explicit click
 *  wrote the URL and the auto-selection on load did not, so the address bar named
 *  the facet while the screen showed a workflow. */
/** The identity of a restore, as `sessionLoadedFor` records it.
 *
 * ONE function because there are THREE call sites — the restore effect writes it, its
 * cleanup compares it, and the autosave gate compares it to decide whether a read has
 * happened for the slot it is about to write. They must agree exactly.
 *
 * They did not, briefly, and the consequence was not subtle: ADR-134 D10 added the
 * operator to the key at the writer and left the autosave gate rebuilding
 * `${agent}/${name}`, so the comparison could never match and the autosave stopped
 * firing — for EVERY workflow, not only for a colleague's draft. A durable-draft feature
 * whose autosave silently does nothing is worse than the invisibility D8 set out to fix.
 *
 * The operator is part of it because a session's identity is (agent, workflow, operator)
 * and D10's fleet-wide read means two of those rows can share one slot. Empty for the
 * caller's own, which keeps the key stable for every pre-D10 path. */
export function sessionKey(agent: string, name: string, operator?: string | null): string {
  return `${agent}/${name}/${operator ?? ''}`
}

function newDraftID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const SESSION_SAVE_DEADLINE_MS = 15_000
const RESTORE_DEADLINE_MS = 15_000
const LINKED_READ_RETRY_DELAY_MS = 750

export function activityPath(agent: string, name: string): string {
  return `/fleet-editor/agents/${encodeURIComponent(agent)}/workflows/${encodeURIComponent(name)}`
}

export function WorkflowsFacet({
  agent,
  initialName,
  initialEdit,
  restoreDeadlineMs = RESTORE_DEADLINE_MS,
  linkedReadRetryDelayMs = LINKED_READ_RETRY_DELAY_MS,
  sessionSaveDeadlineMs = SESSION_SAVE_DEADLINE_MS,
}: {
  agent: string
  // Deep-link support (read from the route by FleetEditor,
  // /fleet-editor/agents/:agent/workflows/:name) — selection stays in
  // `selected` state; the URL is rewritten (replace) as the operator navigates.
  initialName?: string
  // Arrive already in edit mode (e.g. a skill route's "open ↗ to edit").
  initialEdit?: boolean
  restoreDeadlineMs?: number
  linkedReadRetryDelayMs?: number
  sessionSaveDeadlineMs?: number
}) {
  const notifyAttention = useAttentionNotify()
  const { resolved } = useTheme()
  const { user } = useAuth()
  const canPropose = user.fleet_role === 'fleet_owner'
  const navigate = useNavigate()

  const [activities, setActivities] = useState<ActivitySummary[]>([])
  const activitiesSeq = useRef(0)
  // ADR-103: core agents — a core-owned agent's Workflows facet hides "New
  // workflow" and "Edit" (viewing is still fine, authoring is not).
  const [coreAgentNames, setCoreAgentNames] = useState<string[]>([])
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState<string | null>(null)

  const [selected, setSelected] = useState<{ agent: string; name: string } | null>(null)
  const [detail, setDetail] = useState<ActivityDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [externalChanged, setExternalChanged] = useState(false) // ADR-101: published version differs from what's loaded

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [showDataEdges, setShowDataEdges] = useState(false)
  const [legendOpen, setLegendOpen] = useState(false)
  // ADR-136 D2 — whether the activity-level findings are expanded on the graph.
  // Closed by default: the badge's job is to say something exists, and D4's
  // "silence on what is correct" cuts both ways — a panel that is always open
  // is a panel the operator stops reading.
  const [activityFindingsOpen, setActivityFindingsOpen] = useState(false)
  const [rawYAMLOpen, setRawYAMLOpen] = useState(false)

  // Left-pane controls: collapse the whole list for canvas real estate, and
  // search by (partial) name. The list is already scoped to `agent` (a facet
  // prop, not an internal filter), so there is no per-agent grouping here.
  const [listCollapsed, setListCollapsed] = useState(false)
  const [search, setSearch] = useState('')

  // ---- ADR-079 Phase 5b: the shared editor draft ---------------------------
  const [editMode, setEditMode] = useState(false)

  // ADR-177 — a run URL stays shareable now that /runs/:id lands on this page.
  //
  // Held in the query string rather than in state alone, so the address bar an operator
  // copies is the address that reopens what they were looking at. That is the whole point
  // of keeping the deep link when the page stopped being a route of its own.
  const [runParams, setRunParams] = useSearchParams()
  const deepLinkedRunId = runParams.get('run') || undefined
  const setDeepLinkedRunId = (id: string) => {
    const next = new URLSearchParams(runParams)
    if (id) next.set('run', id)
    else next.delete('run')
    // replace, not push: expanding runs in a panel must not fill the back button with
    // every row the operator glanced at.
    setRunParams(next, { replace: true })
  }
  const [mode, setMode] = useState<EditorMode>('visual')
  const [draftYAML, setDraftYAMLState] = useState('')
  // The draft as it was when the edit session opened — draftYAML differing from
  // this means there is unsaved work to guard against silently discarding.
  const [initialDraftYAML, setInitialDraftYAML] = useState('')
  const [draftActivity, setDraftActivity] = useState<Record<string, unknown> | null>(null)
  const [draftParseError, setDraftParseError] = useState<string | null>(null)
  const [draftValidation, setDraftValidation] = useState<ValidateActivityResult | null>(null)
  const [validating, setValidating] = useState(false)
  // ADR-179 — the literals in this draft that a published reference already carries. One
  // call per edit, debounced, asked only while the editor is open: a read-only view has no
  // field to offer the swap in.
  const dataRefMatches = useMatches(editMode ? draftYAML : null)
  // Remounts the uncontrolled task fields after a reference is written. They read the draft
  // through `defaultValue`, so a value the page changed under them is invisible until they
  // mount again — and the operator would see the old literal beside a hint that has gone.
  const [fieldsEpoch, setFieldsEpoch] = useState(0)
  // Why an accepted reference was NOT written. Nothing partial ever lands, so this is the
  // whole outcome of a refused apply and it has to be on screen.
  const [dataRefRefusal, setDataRefRefusal] = useState<string | null>(null)
  // The sweep panel (ADR-179 P2), opened from the canvas toolbar. It lives in the inspector
  // column beside the canvas rather than over it: accepting a row is an edit, and the
  // operator has to see the task it names while they do it.
  const [sweepOpen, setSweepOpen] = useState(false)
  // The Inspector's own scroll box. Everything this page reveals on the operator's behalf
  // (the sweep panel, the task a row points at) lives inside it, and scrolling the box itself
  // rather than `scrollIntoView` keeps the page and the canvas where they were.
  const inspectorScrollRef = useRef<HTMLDivElement | null>(null)
  const inspectorTaskRef = useRef<HTMLDivElement | null>(null)
  // The panel mounts at the top of a list the operator may have scrolled past to reach the
  // task they were editing; without this only the scrollbar moved. Once, on open — not on
  // every render, which would fight their own scrolling inside the panel.
  useEffect(() => {
    if (sweepOpen && inspectorScrollRef.current) inspectorScrollRef.current.scrollTop = 0
  }, [sweepOpen])
  // A sweep row's task link selects the task, whose form sits below the panel and the tree.
  // When the task was already selected the click changed nothing on screen; a request
  // counter makes each click scroll the form into view, even the repeated one.
  const [revealTaskRequest, setRevealTaskRequest] = useState(0)
  useEffect(() => {
    if (!revealTaskRequest) return
    const box = inspectorScrollRef.current
    const form = inspectorTaskRef.current
    if (box && form) scrollWithin(box, form)
  }, [revealTaskRequest])
  // The badge counts locations that can be ACTED on, not rows. A literal nothing carries is
  // still listed inside — "no exact match found" is the answer the operator came for — but it
  // is not work waiting to be done, so counting it would overstate what the panel holds.
  const hardcodedMatchCount = useMemo(
    () => (dataRefMatches.matches?.locations ?? []).filter(m => m.candidates.length > 0).length,
    [dataRefMatches.matches])
  /** The revision this page sends when it re-verifies a selection. Owned here and separate
   *  from the hook's counter: these are answers to a different question. */
  const verifyRevision = useRef(0)
  /** Bumped on every keystroke in the task inspector's fields.
   *
   *  Those fields are uncontrolled and commit on BLUR, so text typed into one while a
   *  verification POST is in flight is in the DOM and nowhere else: the draft YAML is
   *  unchanged, the freshness check that compares it sees nothing, and the `fieldsEpoch`
   *  remount that follows the apply throws the typing away. A generation counter sees the
   *  keystroke the YAML cannot. */
  const fieldEditGeneration = useRef(0)
  const [proposeMessage, setProposeMessage] = useState('')
  const [proposing, setProposing] = useState(false)
  const [proposeError, setProposeError] = useState<string | null>(null)
  // ADR-107 P3: the fleet's own author tests failed against this proposed
  // change (409 author_tests_failing) — rendered via the SAME FailingChecksPanel
  // ADR-1027's PR review uses, not the generic proposeError toast.
  const [proposeFailingChecks, setProposeFailingChecks] = useState<FailingCheck[]>([])
  const [proposeResult, setProposeResult] = useState<ProposeActivityResult | null>(null)
  const validateSeq = useRef(0)

  // ---- ADR-079 Phase 5d: scaffold-from-scratch + the assistant conversation
  // isScaffold marks an edit session with no server-side detail behind it yet
  // (a brand-new activity from "New workflow" — see startNewWorkflow). It
  // guards the detail-loading effect below from fetching/clobbering that
  // session when `selected` is set to the new {agent, name}.
  const [isScaffold, setIsScaffoldState] = useState(false)
  // The latest isScaffold ASKED FOR, written synchronously, for the detail effect.
  //
  // That effect's own `isScaffold` is the value of the render it belongs to, and it
  // can run after a newer update is already queued. The list landing commits
  // {published, isScaffold=false}; a click on a draft row before that commit's
  // effects run queues {draft, true}; React then flushes the landing's effect first,
  // and its exitEditMode() queued isScaffold=false on top of the click. The draft
  // opened as a published file: detail 404 (`activity_not_found`), no Discard.
  // CI caught it on a slow runner. The ?edit=1 auto-edit and the fleet-version refresh
  // effects had the same shape, and check this ref too
  // (draftClickBeforeLandingEffects.contract.test.tsx covers all three).
  const isScaffoldRef = useRef(false)
  const setIsScaffold = (value: boolean) => {
    isScaffoldRef.current = value
    setIsScaffoldState(value)
  }
  const [newWorkflowOpen, setNewWorkflowOpen] = useState(false)
  // ADR-134 D10 — show every operator's drafts, not just mine.
  //
  // OFF by default, on ADR-137 D8's reasoning for the conversation catalog: the common
  // case is resuming your own train of thought, and defaulting to everyone's would make
  // an operator hunt for theirs in the list that is supposed to be how they find it. The
  // fleet-wide read is a capability, not a default.
  const [allDrafts, setAllDrafts] = useState(false)
  const allDraftsRef = useRef(false)

  function loadActivities(): Promise<void> {
    const seq = ++activitiesSeq.current
    return api
      .listActivities(allDraftsRef.current ? { drafts: 'all' } : undefined)
      .then(res => {
        if (seq === activitiesSeq.current) setActivities(res.activities)
      })
      .catch(() => {})
  }
  // WHOSE draft is on screen, when it is not the caller's own — and the fact that it is
  // therefore READ-ONLY.
  //
  // ADR-134 D10's open question is answered: visibility only, no fork and no co-edit.
  // Fork was built and merged first, and taking it out is the point rather than a
  // simplification — a fork is how two drafts of ONE path came to exist from someone
  // merely looking, and two drafts of a path cannot both become that file. Viewing a
  // colleague's work now creates nothing.
  //
  // This is also the flag that keeps the editor OUT of edit mode for such a draft. That
  // matters more than the banner: `enterEditMode` seeds from the restored session, and
  // the autosave would then commit a colleague's changeset into the caller's own slot —
  // manufacturing the second draft by accident, from a screen that was only ever opened
  // to read.
  const [readOnlyDraftOf, setReadOnlyDraftOf] = useState<string | null>(null)
  /** The operator whose draft the next restore should read (ADR-134 D10), or null for
   *  the caller's own. A ref, not state: `openUnpublishedSlot` sets it in the same tick
   *  as `setSelected`, and the restore effect has to see the new value on the render
   *  that `selected` change triggers — a state update would arrive one render late and
   *  the effect would fetch the caller's own empty slot instead of the draft asked for. */
  const openAsOperator = useRef<string | null>(null)
  // When arriving already targeting a specific workflow to edit (the
  // `initialEdit` prop — e.g. a skill route's "open ↗ to edit" navigated here),
  // this holds the target; the detail-load effect below enters edit once it loads.
  const [pendingEdit, setPendingEdit] = useState<{ agent: string; name: string } | null>(
    initialEdit && initialName ? { agent, name: initialName } : null,
  )
  const [chatMessages, setChatMessagesState] = useState<CopilotTurn[]>([])
  // The user turn whose request the server has not yet taken. While it is the transcript's only
  // message the conversation autosave waits: a refused first turn (409 run_finished) then never
  // reaches History, and an accepted one is stored long before the job's own catalog append looks for it.
  const [turnAwaitingAcceptance, setTurnAwaitingAcceptance] = useState<CopilotTurn | null>(null)
  // Live mirrors for the ADR-160 D2 re-attach. Its callback resolves minutes after the
  // effect closed over state, so reading the closure would apply a turn's answer to the
  // draft as it was when the PAGE opened — silently discarding anything typed since.
  const chatMessagesRef = useRef<CopilotTurn[]>([])
  const draftYAMLRef = useRef('')
  function markEditorContentChanged() {
    sessionRetired.current = false
    editorContentRevision.current += 1
  }
  /** Sets the conversation and its live mirror together, for the reason
   *  `setDraftYAML` does — and this one carries the transcript, which is the half
   *  of a session that cannot be retyped. A turn's answer reads the mirror so
   *  completion can restore the sent user turn if a restore replaced the list. */
  function setChatMessages(next: CopilotTurn[]) {
    markEditorContentChanged()
    chatMessagesRef.current = next
    setChatMessagesState(next)
  }
  /** Sets the draft text and its live mirror together. The mirror cannot be an
   *  effect: React flushes passive effects in a later macrotask, so a promise
   *  landing between a keystroke's commit and that flush would read `draftEdited`
   *  already true with `draftYAMLRef` still on the PREVIOUS text — and the restore
   *  then caches, and autosaves, text the operator had already replaced. */
  function setDraftYAML(text: string) {
    markEditorContentChanged()
    draftYAMLRef.current = text
    setDraftYAMLState(text)
  }
  // Is the editor open, readable from a callback that resolved long after its
  // effect ran. Both copilot lanes write the draft and its ownership flags with no
  // check that the editor is still there, so the flags alone cannot answer "is the
  // operator working here" — only that something wrote to the draft.
  //
  // Written at every `setEditMode` site rather than mirrored from an effect: a
  // promise resolving between the commit and the passive-effect flush would read
  // the previous value, and on the way out of the editor that means an abandoned
  // turn's text is cached as the operator's work.
  const editModeRef = useRef(false)
  // Counts session reads. A propose resolves minutes after it was sent, by which
  // time the editor may be on another workflow — or back on this one with a fresh
  // read in flight — and its completion must only retire the read it was sent
  // against.
  const restoreEpoch = useRef(0)
  // ADR-134 D5 — the durable session. `restored` drives the "resumed your work"
  // notice; `saveFailed` is shown persistently rather than swallowed, because a
  // silently-failing autosave is the very defect D5 exists to remove.
  // ADR-134 D1 — the rest of the changeset. The primary activity is draftYAML
  // above; this is every OTHER file the change touches (the cron that drives it,
  // a skill that routes to it, a second workflow it fans out into).
  const [extraFiles, setExtraFilesState] = useState<ProposedChangesetFile[]>([])
  // ADR-134 D1 — the agent's REAL skills, so the changeset picker offers what
  // exists instead of asking the operator to type a slug blind. Activities come
  // from the list this page already loads.
  const [agentSkills, setAgentSkills] = useState<string[]>([])
  // ADR-111 IA-8 — the agent's real activities/*.py step-body stems, so the
  // changeset picker opens an existing body from its CURRENT content rather than
  // a blank box. Read from the fleet tree, which lists every overlay file with no
  // extension filter; the activities list this page already loads is YAML only.
  const [agentPythonSteps, setAgentPythonSteps] = useState<string[]>([])
  // WHAT came back, not merely that something did — the notice has to name the
  // half it is actually reporting.
  const [sessionRestored, setSessionRestored] = useState<
    'changeset' | 'changeset-and-conversation' | 'conversation' | null
  >(null)
  const [sessionSaveFailed, setSessionSaveFailed] = useState(false)
  // ADR-134 D5 — the restored session for the CURRENT slot, held so that
  // entering edit mode can adopt it. Without this, `enterEditMode` reseeds from
  // `detail.yaml` with an empty transcript and the resume is destroyed by the
  // act of opening the editor.
  const restoredSession = useRef<EditorSession | null>(null)
  // The session transcript has role/content only; this cache holds its reconciled
  // catalog turns, including canonical content and job ids, for read-view to Edit.
  const restoredConversationTurns = useRef<CopilotTurn[] | null>(null)
  function clearRestoredSession() {
    restoredSession.current = null
    restoredConversationTurns.current = null
  }
  // Whether the session fetch for the current slot has SETTLED (found, absent,
  // or failed). The autosave must not run before it has: `sessionLoadedFor` is
  // assigned before the request goes out, so it says "a restore was started",
  // not "a restore finished" — and an autosave in that window PUTs the editor's
  // empty transcript over the stored one, which is what made the loss permanent
  // rather than merely invisible.
  const [sessionSettled, setSessionSettled] = useState(false)
  const [restoreFailed, setRestoreFailed] = useState<{ reason: 'timeout' | 'error'; detail: string } | null>(null)
  const [restoreNonce, setRestoreNonce] = useState(0)
  // ADR-134 D5 — the base the resumed session was last validated against, when
  // it no longer matches the fleet's current version. The session is not WRONG
  // when develop moves; it is no longer PROVEN, and presenting its last verdict
  // as current is the divergence D3 calls a defect wearing a different hat.
  const [sessionStaleBase, setSessionStaleBase] = useState<string | null>(null)
  const [discardingDraft, setDiscardingDraft] = useState(false)
  const sessionLoadedFor = useRef<string>('')
  const retryingKey = useRef<string | null>(null)
  function retryRestore() {
    retryingKey.current = sessionLoadedFor.current
    sessionLoadedFor.current = ''
    setRestoreNonce(n => n + 1)
  }
  const draftIdRef = useRef<string>('')
  const draftIdReady = useRef<Promise<string>>(Promise.resolve(''))
  const settleDraftId = useRef<(id: string) => void>(() => {})
  // True while the identity is one the browser minted because the restore found no
  // stored id, and no save under it has landed yet. A turn sent during the restore
  // carries no id, so the server resolves one itself; on a fresh slot that is a row
  // the browser's guess does not match, and the server's answer is the one to keep.
  const draftIdProvisional = useRef(false)
  function beginDraftIdentity() {
    draftIdRef.current = ''
    draftIdProvisional.current = false
    draftIdReady.current = new Promise<string>(resolve => {
      settleDraftId.current = resolve
    })
  }
  function settleDraftIdentity(id: string, provisional = false) {
    draftIdRef.current = id
    draftIdProvisional.current = provisional
    settleDraftId.current(id)
    settleDraftId.current = () => {}
  }
  /** The open draft's identity, waiting for the restore's session read when it is
   *  still in flight. Bounded by that read: the restore settles the identity on
   *  every path, so this never waits for a copilot turn. */
  async function resolvedDraftIdentity(): Promise<string> {
    return draftIdRef.current || (await draftIdReady.current)
  }
  function ensureDraftID(): string {
    if (!draftIdRef.current) settleDraftIdentity(newDraftID())
    return draftIdRef.current
  }
  const autosaveInFlight = useRef<Promise<void> | null>(null)
  const autosavePending = useRef(false)
  const autosaveTimer = useRef<number | null>(null)
  const sessionRevision = useRef(0)
  const cachedRevision = useRef(0)
  const sessionRetired = useRef(false)
  const editorContentRevision = useRef(0)
  // Whether a restore that is still in flight has been VOIDED by a discard.
  // `sessionLoadedFor` says a restore was started, never that it finished, so
  // without this a response that lands after `deleteEditorSession` succeeded
  // resurrects the draft, the extra files and the conversation link belonging to
  // a row the server no longer has. Reset when a fresh restore starts.
  const restoreVoided = useRef(false)
  // Whether the text CURRENTLY in the editor is the operator's own work rather
  // than something the editor seeded. A late restore reads this to decide whether
  // adopting the stored draft would overwrite fresher local work: `sessionLoadedFor`
  // cannot answer that, since it only makes the restore run once.
  //
  // Ownership of the current text, not a count of edits ever made. A counter that
  // only climbed said "they have worked here" forever, so an edit made before a
  // Cancel still read as local work in a freshly REOPENED editor — where the text
  // is the published file the reseed just put there. Substituting that as their
  // draft, and declining the stored one, then let the autosave write the published
  // file over the durable draft.
  //
  // Set by the three lanes that change the draft: typing, a field-mode mutation,
  // and a copilot turn that actually edited. Cleared by every reseed and teardown,
  // because seeded text belongs to nobody.
  const draftEdited = useRef(false)
  const conversationEdited = useRef(false)
  // True once the operator has chosen or completed a conversation here, so a later pending turn cannot hand it to a restore.
  const conversationEstablished = useRef(false)
  // The same question for the rest of the changeset, tracked separately because
  // the answers differ: an edit to the activity says nothing about whether the
  // cron beside it is fresher, and vice versa. Keying the extra files off the
  // primary's flag discarded stored supporting files the operator had never
  // touched, just because they had typed in the YAML pane.
  const extrasEdited = useRef(false)
  // Live mirror, for the same reason `draftYAMLRef` exists: the restore's
  // callback resolves long after the effect closed over state.
  const extraFilesRef = useRef<ProposedChangesetFile[]>([])
  /** Sets the changeset's supporting files and their live mirror together, for the
   *  reason `setDraftYAML` does. The updater form computes from the mirror rather
   *  than React's `prev`, so two merges landing in one tick see each other. */
  function setExtraFiles(
    next: ProposedChangesetFile[] | ((prev: ProposedChangesetFile[]) => ProposedChangesetFile[]),
  ) {
    markEditorContentChanged()
    const value = typeof next === 'function' ? next(extraFilesRef.current) : next
    extraFilesRef.current = value
    setExtraFilesState(value)
  }
  const [chatInput, setChatInput] = useState('')
  const [chatSending, setChatSendingState] = useState(false)
  const chatSendingRef = useRef(false)
  function setChatSending(value: boolean) {
    chatSendingRef.current = value
    setChatSendingState(value)
  }
  // ADR-112 D3 Ask/Edit. Default Edit — ADR-079 D2 keeps authoring the primary
  // interface; Ask is the mode that was missing, not the new default.
  const [chatIntent, setChatIntent] = useState<CopilotIntent>('edit')
  // ADR-200 amendment (2026-09-29) — "Scope this request first". UNCHECKED BY DEFAULT, EVERY
  // TIME: cleared after each send and whenever the slot changes, never remembered. Offered
  // only while the slot holds no run, because the server reads it only from the request
  // that opens one.
  const [scopeFirst, setScopeFirst] = useState(false)
  // ADR-183 Phase 6 — the assistant's per-turn model override.
  //
  // An EMPTY pair is the default, not a missing value: it means "the
  // deployment's configured model", which is what keeps a model comparison
  // against a stable baseline possible. Held per page rather than per turn so a
  // choice survives several turns of one conversation — switching model
  // mid-comparison by accident is the thing this must not do.
  const [chatModel, setChatModel] = useState<ModelChoice>({ provider: '', model: '' })
  const { catalog: modelCatalog, catalogLoading: modelCatalogLoading } = useModelCatalog()
  // ADR-194 — the standing pin, beside the per-turn override it outranks.
  const { pin: assistantPin, pinError: assistantPinError,
          setMine: setMyAssistantPin, clearMine: clearMyAssistantPin,
          proposeFleet: proposeFleetAssistantPin, pinProposal: assistantPinProposal } = useAssistantModel()
  // Elapsed time for the in-flight turn, so the panel can be honest about how
  // long an Architect turn takes instead of promising "up to 20s".
  const [chatElapsedMs, setChatElapsedMs] = useState(0)
  const [chatError, setChatError] = useState<string | null>(null)
  const [runFinished, setRunFinished] = useState<{ runId: string | null } | null>(null)
  const [startingNewRun, setStartingNewRun] = useState(false)
  const [startNewRunError, setStartNewRunError] = useState<string | null>(null)
  const [unsavedAnswer, setUnsavedAnswer] = useState<{ jobId: string; error: string; gone?: boolean } | null>(null)
  const [retryingUnsavedAnswer, setRetryingUnsavedAnswer] = useState(false)
  const [copilotUnconfigured, setCopilotUnconfigured] = useState(false)
  // Whether this fleet has the AI copilot configured (from GET /api/config).
  // Defaults true so we never flash a false "not configured" warning before the
  // config arrives (and older MC images that omit the flag stay unaffected).
  const [copilotConfigured, setCopilotConfigured] = useState(true)
  // ADR-136 D1 — the docked assistant's expanded/collapsed state. `null` means
  // "follow the viewport": expanded when there is room, a single input row when
  // there is not. An explicit toggle pins it, because a default that cannot be
  // overridden is a lock, and an operator on a laptop still has to read answers.
  const [assistantOpen, setAssistantOpen] = useState<boolean | null>(null)
  const [shortViewport, setShortViewport] = useState(
    () => typeof window !== 'undefined' && window.innerHeight < SHORT_VIEWPORT_PX,
  )
  // An answer that arrived while the dock was collapsed. Cleared on expand.
  const [assistantUnread, setAssistantUnread] = useState(false)
  // ADR-137 — the conversation catalog.
  const [assistantTab, setAssistantTab] = useState<'chat' | 'history' | 'process'>('chat')
  // ADR-200 amendment — whether this slot already holds a run, so the scoping box is offered
  // only on a send that would open one. Re-read after every send and after Chat's Start a
  // new run, so a reset immediately offers the choice again.
  const [slotRunRefresh, setSlotRunRefresh] = useState(0)
  const slotRun = useSlotRun(selected?.agent ?? '', selected?.name ?? '', `${chatSending}:${slotRunRefresh}`)
  const scopingControl = scopingControlFor(chatIntent, slotRun, chatSending)
  useEffect(() => { setScopeFirst(false) }, [selected?.agent, selected?.name])

  // A READER LANDS ON RUNS; AN EDITOR LANDS ON THE ASSISTANT.
  //
  // `chat` is the right default while editing and is not even offered otherwise — its
  // composer writes the draft. Without this the dock would open, outside edit mode, on a
  // tab whose button is not rendered: a blank panel with no way to leave it.
  const effectiveTab = editMode ? assistantTab : (assistantTab === 'chat' ? 'process' : assistantTab)
  // ADR-137 D8 — the catalog is fleet-wide, and opens on your own.
  const [historyFilter, setHistoryFilter] = useState<string>(AUTHOR_MINE)
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [conversationsLoading, setConversationsLoading] = useState(false)
  const [conversationsError, setConversationsError] = useState<string | null>(null)
  const selectedSlotRef = useRef<{ agent: string; name: string } | null>(null)
  selectedSlotRef.current = selected ? { agent: selected.agent, name: selected.name } : null
  const catalogGen = useRef(0)
  function isOpenSlot(a: string, n: string) {
    const s = selectedSlotRef.current
    return !!s && s.agent === a && s.name === n
  }
  // The conversation the composer is appending to. `null` means the next turn
  // starts a new one — which is the default, and what "+ New chat" returns to.
  const [activeConversationId, setActiveConversationIdState] = useState<string | null>(null)
  const activeConversationIdRef = useRef<string | null>(null)
  function setActiveConversationId(id: string | null) {
    activeConversationIdRef.current = id
    setActiveConversationIdState(id)
  }
  // Set while a resume is being applied, so the autosave effect does not
  // immediately write the just-loaded transcript back as a NEW conversation.
  const resumingRef = useRef(false)
  useEffect(() => {
    const onResize = () => setShortViewport(window.innerHeight < SHORT_VIEWPORT_PX)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const assistantExpanded = assistantOpen ?? !shortViewport
  // The operator's dragged divider position, in px. `null` = the default share
  // of the column. Seeded from localStorage so a preference survives a reload.
  const [dockHeight, setDockHeight] = useState<number | null>(() =>
    typeof window === 'undefined' ? null : readStoredDockHeight(),
  )
  // ADR-111 — the inspector column: which tab, and how wide.
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>('inspector')
  const [panelWidth, setPanelWidth] = useState<number | null>(() =>
    typeof window === 'undefined' ? null : readStoredPanelWidth(),
  )
  function resizePanel(px: number) {
    setPanelWidth(px)
    try {
      window.localStorage.setItem(PANEL_WIDTH_KEY, String(Math.round(px)))
    } catch {
      // Storage unavailable — the drag still works for this session.
    }
  }
  const panelRef = useRef<HTMLDivElement>(null)

  /** Drag the column's left edge. Mirrors the dock's divider: pointer events
   *  with capture so a fast drag does not detach, bounded so neither pane can
   *  be starved. */
  function startPanelDrag(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault()
    const handle = e.currentTarget
    handle.setPointerCapture(e.pointerId)
    const grid = panelRef.current?.parentElement
    if (!grid) return
    const onMove = (ev: PointerEvent) => {
      const rect = grid.getBoundingClientRect()
      const proposed = rect.right - ev.clientX
      const ceiling = Math.max(MIN_PANEL_PX, rect.width - MIN_CENTER_PX)
      resizePanel(Math.min(Math.max(proposed, MIN_PANEL_PX), ceiling))
    }
    const onUp = (ev: PointerEvent) => {
      handle.releasePointerCapture(ev.pointerId)
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', onUp)
    }
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', onUp)
  }

  function onPanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    const grid = panelRef.current?.parentElement
    const current = panelRef.current?.getBoundingClientRect().width ?? MIN_PANEL_PX
    const ceiling = grid
      ? Math.max(MIN_PANEL_PX, grid.getBoundingClientRect().width - MIN_CENTER_PX)
      : current
    const next = current + (e.key === 'ArrowLeft' ? 24 : -24)
    resizePanel(Math.min(Math.max(next, MIN_PANEL_PX), ceiling))
  }

  // The workflow list's width: the shared left-pane edge (drag, arrow keys,
  // double-click to fit the longest name, remembered). Its ceiling also reserves
  // the inspector and the canvas floor, so widening the list cannot squeeze the
  // canvas to nothing between them.
  const { ref: listRef, width: listWidth, handleProps: listHandleProps } = useResizableWidth({
    storageKey: LIST_WIDTH_KEY,
    min: MIN_LIST_PX,
    max: MAX_LIST_PX,
    room: () => {
      const grid = listRef.current?.parentElement
      if (!grid) return MAX_LIST_PX
      const inspector = panelRef.current?.getBoundingClientRect().width ?? (panelWidth ?? 340)
      return grid.getBoundingClientRect().width - inspector - MIN_CENTER_PX - GRID_GAPS_PX
    },
  })

  /**
   * Delete a task from the draft.
   *
   * Two things have to be said BEFORE it happens, because neither is
   * recoverable from the graph afterwards:
   *
   *   - deleting a container deletes its body, and the operator needs the count
   *   - deleting a task others read leaves a workflow that looks whole and is
   *     broken; validate-activities.py catches it, but only after the node is
   *     gone
   *
   * Owner's ruling on rewiring: close the gap only when it is SAFE — nothing
   * referenced this task's output, so the edit is pure control flow. When
   * something did reference it, the break stays visible as a finding rather than
   * being papered over into a graph that reads as valid.
   */
  function requestDeleteTask(name: string) {
    if (!draftActivity) return
    const body = descendantsOf(draftActivity, name)
    const readers = referrersOf(draftActivity, name)

    const lines = [`Delete task "${name}"?`]
    if (body.length > 0) {
      lines.push('', `This also deletes the ${body.length} task${body.length === 1 ? '' : 's'} inside it: ${body.join(', ')}.`)
    }
    if (readers.length > 0) {
      lines.push(
        '',
        `${readers.length === 1 ? 'One task reads' : `${readers.length} tasks read`} its output and will break: ${readers.join(', ')}.`,
        'They will be reported as findings so you can repoint or remove them.',
      )
    }
    lines.push('', "You can still Cancel the whole edit.")
    if (!window.confirm(lines.join('\n'))) return

    applyDraftMutation(removeTaskAnywhere(draftActivity, name))
    if (selectedNodeId === name || body.includes(selectedNodeId ?? '')) setSelectedNodeId(null)
  }

  function resizeDock(px: number) {
    setDockHeight(px)
    try {
      window.localStorage.setItem(DOCK_HEIGHT_KEY, String(Math.round(px)))
    } catch {
      // Storage unavailable — the drag still works for this session.
    }
  }

  // Learn copilot availability upfront so the assistant's "not configured" notice
  // shows on entry instead of only after the operator's first send fails with a 503.
  useEffect(() => {
    let cancelled = false
    api.getConfig()
      .then(cfg => { if (!cancelled && cfg.copilot_configured === false) setCopilotConfigured(false) })
      .catch(() => { /* leave as configured; the 503 path still covers it */ })
    return () => { cancelled = true }
  }, [])

  // ---- ADR-122 D8: the aggregate pending banner is RETIRED ----------------
  // It answered a real question — "is something waiting to go live?" — with the
  // wrong object in the wrong place: a repo-wide commit count (merge commits and
  // automated platform syncs included; orion read "4 changes" for two real ones)
  // rendered inside ONE agent's tab for a fleet-wide fact. That question is now
  // answered properly on Updates, by the Ready worklist: named changes with
  // provenance and one Apply.
  //
  // What must NOT be lost with it is the founding incident's protection — an
  // operator deleted 3 activities, the deletion sat staged, this page read the
  // deployed branch, and they kept running for 2 days. That protection is the
  // PER-ARTIFACT badge below, not the count: `useStagedPaths` (ADR-111 D2) reads
  // /api/fleet/pending-paths, which includes deletions, so the affected activity
  // carries its own "staged" badge on its own row. That is strictly more precise
  // than an aggregate ever was, and it stays.
  const { paths: stagedPaths } = useStagedPaths()

  useEffect(() => {
    let cancelled = false
    api
      .listAgentSkills()
      .then(res => {
        if (cancelled) return
        const mine = res.agents.find(a => a.agent === agent)
        // A core-owned agent's skills cannot be proposed (ADR-103), so they are
        // not offered — a picker that lists a choice the gate will refuse is
        // worse than one that omits it.
        setAgentSkills(mine && !mine.core ? mine.skills.map(s => s.slug) : [])
      })
      .catch(() => setAgentSkills([]))
    return () => {
      cancelled = true
    }
  }, [agent])

  // ADR-111 IA-8 — the agent's activities/*.py step bodies, from the fleet tree.
  //
  // Deliberately LAZY: only once the operator is actually in the changeset picker
  // (edit mode, Files tab). /api/fleet/tree walks the WHOLE overlay recursively,
  // and the list is needed by exactly one control — fetching it on mount would
  // pay for the walk on every workflow page view to populate a dropdown most
  // visits never open.
  useEffect(() => {
    if (!editMode || inspectorTab !== 'files') return
    let cancelled = false
    api
      .getFleetTree()
      .then(tree => {
        if (cancelled) return
        const mine = tree.agents.find(a => a.kind === 'agent' && a.agent === agent)
        // Core-owned files are read-only (ADR-103) — offering one the gate will
        // refuse is worse than omitting it, same rule as the skills picker.
        if (!mine || mine.core_owned) {
          setAgentPythonSteps([])
          return
        }
        const prefix = `agents/${agent}/activities/`
        setAgentPythonSteps(
          mine.files
            .filter(f => f.path.startsWith(prefix) && f.path.endsWith('.py'))
            // The picker deals in stems; pathFor re-adds the extension.
            .map(f => f.path.slice(prefix.length, -3))
            // Only bodies that sit DIRECTLY in activities/ — the runtime resolves
            // python.file there and refuses a '..', so a nested path is not a
            // legal step body and must not be offered as one.
            .filter(stem => !stem.includes('/'))
            .sort(),
        )
      })
      .catch(() => setAgentPythonSteps([]))
    return () => {
      cancelled = true
    }
  }, [agent, editMode, inspectorTab])

  // Load the activity list once (all agents' — filtered to `agent` below by
  // `visibleActivities`; the API has no per-agent list endpoint).
  useEffect(() => {
    let cancelled = false
    const seq = ++activitiesSeq.current
    api
      // ADR-134 D10 — the scope is passed EXPLICITLY even though it is false at mount.
      // `allDrafts` starts off, so this is the same request it always made; spelling it
      // out is what makes "the default does not list everyone's drafts" a claim a test
      // can falsify, rather than a property of there being no argument here.
      .listActivities(allDraftsRef.current ? { drafts: 'all' } : undefined)
      .then(res => {
        if (cancelled || seq !== activitiesSeq.current) return
        setActivities(res.activities)
        const mine = res.activities.filter(a => a.agent === agent)
        if (mine.length > 0) {
          // Honor the initialName deep-link if it points at a real activity of
          // this agent; otherwise fall back to the first.
          const deepLinked = initialName && mine.some(a => a.name === initialName)
          const landing = deepLinked ? initialName! : mine[0].name
          // ADR-134 D8 / requirement 4 — THE DEEP LINK IS THE ADDRESS OF A DRAFT TOO.
          //
          // `mine` now includes unpublished drafts, so the URL can name one. Two
          // things had to change together for that to mean anything: the match above
          // can now succeed on a draft row, and the landing slot below must not be
          // marked as publishable. `setIsScaffold(false)` was unconditional here, and
          // on a draft row that both let the detail fetch run (404 → "Failed to load"
          // over an intact draft) and told Propose it was editing a deployed file.
          //
          // The FALLBACK is included deliberately: landing on the facet with no
          // deep-link picks mine[0], and after D8 the newest draft can be first.
          if (mine.some(a => a.name === landing && a.draft === true)) {
            openUnpublishedSlot(agent, landing)
            return
          }
          setIsScaffold(false)
          setSelected({ agent, name: landing })
          // Put the auto-selected workflow in the URL too, not just the
          // explicitly-clicked one (selectActivity).
          //
          // Landing on /agents/<a>/workflows selects the first workflow and used
          // to leave the URL naming only the FACET. An operator copying the
          // address bar to share "this workflow" therefore shared "whatever
          // workflow this agent lists first" — which silently resolves to a
          // different workflow for the recipient the moment one is added or
          // renamed. A URL that is one rename away from meaning something else
          // is not a shareable link.
          if (!deepLinked) navigate(activityPath(agent, landing), { replace: true })
        }
      })
      .catch(err => {
        if (cancelled || seq !== activitiesSeq.current) return
        setListError(
          err instanceof UnauthorizedError
            ? 'Session expired. Please re-authenticate.'
            : err instanceof Error
              ? err.message
              : 'Failed to load activities.',
        )
      })
      .finally(() => {
        if (!cancelled && seq === activitiesSeq.current) setListLoading(false)
      })
    return () => {
      cancelled = true
    }
    // Load-once: the deep-link prop is read only at initial mount on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ADR-134 D10 — refetch when the draft scope changes.
  //
  // Separate from the load-once effect above, which reads `initialName` at mount and
  // must not re-run: re-running it would re-resolve the deep link and yank the operator
  // back to the landing workflow every time they ticked the box. This one replaces the
  // LIST and leaves the selection alone. It skips the first run for the same reason —
  // the mount fetch has already been issued, and a second identical request would race
  // it to decide what the catalog says.
  const draftScopeFirstRun = useRef(true)
  useEffect(() => {
    if (draftScopeFirstRun.current) {
      draftScopeFirstRun.current = false
      return
    }
    void loadActivities()
  }, [allDrafts])

  // ADR-103: the core-agent set — a core-owned `agent` hides "New workflow"/"Edit".
  useEffect(() => {
    let cancelled = false
    api
      .listCoreAgents()
      .then(res => { if (!cancelled) setCoreAgentNames(res.agents ?? []) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // Load the selected activity's detail (raw YAML + parsed JSON + validation),
  // and always drop back to the read-only view — a newly selected activity
  // starts a fresh session, never carries over a stale draft (ADR-079 D1
  // scopes the draft to one editor session).
  //
  // Skipped while isScaffold is set: startNewWorkflow sets `selected` to a
  // brand-new {agent, name} that has no file on the server yet (it would
  // 404) and has ALREADY put the editor into its own from-scratch session —
  // this effect must not fetch over it or call exitEditMode() and wipe it.
  useEffect(() => {
    // isScaffoldRef, not only isScaffold: a draft opened after this render committed has
    // already set it, and fetching or calling exitEditMode() here would undo that open.
    if (!selected || isScaffold || isScaffoldRef.current) return
    let cancelled = false
    setDetailLoading(true)
    setDetailError(null)
    setSelectedNodeId(null)
    setRawYAMLOpen(false)
    setExternalChanged(false)
    exitEditMode()
    api
      .getWorkflowActivity(selected.agent, selected.name)
      .then(res => {
        if (!cancelled) setDetail(res)
      })
      .catch(err => {
        if (cancelled) return
        setDetail(null)
        setDetailError(err instanceof Error ? err.message : 'Failed to load activity.')
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
    // exitEditMode is a stable in-component fn; listing it would re-run needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, isScaffold])

  // Auto-enter edit once the pending-edit target's detail has loaded (arriving
  // from a skill route's "open ↗ to edit", i.e. `?edit=1`).
  //
  // The target is cleared only once it has been ACTED ON — entered, or proven
  // unopenable. It used to be cleared on the pass where `selected` matched and
  // `detail` was still null, which is a render that always happens: `selected`
  // is assigned by the activity-list effect, and the detail fetch is a separate
  // effect that has not yet set `detailLoading`. So the intent was discarded in
  // that gap and the operator, who had asked to edit a workflow, landed on the
  // read view with nothing to explain it.
  useEffect(() => {
    if (!pendingEdit || detailLoading) return
    // A draft opened since this render (isScaffoldRef, see its declaration) has replaced
    // the target: entering edit here would seed the draft's editor with this `detail`.
    if (isScaffoldRef.current) {
      setPendingEdit(null)
      return
    }
    if (!selected || selected.agent !== pendingEdit.agent || selected.name !== pendingEdit.name) return
    if (detail) {
      if (!editMode) enterEditMode()
      setPendingEdit(null)
      return
    }
    // The load finished and produced no activity — a 404, or no permission.
    // Clear the target so a later unrelated load cannot trip it, and leave the
    // read view to show the error it already has.
    if (detailError) setPendingEdit(null)
    // enterEditMode is a stable in-component fn; listing it would re-run needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEdit, detail, detailError, detailLoading, selected, editMode])

  /** Select an activity from the left-pane list — clears any scaffold
   * session so the detail-loading effect above actually fetches it. */
  // There is unsaved work whenever an edit session's draft has diverged from
  // the draft it opened with. Used to guard the actions that silently throw a
  // draft away (switching activities, starting a new one, Cancel, navigating).
  const dirty = editMode && draftYAML !== initialDraftYAML
  // The prompt has to tell the truth about what closing actually does, and that
  // depends on whether autosave is reaching the server. Under ADR-134 D5 it
  // normally is, so "Discard them?" was a promise the code did not keep: closing
  // cleared React state and left the session row, and re-entering the editor
  // restored the very draft the operator had just agreed to discard. The only
  // exit from a draft was a successful Propose. Discarding is now an explicit,
  // separate action (`discardDraft`); closing preserves.
  const confirmDiscard = () =>
    !dirty ||
    (sessionSaveFailed
      ? window.confirm(
          'Your draft could NOT be saved to the server, so closing will lose it. Close anyway?',
        )
      : // The autosave waits for the session read, so before it lands "your draft is
        // saved" is not yet true and closing really does lose the text.
        !sessionSettled
      ? window.confirm(
          'Your draft has NOT been saved yet — still checking for a saved draft on the ' +
            'server. Closing now will lose it. Close anyway?',
        )
      : window.confirm(
          'Close the editor? Your draft is saved and will be here when you come back. ' +
            // The control this names differs by case (ADR-134 D9): a scaffold has no
            // deployed version, and its button says so.
            (isScaffold || !detail
              ? 'To throw it away, use "Discard new workflow" instead.'
              : 'To throw it away and start from the deployed version, use "Discard draft" instead.'),
        ))

  // ADR-101: react to a /fleet version bump (published content changed — via the
  // ADR-100 timer or an ADR-101 webhook). Refresh the activity list so new/
  // renamed/deleted workflows appear; for the open activity, reload it when the
  // editor is idle, else surface a banner (never discard an in-progress draft).
  const fleetSha = useFleetVersion()
  const firstSha = useRef(true)
  const reloadFromServer = () => {
    if (!selected) return
    exitEditMode()
    setDetailLoading(true)
    api.getWorkflowActivity(selected.agent, selected.name)
      .then(res => { setDetail(res); setExternalChanged(false) })
      .catch(err => setDetailError(err instanceof Error ? err.message : 'Failed to reload activity.'))
      .finally(() => setDetailLoading(false))
  }
  useEffect(() => {
    if (!fleetSha) return
    if (firstSha.current) { firstSha.current = false; return }
    void loadActivities()
    // The refs, not only this render's values: a draft opened since (isScaffoldRef, see
    // its declaration) must not be reloaded as a published file.
    if (selected && !isScaffold && !isScaffoldRef.current) {
      if (editMode || editModeRef.current) setExternalChanged(true) // don't disturb an open editor
      else reloadFromServer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleetSha])

  // Warn on browser/tab navigation away while a draft has unsaved changes.
  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  /** ADR-134 D8 — is this catalog row a draft with no published file behind it?
   *
   * Asked of the LIST rather than of a prop, because both callers only have a name:
   * the click handler and the `initialName` deep-link, which resolves before
   * anything has been selected. Absent name → false, which is the right answer for
   * the deep-link's real failure case (a URL naming a workflow that neither exists
   * nor has a draft): it falls through to the published path and reports not-found
   * once, rather than opening an empty editor that invites the operator to author
   * over a name they mistyped. */
  function isUnpublishedDraft(name: string): boolean {
    return activities.some(a => a.agent === agent && a.name === name && a.draft === true)
  }

  /** ADR-134 D10 — whose draft the catalog says this row is, or undefined for my own.
   *
   *  Returns undefined when the row is mine so the request omits `?operator=` entirely,
   *  which is the pre-D10 shape and keeps one code path for the overwhelmingly common
   *  case. `draft_operator` names the NEWEST draft when several exist (`draft_count`),
   *  so this opens the one the row is describing rather than an arbitrary one. */
  function draftOwner(name: string): string | undefined {
    const row = activities.find(a => a.agent === agent && a.name === name && a.draft === true)
    if (!row?.draft_operator || row.draft_operator === user.email) return undefined
    return row.draft_operator
  }

  function selectActivity(name: string) {
    if (!confirmDiscard()) return
    // ADR-134 D8 — A DRAFT ROW HAS NO FILE, SO THE NORMAL PATH WOULD 404 ON IT.
    //
    // The detail-loading effect fetches GET /activities/:agent/:name for whatever
    // `selected` names. For a draft of a workflow that has never been proposed there
    // is nothing to fetch, so clicking the row D8 just added to the catalog would
    // have rendered "Failed to load" over a draft that is sitting in the database
    // intact — the invisibility replaced by a false report of absence, which is
    // worse, because it reads as "your work is gone".
    if (isUnpublishedDraft(name)) {
      // ADR-134 D10 — a fleet-wide list can show a draft that is not mine, and opening
      // it must read THEIR row. Passing my own identity (or nothing) would fetch my
      // empty slot at the same name and seed a blank scaffold over a colleague's work —
      // which then autosaves, creating a second draft of a name that can only ever
      // become one file.
      openUnpublishedSlot(agent, name, draftOwner(name))
      return
    }
    openAsOperator.current = null
    setReadOnlyDraftOf(null)
    setIsScaffold(false)
    setSelected({ agent, name })
    // Reflect the selection in the URL so it can be bookmarked/shared. replace:
    // keep the history stack flat (selection isn't a distinct "page").
    navigate(activityPath(agent, name), { replace: true })
  }

  const isDsl = detail ? isDialectActivity(detail.activity) : false
  const isDraftDsl = draftActivity ? isDialectActivity(draftActivity) : false

  // The graph/inspector render off the DRAFT while editing, and off the
  // last-loaded detail otherwise — same rendering path either way.
  // ADR-134 D10 — a READ-ONLY colleague's draft renders from the DRAFT, not from
  // `detail`. There is no `detail` for a workflow that has never been proposed, and
  // without this fallback the one thing D10 exists to show would render as an empty
  // canvas — visibility granted by the API and withheld by the view.
  const readingOthersDraft = !editMode && !!readOnlyDraftOf
  const activeActivity = editMode || readingOthersDraft ? draftActivity : detail?.activity ?? null
  const activeIsDsl = editMode || readingOthersDraft ? isDraftDsl : isDsl

  const graph = useMemo(() => {
    if (!activeActivity || !activeIsDsl) return { nodes: [] as GraphNode[], edges: [] as GraphEdge[] }
    return buildWorkflowGraph(activeActivity)
  }, [activeActivity, activeIsDsl])

  /** The DEPLOYED workflow's task count, for the one comparison the editor was not
   *  making. Built with the same `buildWorkflowGraph` the canvas uses, so the two
   *  numbers in the warning below are the numbers the operator sees elsewhere — a
   *  top-level `do` length would say 5 where the read view says 7. */
  const deployedNodeCount = useMemo(() => {
    if (!detail || !isDialectActivity(detail.activity)) return 0
    return buildWorkflowGraph(detail.activity).nodes.length
  }, [detail])

  /** AN EMPTY DRAFT SITTING ON TOP OF A WORKFLOW THAT HAS TASKS.
   *
   * Reported on orion `analyst/report-portal-health`: the read view showed 7 tasks and
   * Edit showed an empty canvas. The stored draft's primary was 119 bytes with `do: []`
   * while the deployed v1.0.0 was 1710 bytes with 5 nodes, and ADR-134 D5 makes the
   * stored draft win — correctly, in general, but here it silently replaced the workflow
   * with nothing and said only "Resumed your in-progress work here."
   *
   * The danger is not the blank canvas, it is the Propose button beside it: proposing
   * this changeset DELETES every task in the deployed workflow. The stale-base notice
   * cannot catch it, because the base had not moved — the draft itself is the problem.
   *
   * Deliberately not blocked, only named. Emptying a workflow is a legitimate change and
   * the operator may mean it; what they must not do is mean it by accident. */
  const draftIsEmptyOverDeployed =
    editMode && !isScaffold && graph.nodes.length === 0 && deployedNodeCount > 0

  // ADR-136 D2 — attribute the validator's errors to the nodes they concern.
  // Derived from the graph so a task the operator has since deleted falls back
  // to activity scope instead of badging a node that is no longer there.
  const findings = useMemo(
    () => [
      ...deriveFindings(draftValidation?.errors ?? [], graph.nodes.map(n => n.id)),
      // ADR-136 D5/D6 — advice is ADDITIVE, never a replacement: a step can both
      // fail validation and be badly typed, and the blocking one must come first.
      ...adviseNodes(graph.nodes),
    ],
    [draftValidation, graph],
  )

  // ADR-136 D2 — the findings with no task to attach to, worst first. Declared
  // HERE, above the render that reads it: a `useMemo` consuming a `const`
  // declared further down the component is a temporal dead zone that throws on
  // every render, and `tsc --noEmit` exits 0 on it (see
  // workflowCanvasRender.contract.test.tsx, which exists because that shipped).
  const activityLevelFindings = useMemo(() => activityFindings(findings), [findings])

  const rfNodes = useMemo(() => {
    const base = toRFNodes(graph.nodes, findings, editMode ? requestDeleteTask : undefined)
    const controlEdges = toRFEdges(graph.edges.filter(e => e.kind === 'control'))
    return layoutNodes(base, controlEdges)
    // requestDeleteTask closes over draftActivity/selectedNodeId; it is a stable
    // in-component function, and the memo already re-runs when the graph does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, findings, editMode])

  const rfEdges = useMemo(
    () => toRFEdges(graph.edges.filter(e => showDataEdges || e.kind === 'control')),
    [graph, showDataEdges],
  )

  const selectedNode = selectedNodeId ? graph.nodes.find(n => n.id === selectedNodeId) ?? null : null
  // Every task at every depth (ADR-111) — what the tree renders and what the
  // structural operations address. Replaces the top-level-only list that made
  // "Field mode" both necessary and inadequate.
  const taskTree = useMemo<TaskTreeEntry[]>(
    () => (editMode && draftActivity ? getTaskTree(draftActivity) : []),
    [editMode, draftActivity],
  )
  // The reader's tree, off whatever the canvas is drawing (the published file,
  // or a colleague's draft opened read-only). Without it the read view's only
  // way into a nested task was finding it on the canvas.
  const readTaskTree = useMemo<TaskTreeEntry[]>(
    () => (!editMode && activeIsDsl && activeActivity
      ? getTaskTree(activeActivity as Record<string, unknown>)
      : []),
    [editMode, activeIsDsl, activeActivity],
  )

  // ADR-103: a core-owned `agent` gets no "New workflow" authoring target
  // (viewing its workflows is still fine — see the Edit/New-workflow buttons).
  const isCoreAgent = coreAgentNames.includes(agent)
  const authorableAgentOptions = isCoreAgent ? [] : [agent]

  // The list summary (version + triggers) for the currently-selected activity —
  // surfaced in the viewer header so the operator sees what fires a workflow and
  // its version at a glance (the data is fetched but was otherwise unused).
  const selectedSummary = useMemo(
    () => activities.find(a => selected && a.agent === selected.agent && a.name === selected.name) ?? null,
    [activities, selected],
  )

  // This agent's activities, applying the name search.
  const agentActivities = useMemo(() => activities.filter(a => a.agent === agent), [activities, agent])
  const visibleActivities = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? agentActivities.filter(a => a.name.toLowerCase().includes(q)) : agentActivities
  }, [agentActivities, search])

  // ---- Editor session lifecycle ---------------------------------------

  function enterEditMode() {
    if (!detail) return
    // ADR-134 D5 — a resumed session WINS over the published file. This used to
    // reseed unconditionally from `detail.yaml` with an empty transcript, so
    // opening the editor destroyed the resume the restore effect had just
    // performed, and the autosave then wrote the emptiness back. D5's whole
    // point is that the draft can be retyped and the reasoning cannot; clearing
    // the conversation on entry threw away exactly the half worth keeping.
    const sess = restoredSession.current
    const selfPath = selected ? selfPathFor(selected) : ''
    const storedPrimary = storedDraftFor(sess?.files, selfPath)
    const seedYAML = storedPrimary ?? detail.yaml
    const parsed = parseDraftYAML(seedYAML)
    // Seeded text is nobody's work, so a restore still in flight may adopt over
    // it. Without this a reopened editor still claimed the edit that preceded
    // the Cancel, and the published file it was just reseeded with got cached
    // and autosaved as though the operator had typed it. This is where the
    // ownership flags are cleared, rather than on the way out: a copilot turn
    // answering after Cancel sets them again with the editor closed, so a clear
    // in `exitEditMode` would not hold.
    draftEdited.current = false
    extrasEdited.current = false
    setDraftYAML(seedYAML)
    // The baseline for "is there unsaved work" stays the PUBLISHED file, not the
    // resumed draft — a resumed draft that differs from what is on disk is
    // precisely unsaved work, and calling it clean would let it be discarded
    // without a prompt.
    setInitialDraftYAML(detail.yaml)
    setDraftActivity(parsed.activity)
    setDraftParseError(parsed.error)
    // Seed with the already-known read-view validation result so the panel
    // isn't blank while the first debounced re-validate is in flight. A resumed
    // draft is NOT the published file, so that verdict does not describe it —
    // the debounced validate below replaces it either way.
    setDraftValidation(storedPrimary !== undefined ? null : detail.validation)
    setProposeError(null)
    setProposeResult(null)
    setProposeMessage('')
    if (sess?.files.length) {
      setExtraFiles(sess.files.filter(f => f.path !== selfPath))
    }
    setChatMessages(restoredConversationTurns.current ?? [])
    conversationEdited.current = false
    conversationEstablished.current = false
    setChatInput('')
    setChatSending(false)
    setChatError(null)
    setRunFinished(null)
    setStartNewRunError(null)
    setUnsavedAnswer(null)
    setCopilotUnconfigured(false)
    // The graph is the default workflow view on entry (ADR-121 D4) — for a
    // legacy (stages/steps) activity this falls back to the read-only
    // LegacyView (see the center-pane render below) rather than a graph,
    // same as it always has. The assistant is docked under it either way
    // (ADR-136 D1): there is no longer a mode to switch into to reach it.
    setMode('visual')
    setAssistantOpen(null)
    setAssistantUnread(false)
    // Assigned here rather than mirrored from an effect: a promise resolving
    // between this commit and the passive-effect flush would otherwise read the
    // previous value, and on the way out that means an abandoned turn's text is
    // cached as the operator's.
    editModeRef.current = true
    setEditMode(true)
  }

  function exitEditMode() {
    editModeRef.current = false
    setEditMode(false)
    setIsScaffold(false)
    setDraftYAML('')
    setDraftActivity(null)
    setDraftParseError(null)
    setDraftValidation(null)
    setProposeError(null)
    setProposeResult(null)
    setProposeMessage('')
    setChatMessages([])
    setChatInput('')
    setChatSending(false)
    setChatError(null)
    setRunFinished(null)
    setStartNewRunError(null)
    setUnsavedAnswer(null)
    setCopilotUnconfigured(false)
  }

  /** Cancel button: exit the edit session, and if it was a from-scratch
   * scaffold (no real activity behind `selected`), also clear `selected` so
   * the center pane returns to "select an activity" rather than a phantom
   * selection with no detail. */
  function handleCancelEdit() {
    if (!confirmDiscard()) return
    const wasScaffold = isScaffold
    snapshotBeforeExit()
    exitEditMode()
    if (wasScaffold) setSelected(null)
  }

  /**
   * Throw the durable draft away and start again from the DEPLOYED workflow.
   *
   * The gap this closes: ADR-134 D5 made the draft durable and shipped the
   * DELETE endpoint for exactly this, but nothing ever called it — `api.
   * deleteEditorSession` had no caller anywhere. So the only way to clear a
   * draft was to Propose it successfully, which means the one escape from work
   * you did not want was to ship it. Worse, a draft outlives the file it was
   * based on: once the workflow changes in the repo, resuming silently edits on
   * top of the version you replaced, because `enterEditMode` prefers the
   * session over the published file.
   *
   * Only the CHANGESET is destroyed. Assistant conversations live in their own
   * table (ADR-137 D1) and stay in the History tab, so this discards the *what*
   * and keeps the *why* — which is what makes it safe to offer as one click.
   */
  async function discardDraft() {
    // ADR-134 D9 — A SCAFFOLD HAS NO `detail`, AND THAT USED TO END THE FUNCTION HERE.
    //
    // `detail` is the DEPLOYED file, so a workflow being authored for the first time has
    // none. The old guard returned before the DELETE, and the button was hidden from a
    // scaffold anyway on the reasoning that "discard there is Cancel" — but Cancel never
    // called deleteEditorSession. So Cancel on a new workflow left the row on the server
    // and cleared the slot, producing exactly the orphan D8 had to go looking for: a draft
    // that survives, that the catalog could not list, reachable only by retyping its name.
    //
    // Now: a scaffold discards too, and its post-discard state is an EMPTY editor rather
    // than a reseed from a file that does not exist.
    if (!selected) return
    const scaffold = isScaffold || !detail
    // ADR-134 D10 — DISCARDING SOMEONE ELSE'S DRAFT IS A DIFFERENT ACT, AND MUST SAY SO.
    //
    // The server gates it to the author plus fleet_owner (ADR-137 D9), but a gate is not
    // a warning: destroying a colleague's unproposed work removes the only copy that
    // exists anywhere, and the prompt that precedes it must name whose work it is rather
    // than reading as "discard my draft".
    const owner = readOnlyDraftOf
    if (
      !window.confirm(
        owner
          ? `Discard ${owner}'s draft of this workflow?\n\n` +
              'This is not your draft. It has never been proposed, so there is no other ' +
              'copy of it anywhere — discarding destroys their work, not yours, and it ' +
              'cannot be undone.'
          : scaffold
            ? 'Discard this new workflow and everything drafted for it?\n\n' +
                'It has never been proposed, so there is no deployed version to fall back to — ' +
                'the draft is thrown away and the workflow will not exist. This cannot be undone. ' +
                'Your assistant conversations are kept in the History tab.'
            : 'Discard this draft and start from the deployed version?\n\n' +
                'The draft and any other files in this change are thrown away. This cannot be undone. ' +
                'Your assistant conversations are kept in the History tab.',
      )
    )
      return
    // Which session read this discard belongs to, for the reason `handlePropose`
    // captures one: activity selection stays enabled while the DELETE runs, so an
    // unscoped completion nulls another slot's cache, voids ITS in-flight read,
    // and reseeds it from a `detail` that is no longer on screen.
    // THE DISCARD OPENS A NEW EPOCH, SYNCHRONOUSLY, BEFORE THE DELETE.
    //
    // This is the fix for "drafts are not deleting", reported on orion
    // analyst/build-report: the row came straight back, carrying its whole 5-file
    // changeset. Every server-side writer creates a FILES-FREE row (AppendAssistantTurn
    // and SaveAuthoringRun both say so in as many words), so only the browser's own
    // autosave could have written it — and it did.
    //
    // The window is the `await` below. `exitEditMode()` and `setSelected(null)` run
    // AFTER it, so for the whole round-trip `editMode` is still true and any autosave
    // timer already scheduled by the operator's last keystroke is still pending. It
    // fires into the gap and PUTs the pre-discard changeset back over the row the DELETE
    // just removed. An operator who types and then clicks Discard within 800ms hits this
    // every time, which is why it reads as "discard does nothing" rather than as a race.
    //
    // Bumping the epoch here invalidates every in-flight autosave AND every in-flight
    // restore in one move, using the mechanism this component already has — `handlePropose`
    // guards itself the same way. Edits made AFTER the discard schedule timers against the
    // new epoch and save normally, so nothing is blocked beyond the discard itself.
    const discardedEpoch = restoreEpoch.current + 1
    restoreEpoch.current = discardedEpoch
    const pendingSave = autosaveInFlight.current
    // The DELETE follows the in-flight PUT, whose own deadline bounds the wait.
    if (pendingSave) await pendingSave.catch(() => undefined)
    setDiscardingDraft(true)
    setProposeError(null)
    try {
      // `owner ?? undefined` — omitted for my own, which is the pre-D10 request shape.
      await api.deleteEditorSession(selected.agent, selected.name, owner ?? undefined)
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      // Do NOT reseed on failure. Clearing the editor while the row survives
      // would show a clean draft that comes back on the next reload — the same
      // "discarded but still there" confusion, now with the operator believing
      // it worked.
      setProposeError(
        `Could not discard the draft: ${err instanceof Error ? err.message : 'unknown error'}. It is still saved.`,
      )
      setDiscardingDraft(false)
      return
    }
    // Still the same read: everything below belongs to the slot this discard was
    // for, and the editor may have moved on.
    if (restoreEpoch.current !== discardedEpoch) {
      setDiscardingDraft(false)
      return
    }
    void loadActivities()
    // The client's cached copy must go too, or enterEditMode would resurrect it.
    clearRestoredSession()
    // …and so must a restore that is still in flight. It is a read of the row
    // just deleted, so letting it land would put the discarded draft, extra
    // files and conversation link straight back — after the operator was told
    // it could not be undone.
    restoreVoided.current = true
    setSessionRestored(null)
    setSessionStaleBase(null)
    setSessionSaveFailed(false)
    // Seeded text belongs to nobody, so the flags clear at this reseed as they do
    // at every other one. Leaving them set would let a later read decline a stored
    // draft on the strength of work that has just been discarded.
    draftEdited.current = false
    extrasEdited.current = false
    conversationEdited.current = false
    conversationEstablished.current = false
    setExtraFiles([])
    // A SCAFFOLD LEAVES THE EDITOR; THERE IS NOTHING TO RESEED FROM.
    //
    // Reseeding a scaffold from `detail` is what the old early-return was avoiding, and
    // the right answer is not to skip the discard but to skip the reseed: the workflow
    // does not exist, so the honest end state is no selection at all — the same place
    // Cancel leaves a scaffold, now with the server row actually gone.
    if (scaffold || !detail) {
      setChatMessages([])
      setActiveConversationId(null)
      exitEditMode()
      setSelected(null)
      // The disclosure described a row that no longer exists. Leaving it set would
      // attribute the NEXT draft opened in this editor to whoever owned the last one.
      openAsOperator.current = null
      setReadOnlyDraftOf(null)
      settleDraftIdentity(newDraftID())
      sessionLoadedFor.current = ''
      setDiscardingDraft(false)
      return
    }
    settleDraftIdentity(newDraftID())
    // Reseed from the deployed file — the point of the action, not a side effect.
    const parsed = parseDraftYAML(detail.yaml)
    setDraftYAML(detail.yaml)
    setInitialDraftYAML(detail.yaml)
    setDraftActivity(parsed.activity)
    setDraftParseError(parsed.error)
    setDraftValidation(detail.validation)
    // The inspector's scalar fields are uncontrolled, so without a remount they keep
    // showing the discarded draft's text — and the next blur writes it back into the
    // draft that was just reseeded.
    setFieldsEpoch(e => e + 1)
    // The draft now equals the deployed file, so the "reloaded underneath you"
    // banner is answered: this IS the current version.
    setExternalChanged(false)
    // Detach from the live conversation without deleting it. It stays in History,
    // and a fresh draft should not carry turns that argued for the discarded one.
    setChatMessages([])
    setActiveConversationId(null)
    void refreshConversations(selected.agent, selected.name)
    setDiscardingDraft(false)
  }

  /** Close the editor after a successful propose — no discard confirm (the work
   * was committed), but still clear a scaffold's phantom selection like Cancel. */
  function finishAfterPropose() {
    const wasScaffold = isScaffold
    exitEditMode()
    if (wasScaffold) setSelected(null)
  }

  /** "New workflow" (ADR-079 D7 scaffold-from-scratch): start a blank dsl
   * draft for `agent`/`name` — no server-side detail exists yet — and drop
   * straight into Edit mode with the assistant open, so "build me a workflow
   * that…" can wire it up from nothing. `selected` is set so Propose has an
   * {agent, name} target; isScaffold keeps the detail-loading effect from
   * trying to fetch (and 404 on) a file that doesn't exist yet. */
  function startNewWorkflow(agentArg: string, name: string) {
    if (!confirmDiscard()) return
    setAssistantTab('chat')
    const nextRunParams = new URLSearchParams(runParams)
    nextRunParams.delete('run')
    setRunParams(nextRunParams, { replace: true })
    openUnpublishedSlot(agentArg, name)
    setNewWorkflowOpen(false)
  }

  /** ADR-134 D8 — OPEN AN EXISTING DRAFT OF A WORKFLOW THAT HAS NO FILE YET.
   *
   * The mechanism this needs already worked, which is precisely why the defect was
   * hard to see: the restore effect below is keyed on the SLOT, not on how the slot
   * was opened, so seeding a scaffold at (agent, name) and letting the stored
   * session adopt over the seed is what happened when an operator retyped the name
   * into the create flow. The fleet owner reported exactly that — "if the operator
   * knows the name they can type it in as if creating new and it pull up the draft".
   *
   * So what was missing was never the loading, it was the ROUTE. A draft you can
   * only reach by knowing its name is a password, not a route (D8). This is the
   * same entry with an address: the catalog row and the URL both land here.
   *
   * Factored out of startNewWorkflow rather than called through it because
   * startNewWorkflow opens with `confirmDiscard`, and both callers here have
   * already asked — routing through it would prompt twice for one action.
   */
  function openUnpublishedSlot(agentArg: string, name: string, operator?: string) {
    // ADR-134 D10 — WHOSE draft, read before anything else so the restore below fetches
    // the one that was asked for. Absent means the caller's own.
    openAsOperator.current = operator ?? null
    setReadOnlyDraftOf(null)
    setIsScaffold(true)
    setSelected({ agent: agentArg, name })
    setDetail(null)
    setDetailError(null)
    setDetailLoading(false)
    setSelectedNodeId(null)
    setRawYAMLOpen(false)

    const yamlText = buildScaffoldYAML(agentArg, name)
    const parsed = parseDraftYAML(yamlText)
    // A scaffold is seeded text, so this slot's restore may adopt over it. The
    // reset is not redundant with `exitEditMode`: a typed copilot turn has no
    // cancellation check, so a response landing after Cancel sets ownership
    // again, and this seeds without going back through the editor teardown. The
    // new slot would then decline its own stored draft and autosave the blank
    // scaffold over it.
    draftEdited.current = false
    extrasEdited.current = false
    conversationEdited.current = false
    conversationEstablished.current = false
    setDraftYAML(yamlText)
    setInitialDraftYAML(yamlText)
    setDraftActivity(parsed.activity)
    setDraftParseError(parsed.error)
    setDraftValidation(null)
    setProposeError(null)
    setProposeResult(null)
    setProposeMessage('')
    setChatMessages([])
    setChatInput('')
    setChatSending(false)
    setChatError(null)
    setRunFinished(null)
    setStartNewRunError(null)
    setUnsavedAnswer(null)
    setCopilotUnconfigured(false)
    // A scaffold's canvas is empty and the first move is a conversation, so the
    // dock opens explicitly rather than following the viewport — this is the one
    // entry point where the assistant, not the graph, is what there is to look at.
    //
    // It opens for a COLLEAGUE'S draft too, and that is the point of D10 rather than an
    // accident: their conversation is the record of *why* the workflow is shaped as it
    // is (ADR-137 D1), and on a draft parked mid-authoring it is usually the only thing
    // that explains what went wrong.
    setMode('visual')
    setAssistantOpen(true)
    setAssistantUnread(false)
    // ADR-134 D10 — READ-ONLY FOR SOMEONE ELSE'S DRAFT, ENFORCED BY NOT OPENING THE
    // EDITOR AT ALL.
    //
    // Not a disabled input or a guarded save — simply no edit mode, which is what every
    // write path in this component already keys on. The autosave returns early unless
    // `editMode`, `enterEditMode` is the only thing that seeds from the restored session,
    // and the Edit button is already gated on `detail`, which an unpublished draft does
    // not have. So the read-only guarantee is the absence of the one state change below,
    // rather than a list of places that each have to remember to refuse.
    //
    // Getting this wrong would not merely allow an edit — it would CREATE a second draft
    // of a path that can only ever become one file, from a screen opened to read.
    if (operator) {
      navigate(activityPath(agentArg, name), { replace: true })
      return
    }
    editModeRef.current = true
    setEditMode(true)
    // The SLOT is what makes this addressable (D8 / requirement 4). Without it the
    // address bar still names the facet, so a draft an operator was asked to look at
    // could be described but not linked — and the restore effect that adopts the
    // stored session keys on this slot, so a reload has to arrive back at the same
    // one or it resumes nothing.
    navigate(activityPath(agentArg, name), { replace: true })
  }

  /** Chat's copy of the rail's Start a new run: discards only the finished run Chat refused a turn on, then re-reads the slot for the scope-first box. */
  async function handleStartNewRun() {
    if (!selected || !runFinished) return
    setStartingNewRun(true)
    setStartNewRunError(null)
    try {
      await api.resetAuthoringRun(selected.agent, selected.name, runFinished.runId ?? undefined)
      setRunFinished(null)
      setSlotRunRefresh(n => n + 1)
    } catch (err) {
      if (err instanceof RunChangedError) {
        setRunFinished(null)
        setSlotRunRefresh(n => n + 1)
        setChatError(err.message)
      } else {
        setStartNewRunError(friendlyErrorMessage(err, 'the run could not be cleared'))
      }
    } finally {
      setStartingNewRun(false)
    }
  }

  /** The docked assistant's send: append the user turn immediately (so it's visible
   * while the "thinking…" indicator shows), call the copilot with the full
   * running conversation + the current draft (D7 "also handle the
   * access-admin domain" iterates on it), then merge the response into the
   * shared draft via applyCopilotResponse (workflowDraft.ts) — the SAME
   * draftYAML/draftActivity state Field/YAML/the graph preview read.
   *
   * `seedText`, when given, overrides the chat input for this one send — the
   * "Convert to DSL flow" affordance drives the SAME path a typed turn does
   * (handleConvertToDsl below) rather than a parallel AI path. The chat
   * input box is left untouched in that case (nothing was typed into it). */
  async function handleChatSend(seedText?: string) {
    const text = (seedText ?? chatInput).trim()
    if (!text || !selected || chatSending) return

    conversationEdited.current = true
    const userTurn: CopilotTurn = { role: 'user', content: text }
    const withUser = [...chatMessages, userTurn]
    setChatMessages(withUser)
    setTurnAwaitingAcceptance(userTurn)
    const turnAccepted = () => setTurnAwaitingAcceptance(turn => (turn === userTurn ? null : turn))
    if (seedText === undefined) setChatInput('')
    setChatError(null)
    setRunFinished(null)
    setStartNewRunError(null)
    setUnsavedAnswer(null)
    setChatSending(true)
    setChatElapsedMs(0)
    const turnDraftID = draftIdRef.current
    // ADR-200 amendment — the box's value, only when it was offered for THIS send; then it is
    // cleared, so the next run starts unchecked again.
    const turnScoping = scopingForSend(scopingControl, scopeFirst)
    setScopeFirst(false)
    const turnConversationID = activeConversationIdRef.current
    const stillOwnsTurnConversation = () => conversationEstablished.current
      && turnConversationID !== null
      && activeConversationIdRef.current === turnConversationID
    const epochAtSend = restoreEpoch.current
    try {
      const wireMessages: CopilotMessage[] = withUser.map(m => ({ role: m.role, content: m.content }))
      const res = await api.copilotAuthor(
        selected.agent,
        wireMessages,
        draftYAML,
        chatIntent,
        setChatElapsedMs,
        // ADR-134 D3: the SLOT, so the copilot's validate-repair loop checks the
        // draft where propose will commit it. Without this the loop can converge
        // on a draft the gate then rejects — it was answering a different
        // question than the one that gates the change.
        selected.name,
        // ADR-136 D1: the node selected on the canvas, so "this step" resolves
        // to the one the operator is looking at. Only meaningful in Visual —
        // in Field/YAML the selection belongs to a different list, and sending
        // it would scope the turn to something not on screen.
        mode === 'visual' ? selectedNodeId ?? undefined : undefined,
        // ADR-183 Phase 6 — the per-turn model override, or undefined for the
        // deployment's configured model. Passed through as the operator chose
        // it; the server validates the pair against the generated catalog and
        // against what THIS Mission Control can reach, and a rejection comes
        // back as a 400 whose detail is written for them.
        chatModel.model ? chatModel : undefined,
        turnDraftID || undefined,
        // ADR-200 amendment — `scoping: true|false`. The server reads it only from the
        // request that opens a run.
        turnScoping,
        turnAccepted,
      )
      // A same-slot Retry may adopt a different draft id while a follow-up in
      // an established conversation is running. Keep that answer with its
      // conversation, while still dropping responses from replaced drafts.
      if (turnDraftID && draftIdRef.current !== turnDraftID && !stillOwnsTurnConversation()) return
      if (res.draft_id && turnDraftID && res.draft_id !== turnDraftID) return
      // Retry adopted a stored draft while a follow-up in an established
      // conversation was running; its answer belongs to the conversation, but
      // the draft it edited is gone (#2356 class).
      const draftReplaced = Boolean(turnDraftID) && draftIdRef.current !== turnDraftID
      const resFiles = res.files
      const view = draftReplaced ? { ...res, draft: '', draft_changed: false, files: [] } : res
      if (res.draft_id && !turnDraftID) {
        // Sent while the restore was in flight, so dispatched without an id. Hold the
        // answer until the restore says which draft is open, then compare against that.
        const resolvedDraftID = await resolvedDraftIdentity()
        if (restoreEpoch.current !== epochAtSend || draftIdRef.current !== resolvedDraftID) return
        if (res.draft_id !== resolvedDraftID) {
          if (!draftIdProvisional.current) return
          settleDraftIdentity(res.draft_id)
        }
      }
      setUnsavedAnswer(res.unsaved && res.job_id
        ? { jobId: res.job_id, error: res.save_error || 'the answer could not be saved' }
        : null)
      // applyCopilotResponse decides whether the draft is adoptable — an ANSWER
      // (or a failed turn) returns the operator's own draft back unchanged.
      // Passing draftYAML in is what makes that possible; assigning the
      // response's `draft` unconditionally is what used to blank the editor.
      // The LIVE draft, not the closure. A turn runs 110-710s and the session read
      // is milliseconds, so the restore routinely lands first — and applying the
      // turn against the text as it was when the turn was SENT put the pre-restore
      // draft back, after which the autosave wrote it over the durable row. The
      // re-attach lane below already reads the ref; the two lanes have to agree,
      // or the ownership comparison below is drawn against a value the editor no
      // longer holds (#2356).
      const draftAtAnswer = draftYAMLRef.current
      // And the LIVE transcript, for the same reason. Rebuilding it from the
      // closure erased a restore that landed mid-turn — and because the restore
      // adopts `conversation_id` too, the ADR-137 persist effect then wrote the
      // two-turn stub over the archived conversation's own row
      // (`SaveConversation` replaces `messages`). The user turn is already in the
      // live list unless the restore replaced it, so re-append only then.
      const live = chatMessagesRef.current
      const applied = applyCopilotResponse(ensureTrailingUserTurn(live, userTurn), view, draftAtAnswer)
      // History may have landed this job's persisted answer while completion was
      // still polling. mergeHeldAnswer is the single guard against writing it
      // twice: it maps over the LIVE transcript, so a turn already holding this
      // job id keeps its fetched question and answer and gains only the
      // response's structured fields, and the new array identity still triggers
      // the autosave effect after a resume.
      setChatMessages(mergeHeldAnswer(live, applied.messages, view.job_id))
      conversationEstablished.current = true
      notifyAttention({
        id: copilotJobAttentionId(res, 'workflow-copilot'),
        source: 'workflow-copilot',
        reason: 'new-reply',
      })
      if (draftReplaced && (res.draft_changed || supportingFilesOf(resFiles, selfPathFor(selected)).length > 0)) {
        setChatError("The assistant's draft edit was not applied: Retry restored a different saved draft. Send the request again to apply it here.")
      }
      // ADR-136 D1 — a reply that lands in a collapsed dock is invisible, which
      // reads as the assistant having failed. The dock says there is one.
      if (!assistantExpanded) setAssistantUnread(true)
      // ADR-134 D1: adopt the extra files the turn proposed, MERGING rather than
      // replacing — the operator may have added files by hand, and a copilot turn
      // that silently dropped them would be the same class of lost work as the
      // draft wipe. A path the turn re-states wins, because that is the version
      // it just validated.
      // The slot's own activity is NOT an extra — it is the draft, adopted below. A
      // phased implementation turn returns it as a changeset file too, and keeping it
      // here listed the activity twice in the changeset editor and sent it twice to
      // propose. buildChangeset refuses the duplicate either way; this keeps it from
      // ever entering the list the operator reads.
      //
      // Filtered BEFORE ownership is claimed, not after: a turn whose changeset is
      // only the primary changes no supporting file, and claiming them anyway made a
      // pending restore decline stored ones the operator had never touched — the
      // lost-extras regression again, through a different predicate.
      const turnSupporting = supportingFilesOf(applied.extraFiles, selfPathFor(selected))
      if (turnSupporting.length) {
        extrasEdited.current = true
        setExtraFiles(prev => {
          const byPath = new Map(prev.map(f => [f.path, f]))
          for (const f of turnSupporting) byPath.set(f.path, f)
          return [...byPath.values()]
        })
      }
      // An APPLIED turn is the operator's work as much as typing is, so a restore
      // landing afterwards must not overwrite it. An answer-only turn is not:
      // `applyCopilotResponse` hands the draft back untouched, and counting that
      // as an edit would decline a legitimate resume — after which the autosave
      // writes the published file over the stored draft. Derived by comparing
      // against what went in, so this cannot drift from the helper's own rule.
      if (applied.draftYAML !== draftAtAnswer) draftEdited.current = true
      setDraftYAML(applied.draftYAML)
      setDraftActivity(applied.draftActivity)
      setDraftParseError(applied.draftParseError)
      // An answer says nothing about the draft's validity — keep the validation
      // the draft already had rather than overwriting it with the turn's.
      if (!draftReplaced && (!view.answer || view.draft_changed)) setDraftValidation(view.validation)
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      if (turnDraftID && draftIdRef.current !== turnDraftID && !stillOwnsTurnConversation()) return
      if (err instanceof RunFinishedError) {
        // The live transcript may have been replaced by a restore or History resume while
        // the request was pending. Withdraw only this send's own user turn by identity.
        const live = chatMessagesRef.current
        if (live.includes(userTurn)) setChatMessages(live.filter(m => m !== userTurn))
        if (restoreEpoch.current !== epochAtSend) return
        if (seedText === undefined) setChatInput(current => (current.trim() ? current : text))
        setRunFinished({ runId: err.runId })
        return
      }
      notifyAttention({
        id: localAttentionId('workflow-copilot'),
        source: 'workflow-copilot',
        reason: 'new-reply',
      })
      if (err instanceof CopilotNotConfiguredError) {
        setCopilotUnconfigured(true)
      } else {
        const message = friendlyErrorMessage(err, 'The copilot request failed.')
        setChatError(
          err instanceof StaleDraftError
            ? 'This draft is no longer current. Reload the workflow before sending again.'
            : message,
        )
      }
    } finally {
      setChatSending(false)
      turnAccepted()
    }
  }

  async function handleRetryUnsavedAnswer() {
    if (!unsavedAnswer || retryingUnsavedAnswer) return
    setRetryingUnsavedAnswer(true)
    try {
      await api.retryCopilotSave(unsavedAnswer.jobId)
      setUnsavedAnswer(null)
    } catch (err) {
      setUnsavedAnswer({
        ...unsavedAnswer,
        error: friendlyErrorMessage(err, 'the answer could not be saved'),
        ...(err instanceof UnsavedAnswerGoneError ? { gone: true } : {}),
      })
    } finally {
      setRetryingUnsavedAnswer(false)
    }
  }

  /** "Convert to DSL flow" (owner decision: the migration path for legacy
   * single-prompt activities is a copilot seed in this SAME chat, not a
   * dedicated feature). Composes the canonical seed from the CURRENT DRAFT
   * (draftYAML) — NOT detail.yaml — and sends it through the identical
   * handleChatSend path a typed turn uses. This matters: handleChatSend
   * passes draftYAML as copilotAuthor's `currentDraft` ("current draft to
   * edit"), and detail.yaml is only the on-disk snapshot from when the
   * editor was opened — YAML mode's handleYAMLChange updates draftYAML but
   * never detail. If the operator edits the legacy YAML (e.g. fixing a GID)
   * and then converts, seeding from detail.yaml would hand the copilot two
   * contradictory documents (the edited draft as "current draft to edit"
   * vs. the stale on-disk YAML as "the legacy activity to convert — keep
   * every GID verbatim"), silently dropping the edit. Seeding from the SAME
   * draftYAML that's already passed as currentDraft keeps them in sync. A
   * no-op while a request is already in flight or there is no draft content
   * to convert (mirrors the disabled/hidden guard on the button itself). */
  function handleConvertToDsl() {
    if (chatSending || !draftYAML.trim()) return
    void handleChatSend(buildConvertToDslSeed(draftYAML))
  }

  // ---- ADR-160 D2: re-attach to a turn that is already running -----------
  //
  // `chatSending` and `chatElapsedMs` are page state and the poll lives in the API
  // client, so navigating away during a 110s-710s turn left the editor looking idle
  // while the job ran on server-side. An operator who cannot tell "running" from
  // "nothing happened" re-sends, spending another multi-minute turn on the same slot.
  //
  // Runs when the editor OPENS, not when the slot is selected: entering edit mode resets
  // chatError and chatSending, so probing earlier set a running state the reset then
  // wiped — neither spinner nor notice, while the poll carried on invisibly underneath.
  useEffect(() => {
    if (!editMode || !selected) return
    const { agent: a, name: n } = selected
    let cancelled = false

    void (async () => {
      let info: CopilotInFlight
      try {
        // Awaiting inside try covers a SYNCHRONOUS throw too. A probe must never break
        // the editor, and `.catch()` alone would miss a client that returns nothing —
        // taking the whole facet down on mount over an optional nicety.
        info = await Promise.resolve(api.copilotInFlight(a, n))
      } catch {
        return // the worst case is the behaviour this replaces: no re-attach
      }
      if (cancelled || !info?.running) return

      if (!info.mine || !info.job_id) {
        // Visible, but never attached: adopting another operator's job would drop THEIR
        // draft into this editor.
        setChatError(
          `${info.operator || 'Another operator'} has an assistant turn running on this workflow` +
            (info.elapsed_s ? ` (${info.elapsed_s}s so far)` : '') +
            ' — its answer will land in their editor.',
        )
        return
      }

      // Seeded with the SERVER's elapsed time: counting from zero here would tell the
      // operator a four-minute turn had just started.
      const already = (info.elapsed_s ?? 0) * 1000
      setChatSending(true)
      setChatElapsedMs(already)
      try {
        const epochAtAttach = restoreEpoch.current
        const res = await api.copilotReattach(info.job_id, already, setChatElapsedMs)
        if (cancelled) return
        if (res.draft_id) {
          // Held until the restore has settled the identity AND applied the stored
          // draft, so an answer for another draft can never be adopted ahead of it.
          const resolvedDraftID = await resolvedDraftIdentity()
          if (cancelled || restoreEpoch.current !== epochAtAttach || !resolvedDraftID) return
          if (res.draft_id !== resolvedDraftID) return
        }
        // The same adoption path a fresh turn takes — applyCopilotResponse decides
        // whether the draft is adoptable, so a re-attached answer cannot blank the
        // editor any more than a live one can.
        const draftAtAnswer = draftYAMLRef.current
        const applied = applyCopilotResponse(chatMessagesRef.current, res, draftAtAnswer)
        conversationEdited.current = true
        conversationEstablished.current = true
        setChatMessages(mergeHeldAnswer(chatMessagesRef.current, applied.messages, info.job_id))
        setUnsavedAnswer(res.unsaved && res.job_id
          ? { jobId: res.job_id, error: res.save_error ?? '' }
          : null)
        notifyAttention({
          id: copilotJobAttentionId(info, 'workflow-copilot'),
          source: 'workflow-copilot',
          reason: 'new-reply',
        })
        // Only a turn that actually changed the draft counts as local work — see
        // the same comparison on the typed-turn path above.
        if (applied.draftYAML !== draftAtAnswer) draftEdited.current = true
        setDraftYAML(applied.draftYAML)
        setDraftActivity(applied.draftActivity)
        setDraftParseError(applied.draftParseError)
        // Same filtering AND the same merge as the typed-turn lane: the primary is
        // the draft, not an extra; a changeset carrying only the primary claims no
        // ownership; and replacing the list dropped supporting files the operator
        // had added by hand, which is the lost-work class this whole change is
        // about. A path the turn re-states wins, because that is the version it
        // just validated.
        const supporting = supportingFilesOf(applied.extraFiles, selfPathFor(selected))
        if (supporting.length) {
          extrasEdited.current = true
          setExtraFiles(prev => {
            const byPath = new Map(prev.map(f => [f.path, f]))
            for (const f of supporting) byPath.set(f.path, f)
            return [...byPath.values()]
          })
        }
      } catch (err) {
        if (!cancelled) {
          setChatError(err instanceof Error ? err.message : 'the resumed turn failed')
          if (!(err instanceof UnauthorizedError)) {
            notifyAttention({
              id: localAttentionId('workflow-copilot'),
              source: 'workflow-copilot',
              reason: 'new-reply',
            })
          }
        }
      } finally {
        if (!cancelled) setChatSending(false)
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, selected?.agent, selected?.name])

  // ---- ADR-134 D5: restore, then autosave -------------------------------
  //
  // Restore runs once per (agent, workflow) so a re-render cannot clobber the
  // editor with a stale server copy mid-typing. It only adopts the session when
  // the operator has NOT already started editing here — a resumed draft must
  // never overwrite fresher local work.
  useEffect(() => {
    if (!selected) return
    // ADR-134 D10 — THE OPERATOR IS PART OF THE KEY.
    //
    // The session's identity is (agent, workflow, operator), and once the read is
    // fleet-wide two of those rows can share a slot. Keying on the slot alone made the
    // once-per-slot guard answer "already loaded" when the operator had changed, so
    // switching from my own draft of a name to a colleague's served the first one and
    // never fetched the second — a stale read that looks exactly like the colleague
    // having nothing.
    const wantOperator = openAsOperator.current
    const key = sessionKey(selected.agent, selected.name, wantOperator)
    if (sessionLoadedFor.current === key) return
    const sameSlotRetry = retryingKey.current === key
    retryingKey.current = null
    sessionLoadedFor.current = key
    beginDraftIdentity()
    restoreEpoch.current += 1
    sessionRevision.current = 0
    cachedRevision.current = 0
    sessionRetired.current = false
    setSessionRestored(null)
    setSessionSettled(false)
    setRestoreFailed(null)
    if (!sameSlotRetry) setActiveConversationId(null)
    clearRestoredSession()
    restoreVoided.current = false

    const controller = new AbortController()
    let cancelled = false
    let settled = false
    let voided = false
    let failed = false
    const deadline = window.setTimeout(() => {
      if (cancelled || settled) return
      voided = true
      controller.abort()
      if (!draftIdRef.current) settleDraftIdentity(newDraftID(), true)
      setRestoreFailed({ reason: 'timeout', detail: `no answer within ${Math.round(restoreDeadlineMs / 1000)}s` })
    }, restoreDeadlineMs)
    api
      .getEditorSession(selected.agent, selected.name, wantOperator ?? undefined, { signal: controller.signal })
      .then(async sess => {
        if (cancelled || voided) {
          if (!draftIdRef.current) settleDraftIdentity(newDraftID())
          return
        }
        settleDraftIdentity(sess?.draft_id || newDraftID(), !sess?.draft_id)
        // A discard landed while this was in flight: the row is gone, so this
        // response describes nothing. Drop it entirely rather than stashing it.
        if (restoreVoided.current) {
          settleDraftIdentity(newDraftID())
          return
        }
        // Nothing stored for this slot. The cache was already cleared above.
        if (!sess) return
        let linkedMessages: EditorConversation['messages'] = []
        let linkedConversationReadFailed = false
        if (sess.transcript?.length) {
          if (sess.conversation_id) {
            const readLinked = () => api.getEditorConversation(selected.agent, selected.name, sess.conversation_id!, { signal: controller.signal })
            try {
              linkedMessages = (await readLinked()).messages
            } catch {
              if (cancelled || voided || restoreVoided.current) return
              await new Promise<void>(resolve => window.setTimeout(resolve, linkedReadRetryDelayMs))
              if (cancelled || voided || restoreVoided.current) return
              try {
                linkedMessages = (await readLinked()).messages
              } catch {
                linkedConversationReadFailed = true
              }
            }
          }
        }
        if (cancelled || voided || restoreVoided.current) return
        // ADR-134 D10 — SAY WHOSE CHANGESET THIS IS, BEFORE THE OPERATOR TYPES.
        //
        // Read from the payload rather than from `wantOperator`, because the server is
        // the authority on whose row it served and a request can be satisfied by the
        // caller's own row. Silence here would leave the operator believing one of two
        // wrong things: that they are co-editing a shared document, or that their own
        // draft has been replaced by someone else's work.
        setReadOnlyDraftOf(sess.operator && sess.operator !== user.email ? sess.operator : null)
            // The primary activity is the file at the slot's own path; everything
        // else is the rest of the changeset and must come back too, or resuming
        // silently narrows a multi-file change to one file.
        const selfPath = selfPathFor(selected)
        const rest = sess.files.filter(f => f.path !== selfPath)
        // This effect is keyed on the SLOT, not on `editMode`, so it can resolve
        // with the editor already open and worked in. That a resumed draft never
        // overwrites fresher local work is this effect's own invariant, stated in
        // the comment above it — ADR-134 D5 settles durability, keying and
        // autosave, and leaves the conflict question to the editor.
        // Ownership only counts while the editor is OPEN. Both copilot lanes
        // apply a turn's answer with no check that the editor is still there, so
        // a turn answering after Cancel sets the flags over a closed editor;
        // taking that as local work declined the stored draft and cached the
        // abandoned text, which the autosave then wrote over the durable row.
        //
        // Nothing real is lost by requiring the editor: the autosave effect
        // returns early unless `editMode`, so a draft written while the editor is
        // closed never reaches the server, and every reopen goes through
        // `enterEditMode`, which clears both flags before it seeds.
        const editorOpen = editModeRef.current
        const localWorkSince = editorOpen && draftEdited.current
        const storedPrimary = storedDraftFor(sess.files, selfPath)
        const adoptPrimary = storedPrimary !== undefined && !localWorkSince
        // Judged on its OWN ownership. Sharing the primary's answer went wrong
        // both ways: unconditional adoption replaced supporting files the
        // operator had added, and keying off the primary discarded stored ones
        // they had never touched because they had typed in the YAML pane.
        //
        // All-or-nothing, deliberately, where both copilot lanes union by path. A
        // turn's changeset is ADDITIVE — it says "also touch these" — so merging
        // it loses nothing. A session's changeset is a SNAPSHOT of the whole
        // change, and in a snapshot a file's absence is meaningful: removing a row
        // in the Files tab is expressed by it no longer being there. Unioning a
        // stored snapshot into the operator's list would resurrect exactly the
        // file they had just removed, and nothing in either list distinguishes
        // "never had this" from "took this out".
        const localExtras = editorOpen && extrasEdited.current
        const adoptExtras = rest.length > 0 && !localExtras
        // Stash for `enterEditMode`, which reads this instead of reseeding from
        // the published file with an empty conversation. Whatever was DECLINED
        // must not be cached: `enterEditMode` seeds from this ref, so a declined
        // copy is handed straight back on the next Cancel/Edit and the autosave
        // then commits it over the operator's work. Each half independently —
        // theirs where it was declined, the session's where it was adopted.
        const cachedPrimary = localWorkSince ? draftYAMLRef.current : storedPrimary
        restoredSession.current = {
          ...sess,
          files: [
            ...(cachedPrimary !== undefined
              ? [{ path: selfPath, content: cachedPrimary, op: 'write' as const }]
              : []),
            ...(adoptExtras
              ? supportingFilesOf(rest, selfPath)
              : localExtras
                ? extraFilesRef.current
                : []),
          ],
        }
        // `draftYAML`, `draftActivity` and `draftParseError` are ONE piece of
        // state: the text, its parse, and the parse's error. Assigning the text
        // alone left the graph, the task tree and the inspector's field forms
        // describing the file the operator is no longer editing, and left
        // `draftParseError` stale in both directions — no error on a restored
        // draft that does not parse, and a surviving error on a restore that
        // fixed one (#2337). Every other seeding site sets all three together.
        //
        // A malformed draft is still adopted: `draftParseError` exists to carry
        // that case into the UI, and discarding a draft for not parsing would be
        // a worse defect than the one this fixes.
        // Spelled out rather than `adoptPrimary` so TS narrows `storedPrimary`.
        if (storedPrimary !== undefined && !localWorkSince) {
          const parsed = parseDraftYAML(storedPrimary)
          // Read BEFORE the seed. `setDraftYAML` assigns the live mirror in the
          // same statement, so asking this question afterwards compares the
          // adopted text with itself and never clears anything.
          const replacesTheText = storedPrimary !== draftYAMLRef.current
          setDraftYAML(storedPrimary)
          setDraftActivity(parsed.activity)
          setDraftParseError(parsed.error)
          // The verdict on screen is the PUBLISHED file's, which is not what the
          // editor now holds — `enterEditMode` drops it for the same reason.
          //
          // Only when the text actually CHANGED, though. The debounced validate is
          // keyed on the draft, so restoring a draft identical to what is already
          // in the pane starts no replacement run, and clearing the verdict then
          // left Propose disabled — reading "Fix validation errors first" over
          // valid YAML — until the operator typed something or reopened.
          if (replacesTheText) setDraftValidation(null)
        }
        if (adoptExtras) setExtraFiles(supportingFilesOf(rest, selfPath))
        const storedTurns = sess.transcript?.length
          ? restoreConversationTurns(sess.transcript, linkedMessages)
          : []
        const storedId = sess.transcript?.length && !linkedConversationReadFailed
          ? sess.conversation_id || null
          : null
        const conv = reconcileConversation({
          local: chatMessagesRef.current,
          localId: activeConversationIdRef.current,
          // A first pending turn over an unowned editor has not established a transcript,
          // so its answer merges into whichever transcript the restore adopts. A pending
          // turn in an established conversation keeps ownership across the restore.
          localOwned: editorOpen && conversationEdited.current && (conversationEstablished.current || !chatSendingRef.current),
          stored: storedTurns,
          storedId,
        })
        if (conv.outcome === 'declined') {
          restoredConversationTurns.current = conv.turns
        } else {
          setActiveConversationId(conv.id)
          if (storedTurns.length) {
            restoredConversationTurns.current = conv.turns
            setChatMessages(conv.turns)
          }
        }
        // What the notice may claim. The changeset and the conversation come back
        // independently now, and one sentence covering both said the draft had
        // been resumed when only the transcript had.
        const restoredTranscript = conv.outcome !== 'declined' && storedTurns.length > 0
        if (adoptPrimary || adoptExtras) {
          setSessionRestored(restoredTranscript ? 'changeset-and-conversation' : 'changeset')
        } else if (restoredTranscript) setSessionRestored('conversation')
        // Compare against the fleet version the page already tracks. Only flag a
        // MISMATCH: an absent base_ref means the session predates the field, and
        // claiming staleness we cannot demonstrate would train the operator to
        // ignore the notice.
        //
        // And only over a DRAFT that came from the session. The notice says the
        // base that draft was last checked against has moved, and its action
        // discards the draft — so over text the operator typed it is advice about
        // a version they are not editing, attached to a button that throws their
        // work away. `handleYAMLChange` clears it on the same reasoning. A session
        // whose changeset is extra files only has no primary to speak for, so an
        // adopted extra carries it instead.
        const noticeDescribesTheDraft =
          adoptPrimary || (storedPrimary === undefined && adoptExtras)
        if (noticeDescribesTheDraft && sess.base_ref && fleetSha && sess.base_ref !== fleetSha) {
          setSessionStaleBase(sess.base_ref)
        }
      })
      .catch(error => {
        if (cancelled || voided) return
        failed = true
        if (!draftIdRef.current) settleDraftIdentity(newDraftID(), true)
        setRestoreFailed({ reason: 'error', detail: error instanceof Error ? error.message : String(error) })
      })
      .finally(() => {
        window.clearTimeout(deadline)
        if (!cancelled && !voided && !failed) {
          settled = true
          setSessionSettled(true)
        }
      })
    return () => {
      cancelled = true
      controller.abort()
      window.clearTimeout(deadline)
      // A read cancelled before it settled loaded nothing for this key, so leaving
      // the marker set makes re-selecting the slot early-return — the read never
      // happens again and `sessionSettled` stays false for the life of the page,
      // which now also means Propose stays disabled. Do NOT settle it from here
      // instead: on a `selected → null` teardown nothing sets it false again, which
      // reopens the autosave-over-an-unread-row hole the flag exists to close.
      if (!settled && sessionLoadedFor.current === key) sessionLoadedFor.current = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.agent, selected?.name, restoreNonce])

  // ---- ADR-137: the conversation catalog ----------------------------------

  /** Reload the catalog for the open slot.
   *
   *  Always the WHOLE slot (ADR-137 D8), filtered to an author in the browser.
   *  One request means switching the filter is instant and the author list can be
   *  derived from the rows — a server round-trip per filter change would also
   *  make "who else has talked about this workflow?" unanswerable until you had
   *  already picked someone. The rows carry no transcripts, so the slot is small
   *  (D6 caps each author at 50). */
  function refreshConversations(agentArg: string, nameArg: string) {
    if (!isOpenSlot(agentArg, nameArg)) return Promise.resolve()
    const gen = ++catalogGen.current
    const live = () => catalogGen.current === gen && isOpenSlot(agentArg, nameArg)
    setConversationsLoading(true)
    setConversationsError(null)
    return api
      .listEditorConversations(agentArg, nameArg, 'all')
      .then(list => { if (live()) setConversations(list) })
      .catch(() => {
        // A catalog that cannot be listed is not a broken editor. Say so where
        // the list would be, and leave the live conversation working.
        if (live()) setConversationsError('Could not load earlier conversations.')
      })
      .finally(() => { if (live()) setConversationsLoading(false) })
  }

  // A refresh for the slot being left must not own the loading state of the next one.
  useEffect(() => {
    setConversationsLoading(false)
  }, [selected?.agent, selected?.name])

  // History loads when the slot opens; nothing is adopted by name. A slot with
  // no stored transcript is the discarded-name and New-chat case, so the
  // session link above is the only automatic resume.
  useEffect(() => {
    if (!editMode || !selected || (!sessionSettled && !restoreFailed)) return
    const { agent: a, name: n } = selected
    void refreshConversations(a, n)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, selected?.agent, selected?.name, sessionSettled, restoreFailed])

  // Persist the live conversation as it grows. Debounced like the session
  // autosave, and skipped while a resume is landing so loading a conversation
  // cannot immediately fork a copy of it.
  useEffect(() => {
    if (!editMode || !selected || !sessionSettled) return
    if (resumingRef.current) return
    if (chatMessages.length === 0) return
    // See turnAwaitingAcceptance above: save a first turn only after the server accepts it.
    if (chatMessages.length === 1 && chatMessages[0] === turnAwaitingAcceptance) return
    const { agent: a, name: n } = selected
    const timer = window.setTimeout(() => {
      const idAtSave = activeConversationId
      const save = api.saveEditorConversation(a, n, {
        ...(idAtSave ? { id: idAtSave } : {}),
        messages: catalogMessages(chatMessages),
      })
      void save
        .then(res => {
          if (!isOpenSlot(a, n) || activeConversationIdRef.current !== idAtSave) return
          setActiveConversationId(res.id)
          return refreshConversations(a, n)
        })
        .catch(() => setConversationsError('Could not save this conversation.'))
    }, 900)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages, turnAwaitingAcceptance, editMode, sessionSettled, selected?.agent, selected?.name])

  /** ADR-137 D2 — resume swaps the TRANSCRIPT and never the draft. */
  function resumeConversation(id: string) {
    if (!selected) return
    conversationEdited.current = true
    conversationEstablished.current = true
    resumingRef.current = true
    setAssistantTab('chat')
    api
      .getEditorConversation(selected.agent, selected.name, id)
      .then(conv => {
        setActiveConversationId(conv.id)
        setChatMessages(conversationTurns(conv.messages))
      })
      .catch(() => setConversationsError('Could not open that conversation.'))
      .finally(() => {
        // Cleared on a later tick so the chatMessages update above has already
        // flushed — otherwise the autosave effect sees the resumed transcript
        // with the guard already down and writes it back as a new conversation.
        window.setTimeout(() => { resumingRef.current = false }, 0)
      })
  }

  function deleteConversation(id: string, title: string) {
    if (!selected) return
    if (!window.confirm(`Delete "${title || 'Untitled conversation'}"? This cannot be undone.`)) return
    const { agent: a, name: n } = selected
    api
      .deleteEditorConversation(a, n, id)
      .then(() => {
        // Deleting the one on screen leaves the composer empty rather than
        // showing a transcript that no longer exists anywhere.
        if (id === activeConversationId) {
          setActiveConversationId(null)
          setChatMessages([])
        }
        return refreshConversations(a, n)
      })
      .catch(() => setConversationsError('Could not delete that conversation.'))
  }

  /** Start an empty conversation. Earlier conversations remain available in History. */
  function newConversation() {
    conversationEdited.current = true
    conversationEstablished.current = true
    setActiveConversationId(null)
    setChatMessages([])
    setChatInput('')
    setChatError(null)
    setRunFinished(null)
    setStartNewRunError(null)
    setUnsavedAnswer(null)
    setAssistantTab('chat')
    setAssistantOpen(true)
  }

  interface SessionSnapshot {
    draftYAML: string
    extraFiles: ProposedChangesetFile[]
    chatMessages: CopilotTurn[]
    conversationId: string | null
  }

  function refreshSessionCache(s: SessionSnapshot & {
    key: string
    epoch: number
    draftID: string
    revision: number
  }) {
    if (sessionRetired.current) return
    if (!selected) return
    if (sessionLoadedFor.current !== s.key) return
    if (restoreEpoch.current !== s.epoch) return
    if (draftIdRef.current !== s.draftID) return
    if (s.revision < cachedRevision.current) return
    cachedRevision.current = s.revision
    restoredSession.current = {
      ...(restoredSession.current ?? {
        agent: selected.agent, workflow: selected.name, transcript: [], updated_at: '',
      }),
      draft_id: s.draftID,
      files: buildChangeset(selected.agent, selected.name, s.draftYAML, s.extraFiles),
      transcript: toStoredTranscript(s.chatMessages),
      conversation_id: s.conversationId ?? '',
    }
    restoredConversationTurns.current = s.chatMessages
  }

  type SessionSavePayload = Partial<Pick<EditorSession, 'files' | 'transcript' | 'base_ref' | 'conversation_id' | 'draft_id'>>

  function sessionSavePayload(draftID: string, s: SessionSnapshot): SessionSavePayload {
    // base_ref is the fleet sha this draft was validated against.
    // conversation_id travels with the transcript; an empty value never clears a stored link (the server coalesces it).
    return {
      draft_id: draftID,
      files: buildChangeset(selected!.agent, selected!.name, s.draftYAML, s.extraFiles),
      base_ref: fleetSha ?? '',
      transcript: toStoredTranscript(s.chatMessages),
      conversation_id: s.conversationId ?? '',
    }
  }

  function enqueueSessionSave(args: {
    key: string
    epoch: number
    draftID: string
    revision: number
    agent: string
    name: string
    snapshot: SessionSnapshot
  }): Promise<void> {
    const payload = sessionSavePayload(args.draftID, args.snapshot)
    const prior = autosaveInFlight.current ?? Promise.resolve()
    const p = prior.catch(() => undefined).then(() => {
      if (restoreEpoch.current !== args.epoch || draftIdRef.current !== args.draftID || sessionRetired.current) return
      const controller = new AbortController()
      const timer = window.setTimeout(() => controller.abort(), sessionSaveDeadlineMs)
      return api.saveEditorSession(args.agent, args.name, payload, { signal: controller.signal })
        .finally(() => window.clearTimeout(timer))
    }).then(() => {
      refreshSessionCache({
        key: args.key,
        epoch: args.epoch,
        draftID: args.draftID,
        revision: args.revision,
        ...args.snapshot,
      })
      if (draftIdRef.current === args.draftID) draftIdProvisional.current = false
      setSessionSaveFailed(false)
    }).catch(() => {
      setSessionSaveFailed(true)
    }).finally(() => {
      if (autosaveInFlight.current === p) autosaveInFlight.current = null
    })
    autosaveInFlight.current = p
    return p
  }

  function snapshotBeforeExit() {
    if (!selected || !editModeRef.current || !sessionSettled || sessionRetired.current) return
    const key = sessionKey(selected.agent, selected.name, openAsOperator.current)
    if (sessionLoadedFor.current !== key) return

    const epoch = restoreEpoch.current
    const draftID = ensureDraftID()
    const revision = ++sessionRevision.current
    const snapshot: SessionSnapshot = {
      draftYAML: draftYAMLRef.current,
      extraFiles: extraFilesRef.current,
      chatMessages: chatMessagesRef.current,
      conversationId: activeConversationIdRef.current,
    }
    refreshSessionCache({ key, epoch, draftID, revision, ...snapshot })

    if (!autosavePending.current) return
    if (autosaveTimer.current !== null) window.clearTimeout(autosaveTimer.current)
    autosaveTimer.current = null
    autosavePending.current = false
    void enqueueSessionSave({
      key, epoch, draftID, revision, agent: selected.agent, name: selected.name, snapshot,
    })
  }

  // Autosave the changeset AND the transcript. The transcript is the point: a
  // draft can be retyped, the reasoning behind its shape cannot.
  useEffect(() => {
    if (!editMode || !selected) return
    // Through sessionKey, NOT a rebuilt template literal. Rebuilding it here is what
    // let the writer's format and this reader's drift apart in the first place.
    if (sessionLoadedFor.current !== sessionKey(selected.agent, selected.name, openAsOperator.current)) return
    // Not until the restore has SETTLED. Autosaving in that window writes the
    // editor's still-empty transcript over the stored one — the difference
    // between a conversation that is briefly not shown and one that is gone.
    if (!sessionSettled) return
    // The epoch and key belong to the schedule-time snapshot. A discard that
    // happens before the debounce fires makes this timer a no-op.
    const scheduledEpoch = restoreEpoch.current
    const key = sessionKey(selected.agent, selected.name, openAsOperator.current)
    autosavePending.current = true
    const timer = window.setTimeout(() => {
      if (autosaveTimer.current === timer) autosaveTimer.current = null
      autosavePending.current = false
      if (restoreEpoch.current !== scheduledEpoch) return
      const draftID = ensureDraftID()
      const revision = ++sessionRevision.current
      const snapshot: SessionSnapshot = {
        draftYAML,
        extraFiles,
        chatMessages,
        conversationId: activeConversationId,
      }
      void enqueueSessionSave({
        key, epoch: scheduledEpoch, draftID, revision,
        agent: selected.agent, name: selected.name, snapshot,
      })
    }, 800)
    autosaveTimer.current = timer
    return () => {
      window.clearTimeout(timer)
      if (autosaveTimer.current === timer) {
        autosaveTimer.current = null
        autosavePending.current = false
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftYAML, chatMessages, extraFiles, editMode, sessionSettled, selected?.agent, selected?.name])

  // Debounced live validation of the draft (~400ms after the last edit,
  // whichever mode produced it) — the SAME endpoint/validator the read view
  // uses, so there is one source of validation truth (PRD §6).
  const slotAgent = selected?.agent
  const slotName = selected?.name
  useEffect(() => {
    if (!editMode) return
    const seq = ++validateSeq.current
    const timer = window.setTimeout(() => {
      setValidating(true)
      api
        .validateWorkflowDraft(
          draftYAML,
          // ADR-134 D3: validate at the slot propose will write to. A scaffold
          // has no slot on the server yet, but its name is still the path it
          // will be created at, so it is passed too.
          slotAgent && slotName ? { agent: slotAgent, name: slotName } : undefined,
          // D1: with extra files, validate the CHANGESET. Validating only the
          // activity while the gate judges the cron and the route too would put
          // the editor back to green-on-a-rejected-change.
          extraFiles.length > 0 && slotAgent && slotName
            ? buildChangeset(slotAgent, slotName, draftYAML, extraFiles)
            : undefined,
        )
        .then(res => {
          if (validateSeq.current === seq) setDraftValidation(res)
        })
        .catch(() => {
          if (validateSeq.current === seq) {
            setDraftValidation({ ok: true, errors: [], note: 'validator unavailable' })
          }
        })
        .finally(() => {
          if (validateSeq.current === seq) setValidating(false)
        })
    }, 400)
    return () => window.clearTimeout(timer)
    // Depend on the slot's two primitives rather than `selected` itself: the
    // object's identity changes on every list refresh, which would re-run the
    // debounce for no reason, and re-validating is a real cost now that D3
    // runs both validators over a seeded tree.
  }, [draftYAML, editMode, slotAgent, slotName, extraFiles])

  function handleYAMLChange(text: string) {
    // The notice describes the RESTORED draft. Once the operator edits, live
    // validation is running against the current tree again, so keeping it up
    // would be stale advice about staleness.
    setSessionStaleBase(null)
    draftEdited.current = true
    setDraftYAML(text)
    const parsed = parseDraftYAML(text)
    setDraftActivity(parsed.activity)
    setDraftParseError(parsed.error)
  }

  /** Field-mode mutators all funnel through here: apply to draftActivity,
   * then re-serialize into draftYAML — the one shared draft, kept in sync
   * across modes (ADR-079 D2: "mode switches are lossless"). */
  function applyDraftMutation(next: Record<string, unknown>) {
    draftEdited.current = true
    setDraftActivity(next)
    setDraftYAML(serializeDraftYAML(next))
  }

  /**
   * Write picked data references — from the inline hint or the caret picker — as ONE draft
   * mutation (ADR-179).
   *
   * Every selection is re-verified first, and the two kinds are verified differently
   * because they claim different things. A `replace` claims the literal at a location is
   * the resolved value of a published reference: that is re-asked of the canonical matcher,
   * and the chosen candidate has to come back with the same token, pin and value, or
   * nothing is written. An `insert` claims only that a caret sat at a range in a field the
   * operator was looking at, so there is nothing to compare values against; the range check
   * in `applyDataReferenceSelections` is its whole verification.
   *
   * A draft edited while the verification was in flight refuses the apply outright. The
   * answer describes a document that no longer exists, and silently applying it to the new
   * one would write a reference the operator never saw offered.
   *
   * A refusal is reported through `setDataRefRefusal`, which the sweep panel and the field
   * affordance both render beside the row it belongs to. The promise settles either way, and
   * carries no outcome: the Apply button only waits on it.
   */
  async function applyDataReferences(
    selections: DataReferenceSelection[],
  ): Promise<void> {
    // `draftYAMLRef` is written synchronously by `setDraftYAML` while `draftActivity` is
    // ordinary state, so at call time the two can disagree — and every freshness check below
    // reads the ref. Parse the document the ref holds rather than trusting the state to be
    // the same one, or a write could be decided against a draft nobody is looking at.
    const parsed = parseDraftYAML(draftYAMLRef.current ?? '')
    const activity = parsed.activity
    setDataRefRefusal(null)
    const refuse = (message: string) => {
      setDataRefRefusal(message)
      // The sweep panel is hidden on the other tabs, and a refusal nobody sees is an apply
      // the operator believes succeeded. Bring them back to where the answer is.
      if (sweepOpen) setInspectorTab('inspector')
    }
    if (!activity) {
      if (!draftActivity) return
      refuse('The draft could not be read, so nothing was applied — fix the YAML first.')
      return
    }

    const replacements = selections.filter(
      (s): s is DataReferenceReplaceSelection => s.kind === 'replace')
    if (replacements.length > 0) {
      const before = draftYAMLRef.current
      const editsBefore = fieldEditGeneration.current
      let verified: DataReferenceMatches
      try {
        verified = await api.dataReferenceMatches(
          before, String(++verifyRevision.current), replacements.map(s => s.location))
      } catch (e) {
        return refuse('Could not re-check these values before writing them: '
          + (e instanceof Error ? e.message : String(e)))
      }
      if (draftYAMLRef.current !== before) {
        return refuse('The draft changed while these values were being re-checked — review again.')
      }
      // A keystroke in an uncontrolled field never reaches the YAML above, and applying now
      // would remount the fields over it. Refuse instead, leaving the typing where it is.
      if (fieldEditGeneration.current !== editsBefore) {
        return refuse('A field was edited while the values were being checked. '
          + 'Re-check and apply again.')
      }
      if (verified.note) return refuse(verified.note)
      for (const selection of replacements) {
        const found = verified.locations.find(m => sameLocation(m.location, selection.location))
        const still = found?.candidates.find(c =>
          c.token === selection.candidate.token
          && c.value === selection.candidate.value
          && c.pin.alias === selection.candidate.pin.alias
          && c.pin.set === selection.candidate.pin.set)
        if (!still) {
          return refuse(
            `${describeLocation(selection.location, ' ', '`')} no longer offers `
            + `${selection.candidate.token}. Nothing was applied — review the matches again.`)
        }
      }
    }

    const result = applyDataReferenceSelections(activity, selections)
    if (result.refusal) return refuse(result.refusal)
    applyDraftMutation(result.activity)
    setFieldsEpoch(e => e + 1)
  }

  async function handlePropose() {
    if (!selected) return
    const proposedDraftID = ensureDraftID()
    // Which session read this propose belongs to. `restoredSession` and
    // `restoreVoided` are single refs shared by every slot, and the completion
    // arrives minutes later — so it must only retire the read it was sent
    // against. The slot is not that identity: leaving a workflow and returning
    // re-runs the restore, so a stale completion would void a NEW read of the
    // same slot and discard the draft it was about to adopt.
    const proposedEpoch = restoreEpoch.current
    const submittedContentRevision = editorContentRevision.current
    setProposing(true)
    setProposeError(null)
    setProposeFailingChecks([])
    try {
      const v = await api.validateWorkflowDraft(
        draftYAML,
        { agent: selected.agent, name: selected.name },
        extraFiles.length > 0
          ? buildChangeset(selected.agent, selected.name, draftYAML, extraFiles)
          : undefined,
      )
      setDraftValidation(v)
      if (!v.ok) {
        setProposeError('The draft has validation errors — fix them before proposing.')
        return
      }
      const res =
        extraFiles.length > 0
          ? await api.proposeWorkflowChangeset(
              selected.agent,
              selected.name,
              buildChangeset(selected.agent, selected.name, draftYAML, extraFiles),
              proposeMessage,
              // ADR-137 D3 — the conversation that produced this change, so the
              // server archives it with the PR instead of letting it die with
              // the session.
              activeConversationId ?? undefined,
              proposedDraftID,
            )
          : await api.proposeWorkflowActivity(
              selected.agent, selected.name, draftYAML, proposeMessage, activeConversationId ?? undefined,
              proposedDraftID,
            )
      setProposeResult(res)
      // The conversation is archived server-side and belongs to history now.
      // Detaching means the operator's next turn opens a fresh one rather than
      // appending to a conversation that has already shipped.
      setActiveConversationId(null)
      void refreshConversations(selected.agent, selected.name)
      // The propose path clears the session SERVER-side on success, so the
      // client's copy must go too — otherwise re-entering the editor on this
      // slot would resurrect a draft and a conversation that have already been
      // proposed, presenting shipped work as unsaved work.
      // Still the same read. Activity selection stays enabled while the request
      // runs, so an unscoped completion cleared another workflow's session cache
      // and dropped its in-flight read.
      if (restoreEpoch.current !== proposedEpoch) return
      sessionRetired.current = editorContentRevision.current === submittedContentRevision
      settleDraftIdentity(newDraftID())
      clearRestoredSession()
      // NO EPOCH BUMP HERE, deliberately, and the reason is worth keeping.
      //
      // Bumping it after a successful propose looked symmetrical with the discard, and it
      // broke two existing guarantees: "keeps text typed while PROPOSE was in flight" and
      // the same for a supporting file a copilot turn produced mid-propose. Proposing does
      // not close the editor, so anything typed while the request was open is work the
      // operator still has and still expects to be durable — retiring its autosave makes
      // the session the one place that work is NOT saved.
      //
      // The discard is genuinely different: it destroys the draft on purpose, so there is
      // nothing left that wants persisting. And there was never any evidence of the
      // propose path resurrecting a session in the first place — the reported defect was
      // an auto-advancing run rebuilding the row, which the server now disarms on delete.
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      if (err instanceof AuthorTestsFailingError) {
        setProposeFailingChecks(err.checks)
        return
      }
      setProposeError(friendlyErrorMessage(err, 'Propose failed.'))
    } finally {
      setProposing(false)
    }
  }

  // AN OUTSTANDING PROPOSAL ON THIS WORKFLOW, FROM A RUN OR FROM ANYONE.
  //
  // THE EDITOR HAD NO WAY TO KNOW ONE EXISTED. Everything else in this file
  // called `propose*` is this session's own action — `proposeResult`,
  // `proposeMessage`, `canPropose`. A run that reaches `done` proposes by itself
  // (ADR-178 D3) and then its draft session is cleared, so the editor reopens
  // looking exactly as it would if nothing had happened, with [Propose] live.
  //
  // MEASURED, spartan 2026-09-17: run 20260917T115852Z-c2d2b1 opened PR #1179,
  // and the fleet owner's report was that the pull request was "nowhere to be
  // found and the workflow editor still shows the Propose button as active".
  // Pressing it would have opened a SECOND pull request for the same change.
  //
  // MATCHED ON THE AGENT AND THE WORKFLOW NAME, which is what the record carries:
  // `owner_agent` is the agent and `verbatim` is "workflow: edit <agent>/<name>".
  // Deliberately not on the run id — an operator's own proposal must suppress the
  // button too, for the same duplicate-PR reason.
  // THE READ LIVES IN A HOOK — see useAwaitingProposal.
  //
  // It used to be an inline effect with `[selected]` as its dependency, so it fired once per
  // workflow and never again: the fleet owner closed a pull request "via the improvement
  // screen and github" and this panel went on calling it "waiting on approval", with the
  // Propose button disabled behind the stale claim. Extracted so the refresh can be DRIVEN
  // by a test, which an effect buried in this file could not be.
  const awaitingProposal = useAwaitingProposal(selected?.agent ?? '', selected?.name ?? '')
  const awaitingApproval = awaitingProposal !== null

  // A STALENESS WARNING HAS TO BE ABOUT SOMETHING THAT IS STALE — see draftStaleNotice.ts.
  //
  // `sessionStaleBase` records a fact: the fleet sha this draft was last checked against has
  // moved. The notice below turns that into a claim about CONTENT ("proposing it could revert
  // someone else's change"), and when the draft is byte-identical to the deployed file that
  // claim is false and its remedy is a no-op — which is what the fleet owner hit immediately
  // after their own change merged and deployed.
  //
  // Derived at RENDER, not resolved once in the restore effect: `detail` arrives from its own
  // fetch and is re-read when the fleet moves, so a value computed at restore time would be
  // answering with whatever had landed by then. Reading it here is also what makes the answer
  // follow the operator typing and typing it back.
  const staleBaseNoticeApplies = staleNoticeApplies({
    staleBase: sessionStaleBase,
    draftYAML,
    // `isScaffold` is redundant here and kept deliberately: the detail fetch is SKIPPED
    // while a scaffold is open, so `detail` is already null on that path — an injection
    // proved this branch changes no existing behaviour. It states the intent at the one
    // place that would otherwise depend on a fetch-skip several thousand lines away.
    deployedYAML: isScaffold ? null : (detail?.yaml ?? null),
    extraFileCount: extraFiles.length,
  })

  const draftIsValid = draftValidation?.ok === true && !draftParseError
  // `sessionSettled`: proposing before the restore has landed leaves a read in
  // flight across the POST, which no amount of repair afterwards can settle
  // without overwriting one side or the other.
  //
  // AND NOT WHEN THIS WORKFLOW ALREADY HAS ONE WAITING. See `awaitingApproval`.
  const canProposeNow =
    canPropose && draftIsValid && !proposing && !proposeResult && sessionSettled && !awaitingApproval

  return (
    <div className={`h-full flex flex-col ${editMode ? 'space-y-2' : 'space-y-4'}`}>
      {/* While editing, the page header collapses to one line. The `Workflows`
          H1 repeats the active facet tab a few pixels above it, and the
          explanatory sentence is orientation an operator mid-draft has already
          had — together they cost ~90px of the canvas they describe. Editing
          names the artifact instead, which is the one thing the header can say
          that the tab cannot; the sentence moves to the ⓘ. */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          {editMode ? (
            <p className="text-sm text-gray-600 truncate">
              <span className="font-medium text-gray-900" title={selected ? `${selected.agent}/${selected.name}` : undefined}>
                {selected?.agent}/{selected?.name}
              </span>
              <span className="text-gray-400"> · draft</span>
              <span
                className="ml-1.5 cursor-help text-gray-300 hover:text-gray-500"
                title="Visual, Field and YAML share one draft, with the assistant docked below it. Propose sends it for review & approval inside Mission Control (no GitHub needed)."
                aria-label="Visual, Field and YAML share one draft, with the assistant docked below it. Propose sends it for review & approval inside Mission Control (no GitHub needed)."
              >
                ⓘ
              </span>
            </p>
          ) : (
            <>
              <h1 className="text-2xl font-semibold text-gray-900">Workflows</h1>
              <p className="text-sm text-gray-500 mt-1">
                {agent}'s deterministic workflows — every change is proposed for review &amp; approval inside Mission Control (no GitHub needed).
              </p>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                // The same promise the confirm makes, and false in the same
                // window: until the read lands the autosave has written nothing.
                title={
                  sessionSettled
                    ? 'Close the editor. Your draft is saved and will be here when you come back.'
                    : 'Close the editor. Your draft has NOT been saved yet — closing now will lose it.'
                }
              >
                Cancel
              </button>
              {/* ADR-134 D9 — offered on a SCAFFOLD too. It was hidden there because a
                  from-scratch workflow has no deployed version to start from, and Cancel
                  was treated as the equivalent. Cancel does not delete the server-side
                  session, so the one case with no other escape was the one case with no
                  control, and leaving it produced an unlistable orphan. The label says
                  which of the two actions this is. */}
              {(isScaffold || detail) && (
                <button
                  type="button"
                  onClick={() => { void discardDraft() }}
                  disabled={discardingDraft}
                  className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50"
                  title={
                    isScaffold || !detail
                      ? 'Throw this new workflow away. It has never been proposed, so there is no deployed version to fall back to. Assistant conversations are kept in History.'
                      : 'Throw this draft away and start again from the deployed version. Assistant conversations are kept in History.'
                  }
                >
                  {discardingDraft
                    ? 'Discarding…'
                    : isScaffold || !detail
                      ? 'Discard new workflow'
                      : 'Discard draft'}
                </button>
              )}
            </>
          ) : (
            <>
              {/* ADR-134 D10 — the fleet owner's discard, on a draft that is not theirs.
                  It lives here because the normal Discard sits inside the edit-mode
                  branch and a colleague's draft never enters edit mode, so without this
                  the one role permitted to clear an abandoned draft had no control for it
                  — the D9 defect ("no way to delete this draft") reopened one level up.

                  Offered to `fleet_owner` ONLY, matching the server's gate. Rendering it
                  for an operator who will get a 403 would be a button whose whole
                  function is to fail, and the row it points at is work with no other
                  copy anywhere. */}
              {readingOthersDraft && user.fleet_role === 'fleet_owner' && (
                <button
                  type="button"
                  onClick={() => { void discardDraft() }}
                  disabled={discardingDraft}
                  className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50"
                  title={`Throw away ${readOnlyDraftOf}'s draft of this workflow. It has never been proposed, so this is the only copy of their work. Their assistant conversations are kept in History.`}
                >
                  {discardingDraft ? 'Discarding…' : `Discard ${readOnlyDraftOf}'s draft`}
                </button>
              )}
              {detail && (
                selectedSummary?.core ? (
                  <span className="text-xs text-gray-400 italic px-1">core-owned · read-only</span>
                ) : (
                  <button
                    type="button"
                    onClick={enterEditMode}
                    className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    Edit
                  </button>
                )
              )}
              {!isCoreAgent && (
                <button
                  type="button"
                  onClick={() => setNewWorkflowOpen(v => !v)}
                  className="px-3 py-1.5 text-sm border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-50"
                >
                  + New workflow
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ADR-114 D2: develop is ahead of main and /workflows' checkout (which
          tracks main, ADR-100) doesn't reflect it yet — mirrors the console's
          model-change banner ("N changes staged on develop and not yet
          live"). Shown regardless of edit mode; hidden entirely when GitHub
          isn't configured for this fleet or there's nothing pending. */}

      {newWorkflowOpen && !editMode && (
        <NewWorkflowForm agents={authorableAgentOptions} onStart={startNewWorkflow} onCancel={() => setNewWorkflowOpen(false)} />
      )}

      {listError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{listError}</div>
      )}

      <div
        className="flex-1 min-h-[520px] grid gap-4"
        style={{ gridTemplateColumns: `${listCollapsed ? '40px' : `${listWidth ?? DEFAULT_LIST_PX}px`} minmax(0,1fr) ${panelWidth ?? 340}px` }}
      >
        {/* LEFT: activity list (collapsible for canvas real estate) */}
        {listCollapsed ? (
          <div className="rounded-lg border border-gray-200 bg-white flex flex-col items-center pt-2">
            <button
              type="button"
              onClick={() => setListCollapsed(false)}
              title="Expand activity list"
              aria-label="Expand activity list"
              className="p-1.5 text-gray-500 hover:text-gray-800"
            >
              »
            </button>
            <span className="mt-3 text-[10px] text-gray-400 [writing-mode:vertical-rl] rotate-180 select-none tracking-wider uppercase">
              Activities
            </span>
          </div>
        ) : (
          <div ref={listRef} data-testid="activity-list" className="rounded-lg border border-gray-200 bg-white flex flex-col min-h-0 relative">
            {/* The list's right edge, in the gutter like the inspector's left
                one. Its own overflow-hidden moved to the children below: on the
                container it would clip this handle, which sits half outside. */}
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize the workflow list"
              tabIndex={0}
              {...listHandleProps}
              data-testid="list-resizer"
              className="absolute right-0 top-0 bottom-0 -mr-2 w-2 cursor-col-resize z-10 group
                         flex items-center justify-center hover:bg-indigo-100 focus:bg-indigo-100 focus:outline-none"
              title="Drag to resize — double-click to fit the longest name, or use the arrow keys"
            >
              <span className="w-0.5 h-8 rounded-full bg-gray-300 group-hover:bg-indigo-400" />
            </div>
            {/* Controls: search + collapse (the list is already scoped to `agent`) */}
            <div className="p-2 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search name…"
                  className="flex-1 min-w-0 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    title="Clear search"
                    aria-label="Clear search"
                    className="p-1 text-gray-400 hover:text-gray-700 shrink-0"
                  >
                    ✕
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setListCollapsed(true)}
                  title="Collapse list"
                  aria-label="Collapse activity list"
                  className="p-1 text-gray-400 hover:text-gray-700 shrink-0"
                >
                  «
                </button>
              </div>
              {/* ADR-134 D10 — the fleet-wide draft read, as a control rather than a
                  default. Off by default on ADR-137 D8's reasoning: the common case is
                  resuming your own work, and everyone's drafts by default would make an
                  operator hunt for theirs in the list that is how they find it. */}
              <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allDrafts}
                  onChange={e => {
                    allDraftsRef.current = e.target.checked
                    setAllDrafts(e.target.checked)
                  }}
                  className="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-400"
                />
                <span title="Include unproposed drafts belonging to other operators. Theirs open read-only — viewing one changes nothing and creates no draft of your own.">
                  Everyone&apos;s drafts
                </span>
              </label>
            </div>

            <div className="overflow-auto flex-1 min-h-0 rounded-b-lg">
              {listLoading ? (
                <p className="text-sm text-gray-400 p-4 animate-pulse">Loading activities...</p>
              ) : agentActivities.length === 0 ? (
                <div className="p-4 space-y-2">
                  <p className="text-sm text-gray-400">No workflows yet.</p>
                  {!isCoreAgent && (
                    <button
                      type="button"
                      onClick={() => setNewWorkflowOpen(true)}
                      className="text-xs px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                      + Create your first workflow
                    </button>
                  )}
                  <p className="text-[11px] text-gray-400">…or switch to this agent&apos;s <span className="font-medium">Skills</span> facet and use “⤳ Convert” to turn an existing skill into workflows.</p>
                </div>
              ) : visibleActivities.length === 0 ? (
                <p className="text-xs text-gray-400 p-4">No activities match the current search.</p>
              ) : (
                <div className="py-1">
                  {visibleActivities.map(item => {
                    const isSelected = selected?.agent === agent && selected?.name === item.name
                    // ADR-111 D2: the activity's on-disk filename isn't
                    // exposed (only its parsed `name`), so this matches by
                    // PREFIX against `agents/<agent>/activities/<name>.` —
                    // covering both the .yaml and .yml extension the
                    // backend's own listActivityFiles/findActivityFile accept.
                    const isStaged = isPrefixStaged(stagedPaths, `agents/${agent}/activities/${item.name}.`)
                    return (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => selectActivity(item.name)}
                        // The full name LEADS the hover text. The name span
                        // truncates behind the badges, and this title — the one
                        // hover target on the row — said everything about the
                        // workflow except what it was called.
                        title={
                          `${item.name}\n\n` + (item.draft
                            ? 'Draft — never proposed, so nothing is deployed under this name. ' +
                              `Last edited ${item.draft_updated_at ?? 'unknown'}` +
                              (item.draft_operator ? ` by ${item.draft_operator}` : '') +
                              // ADR-134 D10 — the row is the hover target, so the two
                              // facts that change what the operator does next belong
                              // here and not only on the badge: whether it is theirs,
                              // and whether more than one draft exists for this path.
                              (item.draft_operator && item.draft_operator !== user.email
                                ? '. Opens read-only — viewing it changes nothing and creates no draft of your own'
                                : '') +
                              ((item.draft_count ?? 0) > 1
                                ? `. ${item.draft_count} operators have a draft of this workflow — only one can be proposed to this path, so the others will conflict`
                                : '')
                            : (item.triggers && item.triggers.length > 0
                                ? `Triggers: ${item.triggers.join(' • ')}`
                                : 'No triggers') +
                              (item.version ? `\nVersion ${item.version}` : '') +
                              (item.has_draft
                                ? `\n\nUnproposed edits in flight${item.draft_operator ? ` by ${item.draft_operator}` : ''}` +
                                  ` — the deployed version is unchanged until they are proposed.`
                                : ''))
                        }
                        className={`w-full text-left pl-3 pr-3 py-1.5 text-sm flex flex-col items-start gap-0.5 transition-colors ${
                          isSelected ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {/* The name owns the row's full width and wraps to a
                            second line before it is clipped. It used to share
                            one line with up to eight badges, which is where long
                            names lost their distinguishing tail. `data-fit-name`
                            is what the edge's double-click measures. */}
                        <span className="w-full line-clamp-2 [overflow-wrap:anywhere]" data-fit-name>{item.name}</span>
                        {/* The badges on their own line beneath, not folded behind
                            an icon: draft ownership, the ×N conflict and `shared`
                            are what ADR-134 put on the row to be SEEN while
                            scanning, and a hover-only icon would hide exactly them. */}
                        <span className="flex flex-wrap items-center gap-1">
                          {/* Composition at a glance. This list was where an operator
                              looked to ask "how do I link two workflows" while three of
                              this agent's were ALREADY linked — nothing here said so. */}
                          {item.calls?.length ? (
                            <span
                              title={`Calls ${item.calls.length} workflow${item.calls.length === 1 ? '' : 's'}: ${item.calls.join(', ')}`}
                              className="text-[10px] px-1 rounded font-medium bg-indigo-50 text-indigo-600"
                            >
                              ↳{item.calls.length}
                            </span>
                          ) : null}
                          {item.called_by?.length ? (
                            <span
                              title={`Shared — called by ${item.called_by.join(', ')}. Editing it changes their behaviour; deleting it is blocked while they reference it.`}
                              className="text-[10px] px-1 rounded font-medium bg-amber-50 text-amber-700"
                            >
                              shared
                            </span>
                          ) : null}
                          {isStaged && <StagedBadge />}
                          {item.version && (
                            <span className="text-[10px] text-gray-400 font-mono">v{item.version}</span>
                          )}
                          {item.core && (
                            <span
                              title="Core-owned (ADR-103) — changes only through core, not per-fleet."
                              className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-500"
                            >
                              core
                            </span>
                          )}
                          {/* ADR-134 D8 — a published row with unproposed work on it. Small
                              and quiet: the workflow IS deployed and running, and the only
                              thing this says is that a change exists somewhere that has not
                              shipped. Naming whose it is matters more than the badge, and
                              that is in the row's title. */}
                          {item.has_draft && !item.draft ? (
                            <span
                              title={`Unproposed edits in flight${item.draft_operator ? ` by ${item.draft_operator}` : ''} — the deployed version is unchanged until they are proposed.`}
                              className="text-[10px] px-1 rounded font-medium bg-amber-50 text-amber-700"
                            >
                              ✎
                            </span>
                          ) : null}
                          {/* ADR-134 D8 — THE TYPE BADGE MUST BRANCH ON `draft` FIRST.
                              A draft row carries no `dsl`, because the server refuses to
                              report a shape the fleet does not have, and `item.dsl ? … :
                              'legacy'` reads that absence as a legacy stages/steps activity —
                              badging every new draft with a file format it does not have and
                              an invitation to "convert" a file that does not exist. The badge
                              a reader keys on here has to be the one fact that is true. */}
                          {/* ADR-134 D10 — two operators drafting one name. ONE row,
                              because the workflow's path is its identity and two drafts
                              of it cannot both become that file; they collide at
                              propose. The count is how that collision becomes visible
                              before it fires, rather than at the moment it does. */}
                          {(item.draft_count ?? 0) > 1 ? (
                            <span
                              title={`${item.draft_count} operators have a draft of this workflow. Only one can be proposed to this path — the others will conflict.`}
                              className="text-[10px] px-1 rounded font-medium bg-red-50 text-red-700"
                            >
                              ×{item.draft_count}
                            </span>
                          ) : null}
                          {item.draft ? (
                            <span
                              title={
                                item.draft_operator && item.draft_operator !== user.email
                                  ? `${item.draft_operator}'s draft — never proposed, so nothing is deployed under this name. Opens read-only.`
                                  : 'Draft — never proposed, so nothing is deployed under this name.'
                              }
                              className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                item.draft_operator && item.draft_operator !== user.email
                                  ? 'bg-sky-100 text-sky-800'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {/* WHOSE, not just "draft". A per-operator draft rendered
                                  with no owner beside it reads as the reader's own, and
                                  the reader's next move is to edit it. */}
                              {item.draft_operator && item.draft_operator !== user.email
                                ? `draft · ${item.draft_operator.split('@')[0]}`
                                : 'draft'}
                            </span>
                          ) : (
                            <span
                              title={item.dsl
                                ? 'Deterministic workflow (DSL) — runs the same way every time.'
                                : 'Legacy stages/steps activity — consider converting it to a DSL workflow.'}
                              className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                item.dsl ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {item.dsl ? 'dsl' : 'legacy'}
                            </span>
                          )}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* CENTER: node canvas / legacy view / YAML editor */}
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden flex flex-col">
          {/* ADR-134 D10 — A COLLEAGUE'S DRAFT IS VISIBLE AND READ-ONLY.
              Here rather than in the Propose panel, because that panel needs a tab
              opened and this has to be read on arrival. The banner carries the two facts
              that decide what the operator does next — it is not theirs, and there is no
              edit to make — plus the route forward, because "read-only" without a next
              step is a dead end and the operator's real goal is to get the work shipped. */}
          {readOnlyDraftOf && (
            <div className="border-b border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 space-y-0.5">
              <p>
                <span className="font-medium">{readOnlyDraftOf}&apos;s draft — read only.</span>{' '}
                You are viewing their unproposed work.
              </p>
              <p>
                Nothing here saves, and opening it has not created a draft of your own. Ask
                them to propose it, or start your own workflow under a different name — only
                one draft can be proposed to this path.
              </p>
            </div>
          )}
          {/* THE WARNING THE EMPTY CANVAS DID NOT CARRY. Red rather than amber: the
              adjacent Propose would delete every task in the deployed workflow, which
              is destructive, not merely stale. The recovery is the action that already
              does exactly this — discard the draft and reseed from the deployed file —
              surfaced here because an operator staring at an empty canvas has no reason
              to read "Discard draft" as "get my workflow back". */}
          {draftIsEmptyOverDeployed && (
            <div className="border-b border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex items-center justify-between gap-3">
              <span>
                <span className="font-medium">
                  Your draft has no tasks, but the deployed version has {deployedNodeCount}.
                </span>{' '}
                Proposing this draft would delete all of them. If you did not mean to empty
                this workflow, start again from what is deployed.
              </span>
              <button
                type="button"
                onClick={() => { void discardDraft() }}
                disabled={discardingDraft}
                className="shrink-0 px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {discardingDraft ? 'Loading…' : 'Load the deployed version'}
              </button>
            </div>
          )}
          {externalChanged && (
            <div className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center justify-between gap-3">
              <span>A newer version of this workflow was published to the fleet source. Your unsaved edits are preserved.</span>
              <button
                type="button"
                onClick={() => { if (!dirty || window.confirm('Discard your unsaved edits and load the published version?')) reloadFromServer() }}
                className="shrink-0 px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700"
              >
                Reload published version
              </button>
            </div>
          )}
          {detailLoading ? (
            <p className="text-sm text-gray-400 p-6 animate-pulse">Loading activity...</p>
          ) : detailError && !editMode && !readingOthersDraft ? (
            <p className="text-sm text-red-600 p-6">{detailError}</p>
          ) : !detail && !editMode && !readingOthersDraft ? (
            <p className="text-sm text-gray-400 p-6">Select an activity, or start a new one, to open the studio.</p>
          ) : (
            <>
              {editMode && <ModeTabs mode={mode} onChange={setMode} />}

              {editMode && mode === 'yaml' ? (
                <div className="flex-1 min-h-0 flex flex-col">
                  {draftParseError && (
                    <p className="text-xs text-red-600 bg-red-50 border-b border-red-100 px-3 py-1.5">
                      YAML parse error: {draftParseError}
                    </p>
                  )}
                  <textarea
                    value={draftYAML}
                    onChange={e => handleYAMLChange(e.target.value)}
                    spellCheck={false}
                    className="flex-1 w-full font-mono text-xs p-3 resize-none focus:outline-none"
                  />
                </div>
              ) : activeIsDsl ? (
                <>
                  <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 shrink-0">
                    <div className="min-w-0">
                      {/* Truncates in a narrow canvas, so the full name is on hover. */}
                      <p className="text-xs text-gray-500 truncate" title={selected ? `${selected.agent}/${selected.name}` : undefined}>
                        {selected?.agent}/{selected?.name} — {graph.nodes.length} task
                        {graph.nodes.length === 1 ? '' : 's'}
                        {editMode && ' (draft)'}
                        {!editMode && selectedSummary?.version && (
                          <span className="ml-1 font-mono text-gray-400">· v{selectedSummary.version}</span>
                        )}
                      </p>
                      {!editMode && selectedSummary?.triggers && selectedSummary.triggers.length > 0 && (
                        <p className="text-[11px] text-gray-400 truncate" title={selectedSummary.triggers.join(' • ')}>
                          Triggers: {selectedSummary.triggers.slice(0, 2).join(' • ')}
                          {selectedSummary.triggers.length > 2 && ` +${selectedSummary.triggers.length - 2} more`}
                        </p>
                      )}
                      {/* Composition, shown in BOTH modes — unlike version/triggers,
                          which are read-view only. Who calls this workflow is exactly
                          what an operator needs while EDITING it: a shared sub-workflow's
                          steps run inside every caller, and a `run.workflow` reference
                          hard-blocks its delete (ADR-123 D5). Without this the editor
                          showed no relationship at all, so the reuse feature and its
                          existing uses were both invisible. */}
                      {(selectedSummary?.calls?.length || selectedSummary?.called_by?.length) ? (
                        <p className="text-[11px] text-gray-400 truncate">
                          {selectedSummary.calls?.length ? (
                            <span title={`Runs as sub-steps: ${selectedSummary.calls.join(', ')}`}>
                              Calls: <span className="font-mono">{selectedSummary.calls.join(', ')}</span>
                            </span>
                          ) : null}
                          {selectedSummary.calls?.length && selectedSummary.called_by?.length ? ' · ' : null}
                          {selectedSummary.called_by?.length ? (
                            <span
                              className="text-amber-700"
                              title={`Editing this changes behaviour for: ${selectedSummary.called_by.join(', ')}. Deleting it is blocked while these reference it.`}
                            >
                              Called by: <span className="font-mono">{selectedSummary.called_by.join(', ')}</span>
                            </span>
                          ) : null}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {/* ADR-136 D2's other half — the activity-level badge. A
                          finding with no task to attach to has no node to sit
                          on, and the prose channel for it lives in the
                          right-hand Validation section: the right place to READ
                          it, the wrong place to learn it exists, because the
                          operator is looking at the canvas. Without this the
                          finding had only moved from "not rendered" to
                          "rendered where nobody was looking". */}
                      {activityLevelFindings.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setActivityFindingsOpen(o => !o)}
                          aria-expanded={activityFindingsOpen}
                          className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                            activityLevelFindings[0].severity === 'blocks'
                              ? 'bg-red-600 text-white hover:bg-red-700'
                              : 'bg-fixed-amber-400 text-fixed-amber-950 hover:bg-amber-500'
                          }`}
                          title="These are about the workflow as a whole, not any one step"
                        >
                          {activityLevelFindings.length} about this workflow
                        </button>
                      )}
                      {/* ADR-179 P2 — the sweep is reached from the canvas because that
                          is where the literals are. Its answer is computed for the hints
                          anyway, so the badge costs nothing extra and is the only place
                          "this activity has values a set already carries" is visible
                          without opening anything. */}
                      {editMode && (
                        <button
                          type="button"
                          data-testid="sweep-open"
                          aria-pressed={sweepOpen}
                          onClick={() => {
                            const next = !sweepOpen
                            setSweepOpen(next)
                            // Opening asks again: the panel writes to the draft, so it must
                            // be looking at the draft as it is now. It also lands the
                            // operator on the tab the panel lives in, so the click has a
                            // visible result wherever they were.
                            if (next) {
                              dataRefMatches.refetch()
                              setInspectorTab('inspector')
                            }
                          }}
                          className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                            sweepOpen ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'
                          }`}
                          title="Literals in this activity that a published data set already carries"
                        >
                          Review hardcoded values
                          {hardcodedMatchCount > 0 ? ` (${hardcodedMatchCount})` : ''}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setLegendOpen(o => !o)}
                        className="text-xs text-gray-500 hover:text-gray-800"
                        title="What the node colors/shapes mean"
                      >
                        {legendOpen ? '▾' : '▸'} Legend
                      </button>
                      <label
                        className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none"
                        title="Data edges (dotted) show where one task's output ({{steps.X.output}}) feeds another."
                      >
                        <input
                          type="checkbox"
                          checked={showDataEdges}
                          onChange={e => setShowDataEdges(e.target.checked)}
                          className="rounded"
                        />
                        Show data edges
                      </label>
                    </div>
                  </div>
                  {activityFindingsOpen && activityLevelFindings.length > 0 && (
                    <ul className="px-3 py-2 border-b border-gray-100 bg-gray-50/60 shrink-0 space-y-1 list-disc list-inside">
                      {activityLevelFindings.map((f, i) => (
                        <li
                          key={i}
                          className={`text-[11px] ${f.severity === 'blocks' ? 'text-red-700' : 'text-amber-800'}`}
                        >
                          {f.message}
                        </li>
                      ))}
                    </ul>
                  )}
                  {legendOpen && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 px-3 py-2 border-b border-gray-100 bg-gray-50/60 shrink-0">
                      {ADDABLE_TASK_TYPES.map(t => (
                        <span key={t} className="flex items-center gap-1.5 text-[11px] text-gray-600" title={TASK_DESC[t]}>
                          <span className={`inline-block px-1 rounded border text-[10px] ${TASK_STYLES[t]}`}>{TASK_ICON[t]}</span>
                          {/* Label AND keyword: the label is what an operator searches
                              for, the keyword is what the YAML and every validator
                              message call it. */}
                          <span className="font-medium">{TASK_LABEL[t]}</span>
                          <code className="text-[10px] text-gray-400">{t}</code>
                          <span className="text-gray-400 hidden lg:inline">— {TASK_DESC[t]}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex-1 min-h-0 relative">
                    {editMode && graph.nodes.length === 0 && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none p-6">
                        <p className="text-sm text-gray-400 text-center max-w-xs">
                          This workflow has no steps yet. Describe what it should do to the <span className="font-medium text-gray-500">assistant</span> below (e.g. “fetch the report, then post it to Slack”) and it will build it here.
                        </p>
                      </div>
                    )}
                    <ReactFlow
                      colorMode={resolved}
                      nodes={rfNodes}
                      edges={rfEdges}
                      nodeTypes={NODE_TYPES}
                      onNodeClick={(_evt, node) => setSelectedNodeId(node.id)}
                      onPaneClick={() => setSelectedNodeId(null)}
                      nodesDraggable={false}
                      nodesConnectable={false}
                      elementsSelectable
                      panOnDrag
                      zoomOnScroll
                      fitView
                      proOptions={{ hideAttribution: true }}
                    >
                      <Background />
                      <Controls showInteractive={false} />
                    </ReactFlow>
                  </div>
                </>
              ) : editMode && draftParseError ? (
                <p className="text-sm text-red-600 p-6">Fix the YAML parse error above (or switch to YAML mode) to see a graph preview.</p>
              ) : (
                <LegacyView activity={activeActivity} />
              )}

              {/* ADR-136 D1 — the assistant, docked beneath the canvas rather
                  than rendered instead of it. Present in EVERY editor mode:
                  what it can talk about is the draft, and the draft is the same
                  one Visual, Field and YAML all edit. It sits inside the centre
                  column's flex, after the editor pane, so the pane above keeps
                  `flex-1` and the dock can only take what is left over — the
                  canvas is the pane that never yields. */}
              {/* NO LONGER GATED ON editMode (ADR-177).
                  The dock was `editMode && …`, so the phase state of the run happening
                  right now was unreachable unless you entered edit mode on a workflow you
                  had no intention of editing — and a finished run had no home on this page
                  at all. The tab's WRITE controls are gated instead, by `canEdit`. */}
              {(
                <AssistantDock
                  expanded={assistantExpanded}
                  onToggleExpanded={() => {
                    const next = !assistantExpanded
                    setAssistantOpen(next)
                    if (next) setAssistantUnread(false)
                  }}
                  scopeNodeId={mode === 'visual' ? selectedNodeId : null}
                  onClearScope={() => setSelectedNodeId(null)}
                  unread={assistantUnread}
                  tab={effectiveTab}
                  onTabChange={setAssistantTab}
                  history={conversations}
                  historyLoading={conversationsLoading}
                  historyError={conversationsError}
                  activeConversationId={activeConversationId}
                  onResumeConversation={resumeConversation}
                  onDeleteConversation={deleteConversation}
                  onNewConversation={newConversation}
                  historySelf={user.email}
                  historyFilter={historyFilter}
                  onHistoryFilterChange={setHistoryFilter}
                  // ADR-137 D9 — the same role that can merge any change to the
                  // fleet is the one that can remove something posted in error.
                  historyCanDeleteAny={canPropose}
                  railAgent={selected?.agent ?? ''}
                  railWorkflow={selected?.name ?? ''}
                  onRunProposed={(a, n) => { void refreshConversations(a, n) }}
                  // THE SAME SEND A TYPED TURN USES, following the "Convert to DSL flow"
                  // precedent rather than opening a parallel path: the continuation must
                  // land in the conversation, the draft and the run exactly as a typed
                  // turn does, or the button would advance a run the transcript has no
                  // record of.
                  onContinuePhase={prompt => { void handleChatSend(prompt) }}
                  canEdit={editMode}
                  selectedRunId={deepLinkedRunId}
                  onSelectRun={setDeepLinkedRunId}
                  height={dockHeight}
                  onResize={resizeDock}
                  // Dragged past the floor: collapse rather than leave a
                  // transcript too short to read. The height is kept, so
                  // Expand returns to where they had it.
                  onDragCollapse={() => setAssistantOpen(false)}
                  messages={chatMessages}
                  input={chatInput}
                  onInputChange={setChatInput}
                  onSend={() => { void handleChatSend() }}
                  sending={chatSending}
                  elapsedMs={chatElapsedMs}
                  intent={chatIntent}
                  onIntentChange={setChatIntent}
                  model={chatModel}
                  onModelChange={setChatModel}
                  catalog={modelCatalog}
                  catalogLoading={modelCatalogLoading}
                  pin={assistantPin}
                  pinError={assistantPinError}
                  onPinMine={c => { void setMyAssistantPin(c) }}
                  onClearPin={() => { void clearMyAssistantPin() }}
                  pinProposal={assistantPinProposal}
                  {...(user.fleet_role === 'fleet_owner'
                    ? { onPinFleet: (c: ModelChoice) => { void proposeFleetAssistantPin(c) } }
                    : {})}
                  error={chatError}
                  onDismissError={() => setChatError(null)}
                  runFinished={runFinished !== null}
                  onStartNewRun={() => { void handleStartNewRun() }}
                  startingNewRun={startingNewRun}
                  startNewRunError={startNewRunError}
                  unsavedAnswerError={unsavedAnswer?.error}
                  unsavedAnswerGone={unsavedAnswer?.gone}
                  onRetryUnsavedAnswer={() => { void handleRetryUnsavedAnswer() }}
                  retryingUnsavedAnswer={retryingUnsavedAnswer}
                  unconfigured={copilotUnconfigured || !copilotConfigured}
                  showConvertToDsl={!isDraftDsl && !!draftYAML.trim()}
                  onConvertToDsl={handleConvertToDsl}
                  scopingControl={scopingControl}
                  scopeFirst={scopeFirst}
                  onScopeFirstChange={setScopeFirst}
                />
              )}
            </>
          )}
        </div>

        {/* RIGHT: a tabbed inspector with the terminal actions pinned below it.
            This column used to stack five things with different lifecycles in
            one scroll — the per-selection Inspector, the continuous Validation,
            the once-per-change changeset, Propose, and a Raw YAML that
            duplicated the centre pane. Tabs split the first three; Propose stays
            OUT of them, because hiding the terminal action makes a change feel
            unfinishable. */}
        <div ref={panelRef} className="rounded-lg border border-gray-200 bg-white flex flex-col min-h-0 relative">
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the inspector"
            tabIndex={0}
            onPointerDown={startPanelDrag}
            onKeyDown={onPanelKeyDown}
            data-testid="inspector-resizer"
            className="absolute left-0 top-0 bottom-0 -ml-2 w-2 cursor-col-resize z-10 group
                       flex items-center justify-center hover:bg-indigo-100 focus:bg-indigo-100 focus:outline-none"
            title="Drag to resize — or use the arrow keys"
          >
            <span className="w-0.5 h-8 rounded-full bg-gray-300 group-hover:bg-indigo-400" />
          </div>

          <div className="shrink-0 flex items-center gap-0.5 border-b border-gray-100 px-2 py-1">
            {INSPECTOR_TABS.map(t => {
              const count =
                t.id === 'findings' ? (editMode ? findings.length : 0)
                : t.id === 'files' ? extraFiles.length
                : 0
              return (
                <button
                  key={t.id}
                  type="button"
                  aria-pressed={inspectorTab === t.id}
                  onClick={() => setInspectorTab(t.id)}
                  className={`px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                    inspectorTab === t.id ? 'bg-indigo-50 text-indigo-700' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {t.label}{count > 0 ? ` (${count})` : ''}
                </button>
              )
            })}
          </div>

          <div className="flex-1 min-h-0 overflow-auto p-3 space-y-4" ref={inspectorScrollRef}
               data-testid="inspector-scroll">
          {/* The sweep belongs to the Inspector tab — on Findings and Files it sat on top of
              content it had nothing to do with. Hidden rather than unmounted on the other
              tabs, so the ticks the operator made survive a look at the findings. */}
          {sweepOpen && editMode && (
            <div
              className="border-b border-gray-100 pb-3 text-xs"
              hidden={inspectorTab !== 'inspector'}
            >
              <HardcodedValuesPanel
                matches={dataRefMatches.matches}
                unavailableNote={dataRefMatches.unavailableNote}
                refusal={dataRefRefusal}
                // Returned, not voided: the panel disables Apply while the promise is
                // outstanding, so a `void` here would leave a second click able to start a
                // second verify against the same captured draft.
                onApply={selections => applyDataReferences(selections)}
                onSelectTask={id => { setSelectedNodeId(id); setRevealTaskRequest(n => n + 1) }}
                onRefetch={dataRefMatches.refetch}
                onClose={() => setSweepOpen(false)}
              />
            </div>
          )}

          {/* The task tree — part of the normal edit view now, not a mode.
              "Field" existed only because this list did, and the list only ever
              showed the top level; both of those are fixed here rather than
              kept behind a toggle whose whole meaning was "show the tasks". */}
          {inspectorTab === 'inspector' && editMode && isDraftDsl && draftActivity && (
            <div className="border-b border-gray-100 pb-3">
              <TaskTree
                entries={taskTree}
                selectedId={selectedNodeId}
                onSelect={setSelectedNodeId}
                // ADR-128 step 2 — when the selection is a container (`for`,
                // `fork`, `switch`), the palette inserts INSIDE it instead of at
                // the top level.
                containerTarget={selectedNode && isContainerType(selectedNode.taskType) ? selectedNode.id : undefined}
                onAdd={(name, ttype) => applyDraftMutation(
                  selectedNode && isContainerType(selectedNode.taskType)
                    ? addTaskInContainer(
                        draftActivity,
                        {
                          task: selectedNode.id,
                          // A switch nests per clause; default to the first case.
                          clauseIndex: selectedNode.taskType === 'switch' ? 0 : undefined,
                        },
                        name, ttype, defaultSpecFor(ttype),
                      )
                    : addTopLevelTask(draftActivity, name, ttype, defaultSpecFor(ttype)),
                )}
                onRemove={requestDeleteTask}
                onMove={(name, delta) => applyDraftMutation(moveTaskAnywhere(draftActivity, name, delta))}
              />
            </div>
          )}
          {inspectorTab === 'inspector' && !editMode && activeIsDsl && activeActivity && (
            <div className="border-b border-gray-100 pb-3">
              <TaskTree
                readOnly
                entries={readTaskTree}
                selectedId={selectedNodeId}
                onSelect={setSelectedNodeId}
                onAdd={() => {}}
                onRemove={() => {}}
                onMove={() => {}}
              />
            </div>
          )}

          {/* Inspector — no heading: the tab says what this is, and the empty
              state ("click a node") no longer needs a labelled section to
              announce that it has nothing to show. */}
          {inspectorTab === 'inspector' && (
          <div>
            {/* At the tab root, not inside the selected task: an apply can be refused after
                the sweep was closed with nothing selected, and a refusal with no place to
                render is an apply the operator believes succeeded. The sweep panel renders
                the same sentence beside the row it names, so it is not repeated while open. */}
            {dataRefRefusal && !sweepOpen && (
              <p className="mb-2 text-xs text-amber-800" data-testid="dataref-apply-refused">
                {dataRefRefusal}
              </p>
            )}
            {editMode && mode === 'visual' ? (
              selectedNode && draftActivity ? (
                <div className="space-y-2" ref={inspectorTaskRef} data-testid="inspector-task">
                  <p className="text-sm font-medium text-gray-900">{selectedNode.id}</p>
                  <span
                    className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-medium ${TASK_STYLES[selectedNode.taskType]}`}
                  >
                    {TASK_LABEL[selectedNode.taskType]}
                  </span>
                  {selectedNode.parentId && (
                    <p className="text-xs text-gray-500">inside <span className="font-mono">{selectedNode.parentId}</span></p>
                  )}
                  {/* Click-to-edit (PRD §5b): the mutators walk the WHOLE
                      tree (findTaskRaw in workflowDraft.ts), so this works
                      for a node nested in a `for` body or a `switch`/`fork`
                      branch, not just top-level `do` tasks. Structural edits
                      (add/remove a branch or loop body task) stay out of
                      scope for this pass — TaskFieldForm already says so
                      per task type; use YAML mode for those. */}
                  {/* One capturing-free `onInput` on the wrapper sees every keystroke in
                      every uncontrolled field below it, which is what `applyDataReferences`
                      needs to know an edit was in flight. Per-field callbacks would have to
                      be threaded through a dozen inputs to say the same thing. */}
                  <div onInput={() => { fieldEditGeneration.current += 1 }}>
                  <TaskFieldForm
                    // The fields are uncontrolled, so a reference this page wrote into the
                    // draft is invisible until they mount again (ADR-179). The task id is in
                    // the key for the same reason a switch is not a re-render here: without it
                    // the fields keep the previous task's `defaultValue` text and every
                    // affordance keeps its own hint, whose location still names the task the
                    // operator has just left.
                    key={`${selectedNode.id}:${fieldsEpoch}`}
                    task={{ name: selectedNode.id, ttype: selectedNode.taskType, spec: selectedNode.spec }}
                    slot={selected ? { agent: selected.agent, name: selected.name } : undefined}
                    draft={draftYAML}
                    // Fleet-wide, not agent-scoped: `run.workflow` resolves an
                    // activity by NAME across the whole fleet (deps.workflowFile
                    // matches on name with the agent optional), so restricting the
                    // list to this agent would hide legal targets. Self is excluded
                    // — a workflow calling itself is a cycle the validator rejects.
                    callableWorkflows={activities
                      .filter(a => !(selected && a.agent === selected.agent && a.name === selected.name))
                      .map(a => a.name)
                      .filter((n, i, all) => all.indexOf(n) === i)
                      .sort()}
                    onScalarChange={patch => applyDraftMutation(updateTaskFields(draftActivity, selectedNode.id, patch))}
                    onSwitchCaseChange={(idx, expr) =>
                      applyDraftMutation(updateSwitchCaseExpr(draftActivity, selectedNode.id, idx, expr))
                    }
                    onMappingChange={entries =>
                      applyDraftMutation(updateTaskMapping(draftActivity, selectedNode.id, entries))
                    }
                    lookupMatch={dataRefMatches.lookup}
                    onApplyReferences={selections => { void applyDataReferences(selections) }}
                    onEnsurePin={pin => {
                      // The pin lands in the SAME draft mutation path as every
                      // other field edit, so it is serialized into draftYAML and
                      // survives a mode switch (ADR-079 D2: losslessly).
                      const result = ensureDataPin(draftActivity, pin.alias, pin.set)
                      if (result.refusal) return result.refusal
                      applyDraftMutation(result.activity)
                      return null
                    }}
                  />
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400">
                  {isDraftDsl ? 'Click a node in the graph to edit its fields.' : 'Legacy activity — edit in YAML mode.'}
                </p>
              )
            ) : selectedNode ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-900">{selectedNode.id}</p>
                <span
                  className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-medium ${TASK_STYLES[selectedNode.taskType]}`}
                >
                  {TASK_LABEL[selectedNode.taskType]}
                </span>
                {selectedNode.parentId && (
                  <p className="text-xs text-gray-500">inside <span className="font-mono">{selectedNode.parentId}</span></p>
                )}
                <p className="text-xs text-gray-600">{TASK_DESC[selectedNode.taskType]}</p>
                {/* The SAME field form the editor shows, under a disabled
                    fieldset. The read view used to offer a one-line type
                    description and the spec as JSON, so understanding a task —
                    its prompt, its inputs, the python it runs — meant entering
                    edit mode, which opens a draft. One form for both views keeps
                    them from drifting, and `disabled` on the fieldset reaches
                    every control inside it, including ones added later, so the
                    reader cannot write by accident. Links (the python body's
                    "Edit in Files") are not form controls and stay live. */}
                <fieldset
                  disabled
                  data-testid="inspector-task-readonly"
                  className="min-w-0 [&_:is(input,textarea,select)]:bg-gray-50 [&_:is(input,textarea,select)]:text-gray-800"
                >
                  <TaskFieldForm
                    key={selectedNode.id}
                    task={{ name: selectedNode.id, ttype: selectedNode.taskType, spec: selectedNode.spec }}
                    slot={selected ? { agent: selected.agent, name: selected.name } : undefined}
                    onScalarChange={() => {}}
                    onSwitchCaseChange={() => {}}
                    onMappingChange={() => {}}
                    onEnsurePin={() => null}
                  />
                </fieldset>
                {/* Drill into a called workflow. Composition is the one place
                    the read view most needs to be navigable: "what does this
                    step do" is answered by another file. */}
                {selectedNode.taskType === 'run' && (() => {
                  const target = typeof (selectedNode.spec as Record<string, unknown> | undefined)?.workflow === 'string'
                    ? (selectedNode.spec as Record<string, string>).workflow
                    : ''
                  const owner = activities.find(a => a.name === target && a.agent === agent)
                    ?? activities.find(a => a.name === target)
                  if (!owner) return null
                  return (
                    <button
                      type="button"
                      onClick={() => owner.agent === agent
                        ? selectActivity(owner.name)
                        : navigate(activityPath(owner.agent, owner.name))}
                      className="text-xs text-indigo-600 underline hover:text-indigo-800"
                      title={`Open ${owner.name}${owner.agent === agent ? '' : ` (${owner.agent})`}`}
                    >
                      Open {owner.name}{owner.agent === agent ? '' : ` (${owner.agent})`} →
                    </button>
                  )
                })()}
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-500 hover:text-gray-700 select-none">Raw spec</summary>
                  <pre className="mt-1 text-gray-700 bg-gray-50 border border-gray-100 rounded p-2 whitespace-pre-wrap break-words max-h-56 overflow-auto font-mono">
                    {JSON.stringify(selectedNode.spec, null, 2)}
                  </pre>
                </details>
              </div>
            ) : (
              <p className="text-xs text-gray-400">
                {activeIsDsl ? 'Click a node to inspect its fields.' : 'No node selected (legacy activity).'}
              </p>
            )}
          </div>
          )}

          {/* Findings */}
          {inspectorTab === 'findings' && (
          <div>
            {editMode ? (
              <ValidationPanel
                validation={draftValidation}
                validating={validating}
                findings={findings}
                onSelectNode={setSelectedNodeId}
                onApplyFix={(prompt, nodeId) => {
                  // Send it as a normal assistant turn: the answer arrives as a
                  // validated changeset in the usual review path, not a silent
                  // mutation of the draft.
                  //
                  // ADR-136 D1 — this used to `setMode('chat')`, which unmounted
                  // the canvas the finding was about. Now it selects the node
                  // and opens the dock: the operator reads the pre-filled
                  // request with the step it concerns highlighted beside it.
                  setChatInput(prompt)
                  if (nodeId) setSelectedNodeId(nodeId)
                  setAssistantOpen(true)
                  setAssistantUnread(false)
                }}
              />
            ) : !detail ? (
              <p className="text-xs text-gray-400">—</p>
            ) : (
              <ValidationPanel validation={detail.validation} validating={false} />
            )}
          </div>
          )}

          {/* Files — the rest of the changeset (ADR-134 D1), and the read-only
              YAML. Its own tab because a changeset is touched once per change,
              not per selection, and it was crowding out the panel's per-click
              content to sit there permanently. */}
          {inspectorTab === 'files' && (
            <div className="space-y-2">
              {/* ADR-134 D1 — the rest of the changeset. A workflow is not
                  edited alone; this is where the cron that drives it, the skill
                  that routes to it, and (ADR-111 IA-8) the python step body it
                  runs join the same PR. */}
              {editMode && selected ? (
                <ChangesetFiles
                  agent={selected.agent}
                  files={extraFiles}
                  onChange={next => {
                    // Hand-editing the changeset is operator work, and the only
                    // signal that says so — a late restore must not replace it.
                    extrasEdited.current = true
                    setExtraFiles(next)
                  }}
                  existingWorkflows={activities
                    .filter(a => a.agent === selected.agent && a.name !== selected.name)
                    .map(a => a.name)}
                  existingSkills={agentSkills}
                  existingPythonSteps={agentPythonSteps}
                  disabled={proposing}
                />
              ) : (
                <p className="text-xs text-gray-400">
                  Open the editor to add the crons, skills or other workflows this change touches.
                </p>
              )}

              {/* Raw YAML — VIEW mode only. In edit mode this rendered the same
                  draftYAML the centre pane's Raw/advanced → YAML shows
                  editable, so it was a worse copy of a thing already on screen,
                  in the narrowest column. Reading the published file has no
                  other home, so it keeps one here. */}
              {!editMode && detail && (
                <div className="border-t border-gray-100 pt-3">
                  <button
                    type="button"
                    onClick={() => setRawYAMLOpen(v => !v)}
                    className="text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600"
                  >
                    {rawYAMLOpen ? '▾' : '▸'} Raw YAML
                  </button>
                  {rawYAMLOpen && (
                    <pre className="mt-2 text-xs text-gray-700 bg-gray-50 border border-gray-100 rounded p-2 whitespace-pre-wrap break-words max-h-96 overflow-auto font-mono">
                      {detail.yaml}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}
          </div>

          {/* Pinned actions. Terminal, so never behind a tab: an operator who
              cannot see Propose cannot tell the change is finishable. The
              session notices belong to the editor, not the propose action — a
              draft that is only in this browser tab must not be a thing you
              have to go looking for. */}
          {(editMode || (canPropose && selected)) && (
            <div className="shrink-0 border-t border-gray-100 p-3 space-y-2">
              {editMode && !proposeResult && (
                <>
                  {/* ADR-134 D5 — the session survived, and says so. */}
                  {/* Names the half that actually came back. One sentence
                      covering both claimed the draft had been resumed when the
                      changeset was declined in favour of local edits and only the
                      conversation was restored. The conversation sentence says
                      nothing about a STORED draft because there may not be one:
                      a copilot turn that finishes after the browser closed
                      creates a row carrying only a transcript
                      (`editorsession/append.go`). */}
                  {sessionRestored === 'changeset' && (
                    <p className="text-xs text-indigo-700">
                      Resumed your in-progress work here. Nothing has been proposed.
                    </p>
                  )}
                  {/* The conversation is the half that cannot be retyped, so when
                      both came back the notice says so — naming only the changeset
                      left the operator to discover the transcript. */}
                  {sessionRestored === 'changeset-and-conversation' && (
                    <p className="text-xs text-indigo-700">
                      Resumed your in-progress work here, including the assistant
                      conversation. Nothing has been proposed.
                    </p>
                  )}
                  {sessionRestored === 'conversation' && (
                    <p className="text-xs text-indigo-700">
                      Resumed your assistant conversation here. Your draft is unchanged.
                      Nothing has been proposed.
                    </p>
                  )}
                  {/* Shown persistently rather than swallowed: an autosave that
                      fails quietly is the same lost-work defect D5 removes. */}
                  {staleBaseNoticeApplies && sessionStaleBase && (
                    <div className="text-xs text-amber-700 space-y-1">
                      <p>
                        The fleet has changed since this draft was last checked (base{' '}
                        <code>{sessionStaleBase.slice(0, 7)}</code>). Its previous result is no
                        longer proof — edit anything, or re-run Propose, to have it re-checked
                        against the current fleet.
                      </p>
                      {/* A scaffold's base_ref is the FLEET sha its draft was last checked
                          against; there is no deployed version of this workflow for it to
                          revert. The sentence above already gives the remedy for
                          staleness, so this paragraph only says what the action does —
                          the same discard as the footer control (ADR-134 D9), never a
                          "deployed version" that does not exist. */}
                      {isScaffold || !detail ? (
                        <p>
                          This workflow has never been proposed, so there is no deployed
                          version this draft could revert.{' '}
                          <button
                            type="button"
                            onClick={() => { void discardDraft() }}
                            disabled={discardingDraft}
                            className="underline font-medium hover:text-amber-900 disabled:opacity-50"
                          >
                            Discard this new workflow
                          </button>
                          .
                        </p>
                      ) : (
                        /* The notice used to stop at the sentence above, which named the
                           problem and offered no way out of it: this draft is built on a
                           version of the workflow that is no longer deployed, and proposing
                           it would re-apply the old content over the new. Adopting the
                           deployed version is the action the warning implies. */
                        <p>
                          This draft may be based on a version that is no longer deployed —
                          proposing it could revert someone else's change.{' '}
                          <button
                            type="button"
                            onClick={() => { void discardDraft() }}
                            disabled={discardingDraft}
                            className="underline font-medium hover:text-amber-900 disabled:opacity-50"
                          >
                            Start fresh from the deployed version
                          </button>
                          .
                        </p>
                      )}
                    </div>
                  )}
                  {/* The autosave and Propose are both gated on the read having
                      landed, so a slow read is a window in which the editor works
                      normally and saves nothing. Unsaid, the operator's only clue
                      was a Propose button that would not enable. */}
                  {restoreFailed && (
                    <div role="status" className="text-xs text-amber-700 space-y-1">
                      <p>Could not check for your saved draft ({restoreFailed.detail}). Nothing you type here is being saved, and Propose is held until the check succeeds.</p>
                      <button type="button" onClick={retryRestore} className="underline font-medium hover:text-amber-900">Retry</button>
                    </div>
                  )}
                  {!sessionSettled && !restoreFailed && (
                    <p className="text-xs text-amber-700">
                      Still checking for your saved draft — nothing you type here is
                      being saved yet.
                    </p>
                  )}
                  {sessionSaveFailed && (
                    <p className="text-xs text-amber-700">
                      Could not save your draft to the server — it is only in this browser tab.
                      Copy anything you cannot lose before navigating away.
                    </p>
                  )}
                </>
              )}
              {!editMode ? (
                /* ADR-123 — delete through the dependency-aware panel: a
                   workflow can be the target of a cron or another workflow's
                   run.workflow, and those must cascade or hard-block rather
                   than be silently orphaned. */
                <DeletePanel
                  key={`${selected!.agent}/${selected!.name}`}
                  target={{ kind: 'workflow', agent: selected!.agent, name: selected!.name }}
                  label="Delete workflow"
                />
              ) : !canPropose ? (
                <p className="text-xs text-gray-400">Only a fleet owner can propose this change.</p>
              ) : proposeResult ? (
                proposeResult.improvement_warning ? (
                  /* The change was committed and its pull request opened, but registering it on
                     the Improvements list did not confirm. Claiming it is review-ready and
                     pointing at Improvements would be two false statements, so the warning
                     replaces both rather than sitting next to them. */
                  <div className={`rounded-lg border px-3 py-2 text-xs space-y-2 ${registrationTone(proposeResult.registration_state)}`}>
                    <p>{proposeResult.improvement_warning}</p>
                    <button
                      type="button"
                      onClick={finishAfterPropose}
                      className={`text-xs px-2 py-1 rounded border bg-white ${registrationButtonTone(proposeResult.registration_state)}`}
                    >
                      Done — close editor
                    </button>
                  </div>
                ) : (
                <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 space-y-2">
                  <p>Sent for review. Nothing ships until you approve it in Mission Control.</p>
                  {proposeResult.improvement_id ? (
                    <p className="text-green-700">
                      <Link
                        to={`/improvements/${encodeURIComponent(proposeResult.improvement_id)}/pr`}
                        className="font-medium underline"
                      >
                        Review &amp; approve in Mission Control →
                      </Link>
                    </p>
                  ) : (
                    <p className="text-green-700">
                      Find it under{' '}
                      <Link to="/improvements" className="font-medium underline">
                        Improvements
                      </Link>{' '}
                      to approve &amp; deploy.
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={finishAfterPropose}
                    className="text-xs px-2 py-1 rounded border border-green-300 bg-white text-green-800 hover:bg-green-100"
                  >
                    Done — close editor
                  </button>
                </div>
                )
              ) : (
                <>
                  <textarea
                    value={proposeMessage}
                    onChange={e => setProposeMessage(e.target.value)}
                    placeholder="Note for reviewers (optional)"
                    rows={2}
                    className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                  />
                  {proposeError && <p className="text-xs text-red-600">{proposeError}</p>}
                  {proposeFailingChecks.length > 0 && <FailingChecksPanel checks={proposeFailingChecks} />}
                  {/* A CHANGE IS ALREADY WAITING ON A DECISION. Stated, with the
                      way to act on it — a disabled button whose reason lives only
                      in a `title` is the failure this file records two comments
                      above ("the operator's only clue was a Propose button that
                      would not enable"). */}
                  {/* AND IT SAYS WHICH OF THE THREE IT IS.
                      A fleet owner spent four days being told a change was
                      "waiting on approval" when they had already denied it and
                      closed its pull request; that is fixed at the source (the
                      gate now reads the PR, not the row's lifecycle status), but
                      the two answers that are NOT "a PR is open" must never wear
                      that sentence again. `unresolved` means we could not check,
                      and `no_pr_url` means a propose is probably still running —
                      neither is a decision anybody owes us. */}
                  {awaitingProposal?.reason === 'pr_open' && (
                    <div className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-900">
                      <p>
                        A change to this workflow is{' '}
                        <span className="font-medium">already proposed and waiting on approval</span>
                        {awaitingProposal.pr_number ? ` — PR #${awaitingProposal.pr_number}` : ''}
                        {awaitingProposal.context?.includes('proposed automatically')
                          ? ', proposed automatically by an authoring run.'
                          : '.'}
                      </p>
                      {awaitingProposal.improvement_id && (
                        <>
                          <Link
                            to={`/improvements/${encodeURIComponent(awaitingProposal.improvement_id)}/pr`}
                            className="underline decoration-emerald-400 hover:decoration-emerald-700"
                          >
                            Review it in Mission Control
                          </Link>
                          <span className="text-emerald-700">
                            {' '}— approve or decline it before proposing again, or this opens a second
                            pull request for the same workflow.
                          </span>
                        </>
                      )}
                    </div>
                  )}
                  {awaitingProposal?.reason === 'no_pr_url' && (
                    <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                      A change to this workflow is being proposed right now — waiting for its
                      pull request to appear. If this does not clear, dismiss the request on the{' '}
                      <Link to="/improvements" className="underline">
                        Improvements page
                      </Link>{' '}
                      and propose again.
                    </div>
                  )}
                  {awaitingProposal?.reason === 'unresolved' && (
                    <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                      Could not check whether a change to this workflow is already proposed
                      {awaitingProposal.pr_number ? ` (PR #${awaitingProposal.pr_number})` : ''} —
                      GitHub did not answer. Proposing now risks a second pull request for the
                      same workflow, so it is held until the check succeeds.
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={!canProposeNow}
                    onClick={() => { void handlePropose() }}
                    // The session first: until the read lands there is no verdict
                    // for the draft the operator will actually be editing, so
                    // "fix validation errors" names errors that may not exist.
                    title={
                      !sessionSettled
                        ? restoreFailed ? 'Could not check for your saved draft. Retry the check to propose.' : 'Checking for your saved draft…'
                        : awaitingApproval
                          ? 'A change to this workflow is already proposed and waiting on approval'
                          : !draftIsValid
                            ? 'Fix validation errors first'
                            : undefined
                    }
                    className="w-full px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {proposing ? 'Running checks…' : 'Propose change'}
                  </button>
                </>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
  )
}
